/**
 * Farm/field analysis UI. Mounted only from main-dashboard `HomePage` (`/`).
 * The `/userdashboard` route is auth + redirect only — it does not use this component.
 */
import { useState, useEffect, useRef, useMemo, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import type Map from 'ol/Map';
import Overlay from 'ol/Overlay';
import { fromLonLat } from 'ol/proj';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Report from './Report';
import GenerateReportPanel from './GenerateReportPanel';
import LayerTogglePanel from './LayerTogglePanel';
import FarmDroneDataPanel from './FarmDroneDataPanel';
import BlsForecastCard from './BlsForecastCard';
import { useTheme } from '../theme/ThemeContext';
import { analysisPanelShellClass, getAnalysisPanelTheme, getAnalysisResultStripTheme } from '../theme/themeStyles';
import type { LayerVisibilityState, LayerOpacityState } from '../utils/MapLayerManager';
import {
  PEST_OCCURRENCE_PROBABILITY_INDEX_NAME,
  PEST_RISK_DESCRIPTION,
  PEST_RISK_LEGEND_DESCRIPTION,
  getPestAlertFrames,
  pickTopPestAlertPoint,
  type PestRiskPestKey,
} from '../constants/pestRiskLayers';
import type { DroneMissionConfig } from '../constants/dronePrescriptionLayers';
import type { DroneSitesControls } from '../constants/droneSites';
import type {
  MultitemporalZonalLayer,
  MultitemporalZonalPanelState,
} from '../utils/multitemporalZonalAnalytics';
import { digisakaAuthRequestInit, digisakaMeteredAuthHeaders, gptInferenceJsonHeaders } from '../../../config/digisakaGptInference';
import { getFarmWebgisApiBaseUrl, type StrawBurnFetchPayload } from '../../../config/farmWebgisApi';
import { onStartDateChange, onEndDateChange, normalizeDateRange } from '../utils/dateRangeInputs';
import { DEFAULT_MAX_CLOUD_COVERAGE_PERCENT } from '../constants/cloudFilter';
import logo from '../../../assets/logo-transparent.png';
import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import {
  buildAllowedEntitlementSet,
  isEntitlementAllowed,
  isFarmRiceYieldForecastAllowed,
  isPanelTabUnlocked,
  type LayerTogglePanelTabId,
} from '../../../config/analysisLayerEntitlements';
import apiClient from '../../../services/api';
import { fetchSentinel2ObservationDatesCached } from '../../../services/sentinel2ObservationDateCache';
import {
  INDEX_MAP_OPTIONS,
  type IndexMapSatellite,
} from '../constants/indexMapSatellites';
import { indexReadingGuide } from '../constants/indexReadingGuide';
import { notifyInferenceComputeUsage } from '../../../utils/inferenceComputeBudget';
import { buildFarmRiceForecastMonthOptions } from '../utils/riceForecastSeason';
import { FarmAlertFeedbackList } from './FarmAlertFeedbacksSection';
import InfoPopupDialog from './InfoPopupDialog';
import ApiListSkeleton from './ApiListSkeleton';
import CropCalendarInputs from './CropCalendarInputs';
import CropCalendarReportCard from './CropCalendarReportCard';
import CropCalendarFieldReadings from './cropCalendar/CropCalendarFieldReadings';
import CropCalendarIntro from './cropCalendar/CropCalendarIntro';
import { formatReportDate } from '../utils/cropCalendarReport';
import {
  buildCropCalendarReport,
  buildPandoyCropCalendarPaste,
} from '../utils/cropCalendarReport';
import { downloadCropCalendarPdf } from '../utils/cropCalendarReportPdf';
import { readCropCalendarCache, writeCropCalendarCache } from '../utils/cropCalendarCache';
import { readCropCalendarIntroSeen, writeCropCalendarIntroSeen } from '../utils/cropCalendarIntroStorage';
import { fetchFarmCurrentWeather, type FarmCurrentWeather } from '../utils/farmCurrentWeather';
import { downloadFarmBriefPdf, type FarmBriefLang } from '../utils/downloadFarmBrief';
import {
  EMPTY_CROP_CALENDAR_FORM,
  farmPublicId,
  fetchFieldCropInformation,
  fieldCropHasDisplayableInfo,
  formValuesFromFieldCrop,
  isCropCalendarFormReady,
  pickPrimaryFarmingPractice,
  toCropCalendarPrefill,
  type CropCalendarFormValues,
  type CropCalendarPrefill,
  type FieldCropInformation,
} from '../../../config/digisakaApi';
import {
  buildCropCalendarSamples,
  type CropCalendarSampleKey,
} from '../fixtures/cropCalendarSamples';
import FarmForecastTrendChart from './FarmForecastTrendChart';
import {
  type CADITimeseriesPoint,
  type FarmCADITimeseriesState,
  fetchCADITimeseries,
} from './FarmCADITimeseriesChart';
import {
  CADI_DROUGHT_DESCRIPTION,
  CADI_DROUGHT_LEGEND_DESCRIPTION,
  cadiCategoryFromMean,
  filterCadiPointsForAlertsTab,
} from '../constants/cadiDroughtLayers';
import {
  getCadiAlertInfo,
  getForecastAlertInfo,
  getPestAlertInfo,
  getSatelliteAlertInfo,
} from '../utils/farmAlertTypeInfo';
import {
  type PestTimeseriesPoint,
  type FarmPestTimeseriesState,
  fetchPestAlertTimeseries,
  todayIsoDate,
} from '../utils/farmPestTimeseries';
import {
  type AlertFeedbackRecord,
  type FarmForecastAlertRecord,
  type FarmForecastAlertsResponse,
  type FarmIndexAlertRecord,
  buildOutboundMobileAlertPayload,
  buildOutboundForecastAlertPayload,
  buildCADIAlertPayload,
  buildPestAlertPayload,
  buildFeedbackTimeSeriesPlan,
  actionableForecastAlerts,
  cadiAccentFromMean,
  pestAccentFromMean,
  pestSeverityLabel,
  countFarmForecastAlerts,
  fetchAlertFeedbacksByFarmId,
  fetchFarmForecastAlerts,
  filterFeedbacksForAlert,
  filterFeedbacksForForecastAlert,
  forecastAlertIcon,
  forecastAlertMeasurementLabel,
  forecastAlertTitle,
  forecastAlertVariant,
  formatForecastAlertDate,
  formatForecastChartDate,
  formatInterventionLabel,
  getFeedbackActionsTaken,
  getForecastTimingInfo,
  severityDisplay,
  hasForecastTrendChart,
  sanitizeForecastAlertMessage,
  sendFarmAlertsToMobileApp,
  pickInterventionComparePair,
  parseFeedbackInterventionData,
  summarizeSeriesAroundAction,
  type FeedbackTimeSeriesPlan,
  type InterventionComparePair,
  type ForecastTimingAccent,
} from '../utils/farmMobileAlerts';

type FarmAlertKind = 'ndvi' | 'ndmi';

type FarmAlertCardFeedbackState = {
  open: boolean;
  loading: boolean;
  error: string | null;
  items: AlertFeedbackRecord[];
};

const EMPTY_ALERT_FEEDBACK_STATE: FarmAlertCardFeedbackState = {
  open: false,
  loading: false,
  error: null,
  items: [],
};

interface AnalysisPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  selectedField?: any;
  fieldGeoJson?: any;
  onAnalysisRun?: (analysisType: string, params: any) => void;
  onZoomToField?: () => void;
  onCloseAnalysis?: () => void;
  /** Farm analysis: leave the current field and show the fields list again. */
  onBackToFields?: () => void;
  processing?: boolean;
  processingType?: string;
  analysisResults?: any;
  indexData?: any;
  vhiData?: any;
  indexHeatMapData?: any;
  timeSeriesData?: any;
  filteredTimeSeriesData?: any;
  onTimeSeriesSliderChange?: (imageData: any) => void;
  // Optional: external farm-level time-series selection (e.g. MapView player)
  externalTimeSeriesDate?: string;
  // Cloud filtering props
  showCloudFilter?: boolean;
  maxCloudCoverage?: number;
  onCloudFilterChange?: (filterSettings: { showCloudFilter: boolean; maxCloudCoverage: number }) => void;
  // Analysis mode
  analysisMode?: 'field-level' | 'large-scale';
  // Multi-field time series mode
  multiFieldMode?: boolean;
  multiFieldSelection?: any[];
  /** Notifies parent when multi-field time series API is in flight (locks sidebar selection). */
  onMultiFieldProcessingChange?: (processing: boolean) => void;
  // Layer control props for large-scale mode
  layerVisibility?: LayerVisibilityState;
  layerOpacity?: LayerOpacityState;
  onLayerToggle?: (layerType: keyof LayerVisibilityState, visible: boolean) => void;
  onOpacityChange?: (layerType: keyof LayerOpacityState, opacity: number) => void;
  legendUrls?: Record<string, string>;
  cropAreaLegends?: Record<string, { colors: string[]; labels: string[]; description: string; totalArea?: number }>;
  loadingLayers?: Set<string>;
  onForecastDataUpdate?: (forecastData: any) => void;
  onForecastResultsUpdate?: (forecastResults: any) => void;
  onStrawBurnResultsUpdate?: (strawBurnResults: any) => void;
  onFloodLayerShow?: (layerInfo: { wmsUrl: string; layerName: string; bbox: [number, number, number, number] }) => void;
  onBSRIDateRangeUpdate?: (dateRange: { start_date: string; end_date: string }) => void;
  bsriIsLoading?: boolean;
  riceGrowthStageLoading?: boolean;
  irrigationTypeLoading?: boolean;
  onBSRIAnalyseRequest?: (dateRange: { start_date: string; end_date: string }) => void;
  onFarmForecastResultsUpdate?: (result: any) => void;
  onStrawBurnDataFetch?: (payload: StrawBurnFetchPayload) => void;
  strawBurnIsLoading?: boolean;
  strawBurnData?: any;
  onTreeDetectionDataUpdate?: (data: any) => void;
  onCrownSegmentationResultsUpdate?: (data: any) => void;
  onCropfieldDelineationUpdate?: (data: any) => void;
  mapCenter?: [number, number];
  mapZoom?: number;
  onExpandedTabChange?: (tab: LayerTogglePanelTabId | null) => void;
  onFloodDamageSelect?: (data: import('../../../types/floodDamage').FloodDamageData | null) => void;
  selectedFloodDamageId?: string | null;
  onLSTPHDateRangeUpdate?: (dateRange: { start_date: string; end_date: string }) => void;
  lstIsLoading?: boolean;
  onVHIPHDateRangeUpdate?: (dateRange: { start_date: string; end_date: string }) => void;
  vhiIsLoading?: boolean;
  clearMultiTemporalAPITiles?: () => void;
  getOlMap?: () => Map | null;
  pestRiskPestKey?: PestRiskPestKey;
  onPestRiskPestChange?: (pestKey: PestRiskPestKey) => void;
  onRequestDronePrescriptionStats?: (mission: DroneMissionConfig) => void;
  onClearDronePrescriptionStats?: () => void;
  /** Registry drone sites (Leads Agri outputs by province) for the large-scale Drone Data tab. */
  droneSites?: DroneSitesControls;
  zonalState?: MultitemporalZonalPanelState;
  zonalLayer?: MultitemporalZonalLayer;
  zonalFrameLabel?: string;
  zonalPestLabel?: string;
  zonalProvinces?: string[];
  zonalSelectedProvince?: string;
  onZonalProvinceChange?: (province: string) => void;
  onZonalMunicipalitySelect?: (municipality: string | null) => void;
  onShowDroneOrthoIndexMap?: (mission: DroneMissionConfig, tileUrl: string) => void;
  onClearDroneOrthoIndexMap?: () => void;
  /** Optional farmer-provided crop calendar form (e.g. after map right-click flow). */
  cropCalendarFarmerInput?: (Partial<CropCalendarFormValues> & {
    crop_establishment_date?: string;
    crop_variety?: string;
  }) | null;
  /** Open Pandoy AI with crop calendar context pasted (Ask button). */
  onCropCalendarAskPandoy?: (pasteText: string, payload: unknown) => void;
  /** Increment to switch the analysis tab to Crop Calendar (e.g. after confirming the farmer form). */
  cropCalendarOpenRequest?: number;
  /** Increment to switch the Farm Analysis tab to Drone Data (e.g. farm-card Drone button). */
  droneDataOpenRequest?: number;
  /** Map container for PDF screenshots (field-level farm forecast, etc.). */
  mapScreenshotRef?: RefObject<HTMLDivElement | null>;
  /** Same as MapView: GET /auth/me layer keys; farm/field users get a strict list from HomePage. */
  allowedLayerKeys?: string[];
  /**
   * Layout shape:
   *  - 'drawer' (default, legacy): fixed full-height drawer that slides in from the right.
   *  - 'inline-card': a floating rounded card positioned by the parent (e.g. `MapToolRail`).
   *    Used by the new icon-rail UI — the parent renders the navigation outside this component.
   */
  displayMode?: 'drawer' | 'inline-card';
  /** When `displayMode='inline-card'` + `analysisMode='large-scale'`: which tab to show. */
  controlledLargeScaleTab?: LayerTogglePanelTabId | null;
  /** Called when the embedded `LayerTogglePanel` requests a tab change (used in inline-card mode). */
  onControlledLargeScaleTabChange?: (tab: LayerTogglePanelTabId | null) => void;
  /**
   * Optional handler invoked when the user clicks the minimize button in the inline-card header.
   * The parent (e.g. `HomePage`) is responsible for hiding this panel and showing a restore
   * affordance; this component only renders the button when `onMinimize` is provided.
   */
  onMinimize?: () => void;
  /** Notifies parent when the farm-level analysis tab changes (e.g. to show heat-map date bar). */
  onActiveFarmTabChange?: (tab: string) => void;
  /** Farm section buttons live in the header, so the panel hides its own tab grid. */
  farmTabsInHeader?: boolean;
  onFarmHeaderTabsChange?: (tabs: { id: string; label: string }[]) => void;
  /** Header click: switch the farm section. `n` changes on every click. */
  requestedFarmTab?: { id: string; n: number } | null;
  /** Tab to reopen after the sidebar is hidden and shown again. */
  initialFarmTab?: string | null;
  /** Bumped when the map overlay is cleared or the user leaves this tool. In-flight work must stop. */
  cancelFarmWorkEpoch?: number;
  /** Large-scale header focus: hide sibling sections inside the layer panel. */
  pinnedLargeScaleSection?: string | null;
  /** Header info button: open that layer’s explanation, then clear the request. */
  infoSectionId?: string | null;
  onInfoSectionHandled?: () => void;
  onClosePinnedSection?: () => void;
  /** When set, syncs heat-map observation date from the bottom map date bar. */
  externalHeatMapObservationDate?: string | null;
  /** Notifies parent when Index Map index type changes (to auto-set satellite on the date bar). */
  onHeatMapIndexChange?: (index: string) => void;
  /** Active Index Map satellite from the observation bar (S2 | S1 | L8). */
  heatMapSatellite?: IndexMapSatellite;
}

/** ERA5-Land climate series from POST /api/fieldreport/ `weather` (matches /api/climate-data/). */
export interface FieldReportClimateSeries {
  success?: boolean;
  parameter?: string;
  parameter_name?: string;
  daily_data?: Array<{ date: string; value: number; parameter?: string }>;
  weekly_data?: Array<{ week_start: string; week_end: string; value: number; parameter?: string }>;
  total_days?: number;
  metadata?: { data_source?: string; spatial_resolution?: string; temporal_resolution?: string };
  error?: unknown;
}

export interface FieldReportWeather {
  temperature_2m?: FieldReportClimateSeries | { success: false; error: unknown };
  total_precipitation_sum?: FieldReportClimateSeries | { success: false; error: unknown };
}

interface ReportData {
  fieldInfo: any;
  success: boolean;
  results: {
    mean_ndvi: Array<{ date: string; mean_ndvi: number }>;
    mean_evi: Array<{ date: string; mean_evi: number }>;
    mean_msavi2?: Array<{ date: string; mean_msavi2: number }>;
  };
  area_ha: number;
  crop_name: string;
  monitoring_period: string;
  indices_returned: string[];
  selectedIndices: string[];
  analysisDate: string;
  reportType: string;
  /** ERA5-Land temperature & rainfall for the same period/AOI (from fieldreport API). */
  weather?: FieldReportWeather | null;
  recommendations_and_insights?: {
    [indexKey: string]: {
      summary: string;
      trend: string;
      trend_description: string;
      statistics: {
        average: number;
        minimum: number;
        maximum: number;
        latest: number;
        latest_date: string;
        observations_count: number;
      };
      latest_recommendation: {
        status: string;
        value: number;
        interpretation: string;
        recommendation: string;
        category: string;
      };
      insights: string[];
      index_explanation: {
        name: string;
        what_it_measures: string;
        explanation: string;
        use_case: string;
      };
    };
  };
  // Maintain backward compatibility
  timeSeriesData?: any;
}

/** Per-farm alert APIs return 404 when there is no record within the last 15 days (server-side). */
async function fetchFarmIndexAlertRecord(
  url: string,
  headers: Record<string, string>
): Promise<FarmIndexAlertRecord | null> {
  try {
    const res = await axios.get<{
      alert_message?: string;
      observation_date?: string | null;
      ndvi_value?: number | null;
      ndmi_value?: number | null;
    }>(url, { headers, withCredentials: true });
    const msg = res.data?.alert_message;
    if (typeof msg !== 'string' || !msg.trim()) return null;
    return {
      alert_message: msg.trim(),
      observation_date: res.data.observation_date ?? null,
      ndvi_value: res.data.ndvi_value ?? null,
      ndmi_value: res.data.ndmi_value ?? null,
    };
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.status === 404) return null;
    console.error('Farm index alert request failed:', url, e);
    return null;
  }
}

/** SSE progress events arrive in parallel worker completion order, not date order — sort for UI/slider. */
function sortTimeSeriesResultsByDate<T extends { date?: string }>(results: T[]): T[] {
  return [...results].sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
}

type FarmAlertTone = 'attention' | 'positive' | 'neutral';

function getFarmAlertTone(message: string): FarmAlertTone {
  const m = message.toLowerCase();
  if (m.includes('attention')) return 'attention';
  if (m.includes('good news')) return 'positive';
  return 'neutral';
}

function farmAlertDisplayBody(message: string, tone: FarmAlertTone): string {
  const t = message.trim();
  if (tone === 'attention') return t.replace(/^Attention:\s*/i, '').trim();
  if (tone === 'positive') return t.replace(/^Good news!\s*/i, '').trim();
  return t;
}

function extractAlertDateFromMessage(message: string): string | null {
  const iso = message.match(/(\d{4}-\d{2}-\d{2})/);
  if (!iso) return null;
  const parsed = new Date(`${iso[1]}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function FarmSatelliteAlertCard({
  title,
  icon,
  message,
  variant = 'vegetation',
  alertKind = 'ndvi',
  compact = false,
  onSend,
  onViewFeedback,
  sending = false,
  feedbackLoading = false,
  feedbackOpen = false,
  actionsDisabled = false,
}: {
  title: string;
  icon: string;
  message: string;
  variant?: 'vegetation' | 'moisture';
  alertKind?: 'ndvi' | 'ndmi';
  compact?: boolean;
  onSend?: () => void;
  onViewFeedback?: () => void;
  sending?: boolean;
  feedbackLoading?: boolean;
  feedbackOpen?: boolean;
  actionsDisabled?: boolean;
}) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);
  const [infoOpen, setInfoOpen] = useState(false);
  const alertInfo = getSatelliteAlertInfo(alertKind);
  const tone = getFarmAlertTone(message);
  const body = farmAlertDisplayBody(message, tone);
  const alertDate = extractAlertDateFromMessage(message);

  const badge =
    tone === 'attention'
      ? {
          label: 'Needs attention',
          className: isDark
            ? 'bg-amber-500/20 text-amber-200 border-amber-500/40'
            : 'bg-amber-50 text-amber-800 border-amber-200',
        }
      : tone === 'positive'
        ? {
            label: 'Improving',
            className: isDark
              ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40'
              : 'bg-[#00854014] text-[#008540] border-[#00854080]',
          }
        : {
            label: 'Update',
            className: isDark
              ? 'bg-blue-500/20 text-blue-200 border-blue-400/40'
              : 'bg-blue-50 text-blue-700 border-blue-200',
          };

  const iconWrap = variant === 'moisture' ? ap.iconWrapMoisture : ap.iconWrapVeg;
  const actionBtnClass = `inline-flex shrink-0 items-center justify-center gap-1 rounded-lg border font-medium leading-none transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${ap.mobileBtn} ${
    compact ? 'px-1.5 py-1 text-[9px]' : 'px-2 py-1.5 text-[10px]'
  }`;
  const actionSpinnerClass = `animate-spin rounded-full border border-current border-t-transparent ${
    compact ? 'h-3 w-3' : 'h-3.5 w-3.5'
  }`;

  return (
    <div
      className={`rounded-xl border text-left ${ap.cardSoft} ${
        compact ? 'p-2.5' : 'p-3.5 sm:p-4'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={`flex shrink-0 items-center justify-center rounded-lg ${iconWrap} ${
            compact ? 'h-8 w-8' : 'h-10 w-10'
          }`}
          aria-hidden
        >
          <i className={`${icon} ${compact ? 'text-base' : 'text-lg'}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
            <span className={`font-semibold ${ap.heading} ${compact ? 'text-xs' : 'text-sm'}`}>{title}</span>
            <span
              className={`inline-flex items-center rounded-full border px-1.5 py-px font-semibold uppercase tracking-wide ${
                compact ? 'text-[8px]' : 'text-[9px]'
              } ${badge.className}`}
            >
              {badge.label}
            </span>
          </div>
          <p className={`leading-snug ${ap.muted} ${compact ? 'text-[10px]' : 'text-xs'}`}>{body}</p>
          {alertDate && (
            <div className={`mt-1 flex items-center gap-1 ${ap.faint} ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
              <i className="ri-time-line" aria-hidden />
              <span>{alertDate}</span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-1">
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            className={`inline-flex shrink-0 items-center justify-center rounded-full border transition-colors ${
              compact ? 'h-6 w-6' : 'h-7 w-7'
            } ${isDark ? 'border-gray-600 text-gray-400 hover:bg-gray-800 hover:text-gray-200' : 'border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
            title={`About ${title.toLowerCase()} alerts`}
            aria-label={`About ${title.toLowerCase()} alerts`}
          >
            <i className={`ri-information-line ${compact ? 'text-sm' : 'text-base'}`} aria-hidden />
          </button>
          {onSend && (
            <button
              type="button"
              disabled={actionsDisabled || sending || feedbackLoading}
              onClick={onSend}
              className={actionBtnClass}
              title={sending ? 'Sending alert…' : `Send ${title.toLowerCase()} alert to the mobile app`}
              aria-label={`Send ${title.toLowerCase()} alert to the mobile app`}
            >
              {sending ? (
                <span className={actionSpinnerClass} aria-hidden />
              ) : (
                <i className={`ri-smartphone-line ${compact ? 'text-xs' : 'text-sm'}`} aria-hidden />
              )}
              <span>{sending ? 'Sending' : 'Send'}</span>
            </button>
          )}
          {onViewFeedback && (
            <button
              type="button"
              disabled={actionsDisabled || sending || feedbackLoading}
              onClick={onViewFeedback}
              className={`${actionBtnClass} ${
                feedbackOpen
                  ? isDark
                    ? 'border-emerald-500/50 bg-emerald-500/10'
                    : 'border-[#00854080] bg-[#00854014]'
                  : ''
              }`}
              title={
                feedbackLoading
                  ? 'Loading feedback…'
                  : feedbackOpen
                    ? `Hide farmer feedback for this ${title.toLowerCase()} alert`
                    : `View farmer feedback for this ${title.toLowerCase()} alert`
              }
              aria-label={`View farmer feedback for this ${title.toLowerCase()} alert`}
              aria-expanded={feedbackOpen}
            >
              {feedbackLoading ? (
                <span className={actionSpinnerClass} aria-hidden />
              ) : (
                <i className={`ri-chat-smile-2-line ${compact ? 'text-xs' : 'text-sm'}`} aria-hidden />
              )}
              <span>
                {feedbackLoading ? 'Loading' : feedbackOpen ? 'Hide' : 'Feedback'}
              </span>
            </button>
          )}
        </div>
      </div>
      <InfoPopupDialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={alertInfo.title}
        accent={variant === 'moisture' ? 'sky' : 'emerald'}
      >
        <p className="mb-3">{alertInfo.description}</p>
        {alertInfo.legend && (
          <p className="text-xs opacity-90">{alertInfo.legend}</p>
        )}
      </InfoPopupDialog>
    </div>
  );
}

function forecastAccentClasses(accent: ForecastTimingAccent, isDark: boolean) {
  if (accent === 'urgent') {
    return {
      cardBorder: isDark ? 'border-red-500/50' : 'border-red-300',
      cardGlow: isDark ? 'shadow-[0_0_24px_rgba(239,68,68,0.18)]' : 'shadow-[0_0_16px_rgba(239,68,68,0.12)]',
      badge: isDark
        ? 'bg-red-500/20 text-red-200 border-red-500/40'
        : 'bg-red-50 text-red-700 border-red-200',
      timingBox: isDark
        ? 'border-red-500/60 bg-red-950/40 text-red-300'
        : 'border-red-400 bg-red-50 text-red-700',
      timingGlow: isDark ? 'shadow-[inset_0_0_20px_rgba(239,68,68,0.15)]' : '',
      heroText: isDark ? 'text-rose-400' : 'text-rose-600',
    };
  }
  if (accent === 'soon') {
    return {
      cardBorder: isDark ? 'border-orange-500/45' : 'border-orange-300',
      cardGlow: isDark ? 'shadow-[0_0_24px_rgba(249,115,22,0.16)]' : 'shadow-[0_0_16px_rgba(249,115,22,0.1)]',
      badge: isDark
        ? 'bg-orange-500/20 text-orange-200 border-orange-500/40'
        : 'bg-orange-50 text-orange-800 border-orange-200',
      timingBox: isDark
        ? 'border-orange-500/60 bg-orange-950/40 text-orange-300'
        : 'border-orange-400 bg-orange-50 text-orange-700',
      timingGlow: isDark ? 'shadow-[inset_0_0_20px_rgba(249,115,22,0.15)]' : '',
      heroText: isDark ? 'text-orange-400' : 'text-orange-600',
    };
  }
  return {
    cardBorder: isDark ? 'border-amber-400/40' : 'border-amber-300',
    cardGlow: isDark ? 'shadow-[0_0_20px_rgba(251,191,36,0.12)]' : 'shadow-[0_0_14px_rgba(251,191,36,0.1)]',
    badge: isDark
      ? 'bg-amber-500/20 text-amber-200 border-amber-500/40'
      : 'bg-amber-50 text-amber-800 border-amber-200',
    timingBox: isDark
      ? 'border-amber-400/55 bg-amber-950/35 text-amber-200'
      : 'border-amber-400 bg-amber-50 text-amber-800',
    timingGlow: isDark ? 'shadow-[inset_0_0_20px_rgba(251,191,36,0.12)]' : '',
    heroText: isDark ? 'text-amber-300' : 'text-amber-700',
  };
}

function cadiSeverityLabel(mean: number): string {
  return cadiCategoryFromMean(mean).label;
}

/** Send / Feedback actions — labeled, bottom-right of severity-hero alert cards. */
function AlertCardIconActions({
  compact = false,
  canSend = false,
  onSend,
  onViewFeedback,
  sending = false,
  feedbackLoading = false,
  feedbackOpen = false,
  disabled = false,
  sendTitle,
  feedbackTitle,
  isDark,
}: {
  compact?: boolean;
  canSend?: boolean;
  onSend?: () => void;
  onViewFeedback?: () => void;
  sending?: boolean;
  feedbackLoading?: boolean;
  feedbackOpen?: boolean;
  disabled?: boolean;
  sendTitle: string;
  feedbackTitle: string;
  isDark: boolean;
}) {
  if (!canSend && !onViewFeedback) return null;
  const busy = disabled || sending || feedbackLoading;
  const btn = `inline-flex items-center justify-center gap-1 rounded-lg border font-medium leading-none transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
    compact ? 'px-2 py-1.5 text-[10px]' : 'px-2.5 py-1.5 text-[11px]'
  } ${
    isDark
      ? 'border-white/10 text-gray-200 hover:bg-white/5 hover:text-white'
      : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900'
  }`;
  const spin = `animate-spin rounded-full border border-current border-t-transparent ${
    compact ? 'h-3 w-3' : 'h-3.5 w-3.5'
  }`;

  return (
    <div className="mt-3 flex items-center justify-end gap-2">
      {canSend && onSend && (
        <button
          type="button"
          disabled={busy}
          onClick={onSend}
          className={btn}
          title={sending ? 'Sending alert…' : sendTitle}
          aria-label={sendTitle}
        >
          {sending ? (
            <span className={spin} aria-hidden />
          ) : (
            <i className={`ri-send-plane-2-line ${compact ? 'text-xs' : 'text-sm'}`} aria-hidden />
          )}
          <span>{sending ? 'Sending' : 'Send'}</span>
        </button>
      )}
      {onViewFeedback && (
        <button
          type="button"
          disabled={busy}
          onClick={onViewFeedback}
          className={`${btn} ${
            feedbackOpen
              ? isDark
                ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-200'
                : 'border-[#00854080] bg-[#00854014] text-[#008540]'
              : ''
          }`}
          title={feedbackLoading ? 'Loading feedback…' : feedbackTitle}
          aria-label={feedbackTitle}
          aria-expanded={feedbackOpen}
        >
          {feedbackLoading ? (
            <span className={spin} aria-hidden />
          ) : (
            <i className={`ri-chat-smile-2-line ${compact ? 'text-xs' : 'text-sm'}`} aria-hidden />
          )}
          <span>{feedbackLoading ? 'Loading' : feedbackOpen ? 'Hide' : 'Feedback'}</span>
        </button>
      )}
    </div>
  );
}

function FarmForecastAlertCard({
  alert,
  compact = false,
  onSend,
  onViewFeedback,
  sending = false,
  feedbackLoading = false,
  feedbackOpen = false,
  actionsDisabled = false,
}: {
  alert: FarmForecastAlertRecord;
  compact?: boolean;
  onSend?: () => void;
  onViewFeedback?: () => void;
  sending?: boolean;
  feedbackLoading?: boolean;
  feedbackOpen?: boolean;
  actionsDisabled?: boolean;
}) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);
  const [infoOpen, setInfoOpen] = useState(false);
  const alertInfo = getForecastAlertInfo(alert);
  const title = forecastAlertTitle(alert);
  const icon = forecastAlertIcon(alert);
  const variant = forecastAlertVariant(alert);
  const message = sanitizeForecastAlertMessage(alert.alert_message);
  const isSlowIndexForecast = variant === 'vegetation' || variant === 'moisture';
  const forecastDay = isSlowIndexForecast
    ? null
    : formatForecastAlertDate(alert.forecast_date);
  const timing = isSlowIndexForecast
    ? null
    : getForecastTimingInfo(alert.forecast_date);
  const severityView = severityDisplay(alert.severity);
  const accent = forecastAccentClasses(severityView.accent, isDark);

  const iconColor =
    variant === 'moisture'
      ? isDark
        ? 'text-sky-300'
        : 'text-sky-700'
      : variant === 'weather'
        ? isDark
          ? 'text-sky-300'
          : 'text-sky-700'
        : isDark
          ? 'text-emerald-300'
          : 'text-[#008540]';

  const valueLabel = isSlowIndexForecast ? null : forecastAlertMeasurementLabel(alert);

  const heroLabel = severityView.label;
  const timingLabel = isSlowIndexForecast
    ? 'Coming week'
    : timing
      ? timing.headline.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
      : null;
  const metaRight = [timingLabel, forecastDay].filter(Boolean).join(' · ') || null;

  return (
    <div
      className={`rounded-2xl border text-left ${ap.cardSoft} ${accent.cardBorder} ${
        compact ? 'p-3' : 'p-3.5 sm:p-4'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <i className={`${icon} shrink-0 ${iconColor} ${compact ? 'text-sm' : 'text-base'}`} aria-hidden />
          <p className={`truncate font-medium ${ap.muted} ${compact ? 'text-[11px]' : 'text-xs'}`}>
            {title}
          </p>
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            className={`inline-flex shrink-0 items-center justify-center rounded-full transition-colors ${
              isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
            }`}
            title={`About ${title.toLowerCase()}`}
            aria-label={`About ${title.toLowerCase()}`}
          >
            <i className="ri-information-line text-sm" aria-hidden />
          </button>
        </div>
        {metaRight && (
          <span className={`shrink-0 ${ap.faint} ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
            {metaRight}
          </span>
        )}
      </div>

      <div className={`mt-2 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5`}>
        <p
          className={`font-bold leading-none tracking-tight ${accent.heroText} ${
            compact ? 'text-[1.65rem]' : 'text-[1.85rem] sm:text-3xl'
          }`}
        >
          {heroLabel}
        </p>
        {valueLabel != null && (
          <span className={`${ap.faint} ${compact ? 'text-xs' : 'text-sm'}`}>{valueLabel}</span>
        )}
      </div>

      <p className={`mt-2 leading-snug ${ap.muted} ${compact ? 'text-[11px]' : 'text-xs'}`}>
        {message}
      </p>

      <AlertCardIconActions
        compact={compact}
        canSend={Boolean(onSend)}
        onSend={onSend}
        onViewFeedback={onViewFeedback}
        sending={sending}
        feedbackLoading={feedbackLoading}
        feedbackOpen={feedbackOpen}
        disabled={actionsDisabled}
        sendTitle={`Send ${title.toLowerCase()} to the mobile app`}
        feedbackTitle={
          feedbackOpen
            ? `Hide farmer feedback for this ${title.toLowerCase()}`
            : `View farmer feedback for this ${title.toLowerCase()}`
        }
        isDark={isDark}
      />

      <InfoPopupDialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={alertInfo.title}
        accent={variant === 'weather' ? 'sky' : variant === 'moisture' ? 'blue' : 'emerald'}
      >
        <p>{alertInfo.description}</p>
        {alertInfo.legend && <p className="mt-3 text-xs opacity-90">{alertInfo.legend}</p>}
      </InfoPopupDialog>
    </div>
  );
}

function FarmCadiMonthAlertCard({
  point,
  compact = false,
  canSend = false,
  onSend,
  onViewFeedback,
  sending = false,
  feedbackLoading = false,
  feedbackOpen = false,
  showActions = false,
}: {
  point: CADITimeseriesPoint;
  compact?: boolean;
  canSend?: boolean;
  onSend?: () => void;
  onViewFeedback?: () => void;
  sending?: boolean;
  feedbackLoading?: boolean;
  feedbackOpen?: boolean;
  showActions?: boolean;
}) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);
  const [infoOpen, setInfoOpen] = useState(false);
  const severityAccent = cadiAccentFromMean(point.mean);
  const accent = forecastAccentClasses(severityAccent, isDark);
  const valueLabel = `CADI ~${point.mean.toFixed(2)}`;
  const severity = cadiSeverityLabel(point.mean);
  const summary =
    severity === 'No drought'
      ? `Little drought stress expected for ${point.label}.`
      : `Drought outlook may reach ${severity.toLowerCase()} levels this month.`;

  return (
    <div
      className={`rounded-2xl border text-left ${ap.cardSoft} ${accent.cardBorder} ${
        compact ? 'p-3' : 'p-3.5 sm:p-4'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <i
            className={`ri-sun-cloudy-line shrink-0 ${
              isDark ? 'text-amber-300' : 'text-amber-700'
            } ${compact ? 'text-sm' : 'text-base'}`}
            aria-hidden
          />
          <p className={`truncate font-medium ${ap.muted} ${compact ? 'text-[11px]' : 'text-xs'}`}>
            Drought outlook
          </p>
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            className={`inline-flex shrink-0 items-center justify-center rounded-full transition-colors ${
              isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
            }`}
            title="About drought outlook"
            aria-label="About drought outlook"
          >
            <i className="ri-information-line text-sm" aria-hidden />
          </button>
        </div>
        <span className={`shrink-0 ${ap.faint} ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
          {point.label}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
        <p
          className={`font-bold leading-none tracking-tight ${accent.heroText} ${
            compact ? 'text-[1.65rem]' : 'text-[1.85rem] sm:text-3xl'
          }`}
        >
          {severity}
        </p>
        <span className={`${ap.faint} ${compact ? 'text-xs' : 'text-sm'}`}>{valueLabel}</span>
      </div>

      <p className={`mt-2 leading-snug ${ap.muted} ${compact ? 'text-[11px]' : 'text-xs'}`}>
        {summary}
      </p>

      {showActions && (
        <AlertCardIconActions
          compact={compact}
          canSend={canSend}
          onSend={onSend}
          onViewFeedback={onViewFeedback}
          sending={sending}
          feedbackLoading={feedbackLoading}
          feedbackOpen={feedbackOpen}
          sendTitle="Send drought alert to the mobile app"
          feedbackTitle={feedbackOpen ? 'Hide farmer feedback' : 'View farmer feedback'}
          isDark={isDark}
        />
      )}

      <InfoPopupDialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={getCadiAlertInfo().title}
        accent="amber"
      >
        <p className="mb-3">{CADI_DROUGHT_DESCRIPTION}</p>
        <p className="text-xs opacity-90">{getCadiAlertInfo().legend}</p>
        <p className="mt-3 text-xs opacity-90">{CADI_DROUGHT_LEGEND_DESCRIPTION}</p>
      </InfoPopupDialog>
    </div>
  );
}

function FarmPestAlertCard({
  point,
  lastUpdated,
  compact = false,
  canSend = false,
  onSend,
  onViewFeedback,
  sending = false,
  feedbackLoading = false,
  feedbackOpen = false,
  showActions = false,
}: {
  point: PestTimeseriesPoint;
  lastUpdated?: string | null;
  compact?: boolean;
  canSend?: boolean;
  onSend?: () => void;
  onViewFeedback?: () => void;
  sending?: boolean;
  feedbackLoading?: boolean;
  feedbackOpen?: boolean;
  showActions?: boolean;
}) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);
  const [infoOpen, setInfoOpen] = useState(false);
  const severityAccent = pestAccentFromMean(point.mean);
  const accent = forecastAccentClasses(severityAccent, isDark);
  const valueLabel = `~${point.mean.toFixed(2)} probability`;
  const severity = pestSeverityLabel(point.mean);
  const updatedLabel =
    formatForecastChartDate(lastUpdated || todayIsoDate()) || 'today';

  return (
    <div
      className={`rounded-2xl border text-left ${ap.cardSoft} ${accent.cardBorder} ${
        compact ? 'p-3' : 'p-3.5 sm:p-4'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <i
            className={`ri-bug-line shrink-0 ${
              isDark ? 'text-rose-300' : 'text-rose-700'
            } ${compact ? 'text-sm' : 'text-base'}`}
            aria-hidden
          />
          <p className={`truncate font-medium ${ap.muted} ${compact ? 'text-[11px]' : 'text-xs'}`}>
            Pest risk · {point.pestLabel}
          </p>
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            className={`inline-flex shrink-0 items-center justify-center rounded-full transition-colors ${
              isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
            }`}
            title="About pest risk outlook"
            aria-label="About pest risk outlook"
          >
            <i className="ri-information-line text-sm" aria-hidden />
          </button>
        </div>
        <span className={`shrink-0 ${ap.faint} ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
          Updated {updatedLabel}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
        <p
          className={`font-bold leading-none tracking-tight ${accent.heroText} ${
            compact ? 'text-[1.65rem]' : 'text-[1.85rem] sm:text-3xl'
          }`}
        >
          {severity}
        </p>
        <span className={`${ap.faint} ${compact ? 'text-xs' : 'text-sm'}`}>{valueLabel}</span>
      </div>

      <p className={`mt-2 leading-snug ${ap.muted} ${compact ? 'text-[11px]' : 'text-xs'}`}>
        Seasonal outlook for this field.
      </p>

      {showActions && (
        <AlertCardIconActions
          compact={compact}
          canSend={canSend}
          onSend={onSend}
          onViewFeedback={onViewFeedback}
          sending={sending}
          feedbackLoading={feedbackLoading}
          feedbackOpen={feedbackOpen}
          sendTitle="Send pest alert to the mobile app"
          feedbackTitle={feedbackOpen ? 'Hide farmer feedback' : 'View farmer feedback'}
          isDark={isDark}
        />
      )}

      <InfoPopupDialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={getPestAlertInfo().title}
        accent="red"
      >
        <p className="mb-3">{PEST_RISK_DESCRIPTION}</p>
        <p className="text-xs opacity-90">{getPestAlertInfo().legend}</p>
        <p className="mt-3 text-xs opacity-90">{PEST_RISK_LEGEND_DESCRIPTION}</p>
        <p className="mt-3 text-xs opacity-90">
          Index: {PEST_OCCURRENCE_PROBABILITY_INDEX_NAME} (0–1).
        </p>
      </InfoPopupDialog>
    </div>
  );
}

function ensureFeedbackLocationOverlay(
  map: Map,
  overlayRef: { current: Overlay | null }
): Overlay {
  const existing = overlayRef.current;
  if (existing) {
    if (existing.getMap() !== map) {
      existing.getMap()?.removeOverlay(existing);
      map.addOverlay(existing);
    }
    return existing;
  }

  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText =
    'display:flex;flex-direction:column;align-items:center;pointer-events:none;filter:drop-shadow(0 3px 6px rgba(0,0,0,0.35));';
  el.innerHTML = `
    <svg width="28" height="36" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 1.2C6.15 1.2 1.4 5.8 1.4 11.5c0 8.3 10.6 22.4 10.6 22.4S22.6 19.8 22.6 11.5C22.6 5.8 17.85 1.2 12 1.2z" fill="#008540" stroke="#ffffff" stroke-width="1.6"/>
      <circle cx="12" cy="11.5" r="3.6" fill="#ffffff"/>
    </svg>
  `;

  const overlay = new Overlay({
    element: el,
    positioning: 'bottom-center',
    stopEvent: false,
    offset: [0, 2],
  });
  map.addOverlay(overlay);
  overlayRef.current = overlay;
  return overlay;
}

function FarmForecastAlertRow({
  alert,
  compact,
  actionsDisabled,
  sending,
  sendFeedback,
  feedbackState,
  onSend,
  onViewFeedback,
  onCheckBeforeAfter,
  onZoomToLocation,
}: {
  alert: FarmForecastAlertRecord;
  compact: boolean;
  actionsDisabled: boolean;
  sending: boolean;
  sendFeedback?: { type: 'success' | 'error'; message: string };
  feedbackState: FarmAlertCardFeedbackState;
  onSend: () => void;
  onViewFeedback: () => void;
  onCheckBeforeAfter?: (feedback: AlertFeedbackRecord) => void;
  onZoomToLocation?: (lon: number, lat: number) => void;
}) {
  const { isDark } = useTheme();

  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
      <FarmForecastAlertCard
        alert={alert}
        compact={compact}
        actionsDisabled={actionsDisabled}
        sending={sending}
        feedbackLoading={feedbackState.loading}
        feedbackOpen={feedbackState.open}
        onSend={onSend}
        onViewFeedback={onViewFeedback}
      />
      {sendFeedback && (
        <p
          role="status"
          className={`text-[11px] leading-snug ${
            sendFeedback.type === 'success'
              ? isDark
                ? 'text-emerald-300'
                : 'text-[#008540]'
              : 'text-red-500'
          }`}
        >
          {sendFeedback.message}
        </p>
      )}
      {feedbackState.open && (
        <FarmAlertFeedbackList
          loading={feedbackState.loading}
          error={feedbackState.error}
          feedbacks={feedbackState.items}
          compact={compact}
          onCheckBeforeAfter={onCheckBeforeAfter}
          onZoomToLocation={onZoomToLocation}
        />
      )}
    </div>
  );
}

// CropCalendar Component integrated within the file
const CropCalendar = ({
  fieldId,
  farmName,
  farmerInput,
  onAskPandoy,
}: {
  /** Digisaka farm / field id — required or the stream API is never called. */
  fieldId: string | number | undefined;
  farmName?: string | null;
  farmerInput?: (Partial<CropCalendarFormValues> & {
    crop_establishment_date?: string;
    crop_variety?: string;
  }) | null;
  onAskPandoy?: (pasteText: string, payload: unknown) => void;
}) => {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);
  const [submittedLocal, setSubmittedLocal] = useState<CropCalendarPrefill | null>(null);
  const [form, setForm] = useState<CropCalendarFormValues>(EMPTY_CROP_CALENDAR_FORM);

  const [cropCalendarData, setCropCalendarData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamProgress, setStreamProgress] = useState<{
    phase: string;
    label: string;
    progress: number | null;
  } | null>(null);
  const [fieldCropInfo, setFieldCropInfo] = useState<FieldCropInformation | null>(null);
  const [fieldCropLoading, setFieldCropLoading] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [farmWeather, setFarmWeather] = useState<FarmCurrentWeather | null>(null);
  const [viewMode, setViewMode] = useState<'sample' | 'live'>('live');
  const [formExpanded, setFormExpanded] = useState(true);
  const [activeSampleKey, setActiveSampleKey] = useState<CropCalendarSampleKey>('rice');
  const [introDone, setIntroDone] = useState(() => readCropCalendarIntroSeen());
  const cropCalendarSamples = useMemo(() => buildCropCalendarSamples(), []);
  const activeSample =
    cropCalendarSamples.find((sample) => sample.key === activeSampleKey) ?? cropCalendarSamples[0];
  const farmerInputRef = useRef(farmerInput);
  farmerInputRef.current = farmerInput;

  const effectiveFarmerInput = useMemo(() => {
    if (editingDetails) return null;
    return submittedLocal;
  }, [submittedLocal, editingDetails]);

  useEffect(() => {
    setSubmittedLocal(null);
    setCropCalendarData(null);
    setError(null);
    setForm(EMPTY_CROP_CALENDAR_FORM);
    setFieldCropInfo(null);
    setEditingDetails(false);
    setFarmWeather(null);
    setViewMode('live');
    setFormExpanded(true);
    setActiveSampleKey('rice');
    setIntroDone(readCropCalendarIntroSeen());
    setFieldCropLoading(
      fieldId != null &&
        String(fieldId).trim() !== '' &&
        String(fieldId) !== 'undefined' &&
        String(fieldId) !== 'null'
    );
  }, [fieldId]);

  useEffect(() => {
    if (!farmerInput) return;
    if (farmerInput.crop_type?.trim() || farmerInput.crop_variety?.trim()) {
      writeCropCalendarIntroSeen();
      setIntroDone(true);
    }
    setForm((prev) => ({
      ...prev,
      crop_type: farmerInput.crop_type || farmerInput.crop_variety || prev.crop_type,
      planting_method: farmerInput.planting_method || prev.planting_method,
      sowing_date: farmerInput.sowing_date || farmerInput.crop_establishment_date || prev.sowing_date,
      planting_date: farmerInput.planting_date || prev.planting_date,
      expected_harvest_date: farmerInput.expected_harvest_date || prev.expected_harvest_date,
      cropping_season: farmerInput.cropping_season || prev.cropping_season,
    }));
  }, [farmerInput]);

  const farmIdOk =
    fieldId != null &&
    String(fieldId).trim() !== '' &&
    String(fieldId) !== 'undefined' &&
    String(fieldId) !== 'null';

  useEffect(() => {
    if (!farmIdOk) {
      setFieldCropLoading(false);
      setFieldCropInfo(null);
      return;
    }

    const ac = new AbortController();
    const loadFieldCrop = async () => {
      setFieldCropLoading(true);
      try {
        const info = await fetchFieldCropInformation(fieldId as string | number, ac.signal);
        if (ac.signal.aborted) return;
        setFieldCropInfo(info);
        const parent = farmerInputRef.current;
        if (parent?.crop_type?.trim() || parent?.crop_variety?.trim()) {
          setForm({
            crop_type: parent.crop_type || parent.crop_variety || '',
            planting_method: parent.planting_method || '',
            sowing_date: parent.sowing_date || parent.crop_establishment_date || '',
            planting_date: parent.planting_date || '',
            expected_harvest_date: parent.expected_harvest_date || '',
            cropping_season: parent.cropping_season || '',
          });
          return;
        }
        setForm(formValuesFromFieldCrop(info));
      } catch (err: unknown) {
        if ((err as Error)?.name === 'AbortError') return;
        console.error('Error fetching field crop information:', err);
        if (!ac.signal.aborted) setFieldCropInfo(null);
      } finally {
        if (!ac.signal.aborted) setFieldCropLoading(false);
      }
    };

    void loadFieldCrop();
    return () => ac.abort();
  }, [fieldId, farmIdOk]);

  useEffect(() => {
    if (viewMode !== 'live') {
      setLoading(false);
      setError(null);
      setStreamProgress(null);
      return;
    }

    const ready =
      farmIdOk &&
      !!effectiveFarmerInput?.crop_establishment_date &&
      !!effectiveFarmerInput?.crop_variety?.trim();
    if (!ready) {
      setCropCalendarData(null);
      setLoading(false);
      setError(null);
      setStreamProgress(null);
      return;
    }

    const ac = new AbortController();

    /** sse-starlette defaults to CRLF (\r\n); splitting only on \n\n never matches \r\n\r\n. */
    const normalizeSseBuffer = (s: string) => s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    const shiftOneSseMessage = (buffer: string): { msg: { event: string; data: string } | null; rest: string } => {
      const nl = buffer.indexOf('\n\n');
      if (nl === -1) return { msg: null, rest: buffer };
      const block = buffer.slice(0, nl);
      const rest = buffer.slice(nl + 2);
      let event = 'message';
      let data = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      return { msg: { event, data }, rest };
    };

    const fetchCropCalendarStream = async () => {
      const requestBody = {
        establishment_date: effectiveFarmerInput.crop_establishment_date,
        crop_variety: effectiveFarmerInput.crop_variety.trim(),
        farm_id: String(fieldId),
        planting_method: effectiveFarmerInput.planting_method || undefined,
        sowing_date: effectiveFarmerInput.sowing_date || undefined,
        planting_date: effectiveFarmerInput.planting_date || undefined,
        expected_harvest_date: effectiveFarmerInput.expected_harvest_date || undefined,
        cropping_season: effectiveFarmerInput.cropping_season || undefined,
      };
      const cached = readCropCalendarCache(requestBody);
      if (cached) {
        setError(null);
        setStreamProgress(null);
        setCropCalendarData(cached);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setCropCalendarData(null);
      setStreamProgress({
        phase: 'starting',
        label: 'Connecting…',
        progress: 0.05
      });

      try {
        const base = getFarmWebgisApiBaseUrl();
        const res = await fetch(
          `${base}/api/crop-calendar/analyze/stream`,
          digisakaAuthRequestInit({
            method: 'POST',
            headers: {
              ...gptInferenceJsonHeaders(),
              Accept: 'text/event-stream',
            },
            body: JSON.stringify(requestBody),
            signal: ac.signal,
          })
        );

        if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          try {
            const j = await res.json();
            detail = typeof j?.detail === 'string' ? j.detail : detail;
          } catch {
            /* ignore */
          }
          throw new Error(detail);
        }

        const reader = res.body?.getReader();
        if (!reader) throw new Error('No response stream');

        const decoder = new TextDecoder();
        let buf = '';
        let gotResult = false;

        while (true) {
          const { done, value } = await reader.read();
          if (value) buf = normalizeSseBuffer(buf + decoder.decode(value, { stream: true }));
          while (true) {
            const { msg, rest } = shiftOneSseMessage(buf);
            if (!msg) {
              buf = rest;
              break;
            }
            buf = rest;
            if (msg.event === 'progress') {
              try {
                const j = JSON.parse(msg.data) as { phase?: string; label?: string; progress?: number | null };
                setStreamProgress({
                  phase: j.phase ?? 'progress',
                  label: j.label ?? 'Working…',
                  progress: j.progress ?? null
                });
              } catch {
                /* ignore */
              }
            } else if (msg.event === 'result') {
              try {
                const parsed = JSON.parse(msg.data);
                setCropCalendarData(parsed);
                writeCropCalendarCache(requestBody, parsed);
                gotResult = true;
              } catch (e) {
                console.error('crop calendar result parse', e);
                setError('Could not read crop calendar result');
              }
            } else if (msg.event === 'error') {
              try {
                const j = JSON.parse(msg.data) as { detail?: string };
                setError(j.detail || 'Crop calendar stream error');
              } catch {
                setError(msg.data || 'Crop calendar stream error');
              }
            }
          }
          if (done) break;
        }

        if (!gotResult) {
          setError((e) => e ?? 'No data returned from crop calendar');
        }
      } catch (err: unknown) {
        if ((err as Error)?.name === 'AbortError') return;
        console.error('Error fetching crop calendar stream:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch crop calendar data');
      } finally {
        setLoading(false);
        setStreamProgress(null);
      }
    };

    fetchCropCalendarStream();
    return () => ac.abort();
  }, [fieldId, effectiveFarmerInput, farmIdOk, viewMode]);

  useEffect(() => {
    if (viewMode !== 'live' || !farmIdOk || !cropCalendarData) {
      setFarmWeather(null);
      return;
    }
    let cancelled = false;
    fetchFarmCurrentWeather(fieldId as string | number).then((w) => {
      if (!cancelled) setFarmWeather(w);
    });
    return () => {
      cancelled = true;
    };
  }, [farmIdOk, fieldId, cropCalendarData, viewMode]);

  const handleLoadCalendar = () => {
    const prefill = toCropCalendarPrefill(form);
    if (!prefill) return;
    setSubmittedLocal(prefill);
    setEditingDetails(false);
    setViewMode('live');
    setFormExpanded(false);
  };

  const handleSelectSample = (key: CropCalendarSampleKey) => {
    const sample = cropCalendarSamples.find((entry) => entry.key === key) ?? cropCalendarSamples[0];
    setActiveSampleKey(sample.key);
    setViewMode('sample');
    setError(null);
    setLoading(false);
    setStreamProgress(null);
  };

  const handleCloseSample = () => {
    setViewMode('live');
  };

  const ccCard = isDark
    ? 'rounded-2xl border border-[#2d3748] bg-[#1c2432] p-3.5'
    : 'rounded-2xl border border-[#e5e7eb] bg-white p-3.5';
  const ccLabel = `text-xs font-bold uppercase tracking-wide ${isDark ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`;
  const ccBtn = `inline-flex min-h-[44px] items-center justify-center rounded-xl border px-3 text-sm font-semibold active:scale-[0.98] ${
    isDark ? 'border-[#2d3748] bg-[#161b22] text-[#e6edf3]' : 'border-[#e5e7eb] bg-white text-[#111827]'
  }`;
  const ccBtnPri = `inline-flex min-h-[44px] w-full items-center justify-center rounded-xl px-3 text-sm font-semibold text-white active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50`;
  const ccInput = `w-full min-h-[44px] rounded-[10px] border px-2.5 text-sm focus:outline-none focus:ring-1 ${
    isDark
      ? 'border-[#2d3748] bg-[#161b22] text-[#e6edf3] placeholder:text-[#6e7681] focus:border-[#34d399] focus:ring-[#34d399]/30'
      : 'border-[#e5e7eb] bg-[#f3f4f6] text-[#111827] placeholder:text-[#6b7280] focus:border-[#008540] focus:ring-[#008540]/30'
  }`;
  const ccFieldLabel = `mb-[3px] mt-2.5 block text-[13.5px] font-semibold first:mt-0 ${
    isDark ? 'text-[#e6edf3]' : 'text-[#111827]'
  }`;

  const formComplete =
    !!effectiveFarmerInput?.crop_establishment_date && !!effectiveFarmerInput?.crop_variety?.trim();
  const formReady = isCropCalendarFormReady(form);
  const showFieldCropCard = fieldCropHasDisplayableInfo(fieldCropInfo);

  const formHelperText = !farmIdOk
    ? 'Needs: a selected field.'
    : !formReady
      ? 'Needs: crop type, planting method, and sowing date.'
      : '';

  const renderModeSwitch = () => (
    <div
      className={`sticky top-0 z-10 mb-2.5 flex rounded-[14px] border p-1 ${
        isDark ? 'border-[#2d3748] bg-[#1c2432]' : 'border-[#e5e7eb] bg-white'
      }`}
      role="tablist"
      aria-label="Crop calendar mode"
    >
      {(['live', 'sample'] as const).map((mode) => {
        const selected = viewMode === mode;
        return (
          <button
            key={mode}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => {
              if (mode === 'sample') handleSelectSample(activeSampleKey);
              else handleCloseSample();
            }}
            className={`min-h-[44px] flex-1 rounded-[11px] text-sm font-semibold transition-colors ${
              selected
                ? isDark
                  ? 'bg-[#34d399] text-[#06210f]'
                  : 'bg-[#008540] text-white'
                : isDark
                  ? 'text-[#9ca3af]'
                  : 'text-[#6b7280]'
            }`}
          >
            {mode === 'live' ? 'My field' : 'Sample'}
          </button>
        );
      })}
    </div>
  );

  const renderSamplePicker = () => (
    <div className={ccCard}>
      <div className={ccLabel}>Just exploring?</div>
      <p className={`mt-1 text-sm ${isDark ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}>
        Preview a demo calendar — no field needed.
      </p>
      <div className="mt-2 flex flex-row flex-wrap gap-2">
        {cropCalendarSamples.map((sample) => (
          <button
            key={sample.key}
            type="button"
            onClick={() => handleSelectSample(sample.key)}
            className={`${ccBtn} min-w-0 flex-1 px-3 text-center`}
          >
            {sample.label}
          </button>
        ))}
      </div>
    </div>
  );

  const renderSampleBanner = () => (
    <div
      className={`mb-2.5 flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm ${
        isDark ? 'bg-[#142a44] text-[#7ab4f2]' : 'bg-[#e1edfb] text-[#1d5fa8]'
      }`}
    >
      <span>
        <strong>Demo data — not your field.</strong> {activeSample.subtitle}.
      </span>
      <button type="button" onClick={handleCloseSample} className={`${ccBtn} shrink-0 px-3`}>
        Close
      </button>
    </div>
  );

  const renderFormSummary = () => (
    <div className={`${ccCard} flex items-center justify-between gap-3`}>
      <div className="min-w-0">
        <p className={`truncate text-sm font-bold ${ap.heading}`}>
          {[form.crop_type, form.planting_method].filter(Boolean).join(' · ') || 'Crop details'}
        </p>
        <p className={`truncate text-[13px] ${ap.faint}`}>
          {form.sowing_date ? `Sown ${formatReportDate(form.sowing_date)}` : 'Crop details saved'}
          {showFieldCropCard ? ' · Saved record' : ''}
        </p>
      </div>
      <button type="button" className={`${ccBtn} shrink-0 px-3`} onClick={() => setFormExpanded(true)}>
        Edit
      </button>
    </div>
  );

  const renderYourFieldSection = () => {
    const showSummary =
      viewMode === 'live' && formComplete && !!cropCalendarData && !formExpanded && !loading && !error;

    if (showSummary) return renderFormSummary();

    return (
      <div className={ccCard}>
        <div className={ccLabel}>Your field</div>
        {!farmIdOk && (
          <div
            className={`mt-2 rounded-xl px-3 py-2.5 text-sm ${
              isDark ? 'bg-[#142a44] text-[#7ab4f2]' : 'bg-[#e1edfb] text-[#1d5fa8]'
            }`}
          >
            Select a saved field on the map first. We’ll fill in your crop details.
          </div>
        )}
        {farmIdOk && showFieldCropCard && (
          <div
            className={`mt-2 rounded-xl px-3 py-2.5 text-sm ${
              isDark ? 'bg-[#1a3324] text-[#34d399]' : 'bg-[#e3f2e8] text-[#008540]'
            }`}
          >
            Saved crop details prefilled — confirm and load.
          </div>
        )}
        {farmIdOk && !showFieldCropCard && !fieldCropLoading && (
          <p className={`mt-1 text-sm ${isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}`}>
            Add crop details for your field.
          </p>
        )}
        {fieldCropLoading && (
          <p className={`mt-1 text-sm ${isDark ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}>
            Checking saved crop records…
          </p>
        )}
        <CropCalendarInputs
          value={form}
          onChange={setForm}
          variant={formExpanded || editingDetails ? 'full' : 'entry'}
          inputClass={ccInput}
          labelClass={ccFieldLabel}
          selectClass={ccInput}
        />
        <button
          type="button"
          onClick={handleLoadCalendar}
          disabled={!farmIdOk || !formReady || loading}
          className={`${ccBtnPri} mt-3`}
          style={!farmIdOk || !formReady || loading ? undefined : { backgroundColor: isDark ? '#34d399' : '#008540', color: isDark ? '#06210f' : '#fff' }}
        >
          Load crop calendar
        </button>
        {formHelperText && (
          <p className={`mt-1.5 text-[12.5px] ${isDark ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}>
            {formHelperText}
          </p>
        )}
      </div>
    );
  };

  const progressPhaseIndex = (phase: string) => {
    if (phase === 'starting' || phase === 'resolving_field') return 0;
    if (phase.includes('greenness') || phase.includes('series') || phase.includes('ndvi')) return 1;
    if (phase.includes('radar') || phase.includes('weather')) return 2;
    if (phase === 'ai_analyzing' || phase.includes('llm')) return 3;
    return 1;
  };

  const renderLiveStatus = () => {
    if (formComplete && !farmIdOk) {
      return (
        <div className={`${ccCard} border-amber-500/30`}>
          <p className={`text-sm font-semibold ${ap.heading}`}>Select a saved field with a valid farm ID.</p>
        </div>
      );
    }

    if (formComplete && loading) {
      const pct = streamProgress?.progress != null ? Math.round(streamProgress.progress * 100) : 62;
      const phase = streamProgress?.phase ?? 'fetching_greenness';
      const step = progressPhaseIndex(phase);
      const phases = ['Connected', 'Found field', 'Greenness', 'Crop story'];
      return (
        <div className={ccCard}>
          <div className={ccLabel}>Reading your field from space…</div>
          <p className={`mt-1 text-sm font-bold ${ap.heading}`}>
            {streamProgress?.label ?? 'Fetching greenness'} · {pct}%
          </p>
          <div className={`my-2.5 h-2.5 overflow-hidden rounded-full ${isDark ? 'bg-[#2d3748]' : 'bg-[#e5e7eb]'}`}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.max(pct, 8)}%`,
                backgroundColor: isDark ? '#34d399' : '#008540',
              }}
            />
          </div>
          <div className={`flex justify-between text-[11.5px] ${ap.faint}`}>
            {phases.map((label, i) => (
              <span key={label} className={i === step ? (isDark ? 'font-bold text-[#34d399]' : 'font-bold text-[#008540]') : ''}>
                {i < step ? '✓ ' : i === step ? '● ' : '○ '}
                {label}
              </span>
            ))}
          </div>
          <p className={`mt-2 text-[13px] ${ap.muted}`}>
            Usually under a minute. We pick the clearest satellite views of your field.
          </p>
        </div>
      );
    }

    if (formComplete && error) {
      return (
        <div className={`${ccCard} ${isDark ? 'border-[#401a17]' : 'border-[#fde7e5]'}`}>
          <div
            className={`mb-2.5 rounded-xl px-3 py-2.5 text-sm ${
              isDark ? 'bg-[#401a17] text-[#f28b82]' : 'bg-[#fde7e5] text-[#a8261d]'
            }`}
          >
            <strong>We couldn’t load your field.</strong>
          </div>
          <p className={`text-sm ${ap.body}`}>Check your connection, then try again. Your details are saved.</p>
          <p className={`mt-1 text-xs ${ap.muted}`}>{error}</p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className={`${ccBtnPri} flex-1`}
              style={{ backgroundColor: isDark ? '#34d399' : '#008540', color: isDark ? '#06210f' : '#fff' }}
              onClick={handleLoadCalendar}
            >
              Try again
            </button>
            <button type="button" className={`${ccBtn} flex-1`} onClick={() => handleSelectSample('rice')}>
              See a sample
            </button>
          </div>
        </div>
      );
    }

    if (formComplete && !cropCalendarData && !loading) {
      return (
        <div className={`${ccCard} text-center`}>
          <div className="text-3xl" aria-hidden>🛰</div>
          <p className={`mt-2 font-bold ${ap.heading}`}>No satellite data found</p>
          <p className={`mt-1 text-sm ${ap.muted}`}>
            We found no clear views of this field since sowing. Try again in a few days or edit crop details.
          </p>
          <button type="button" className={`${ccBtn} mt-3 w-full`} onClick={() => setFormExpanded(true)}>
            Edit crop details
          </button>
        </div>
      );
    }

    if (formComplete && cropCalendarData) {
      return renderCalendarReport(
        cropCalendarData,
        fieldId,
        farmName,
        farmWeather,
        pickPrimaryFarmingPractice(fieldCropInfo)?.status,
        false
      );
    }

    return null;
  };

  const renderCalendarReport = (
    data: any,
    reportFieldId: string | number | undefined,
    reportFarmName: string | null | undefined,
    weather: FarmCurrentWeather | null,
    cropStatus: string | null | undefined,
    isDemo: boolean
  ) => {
    const ndviSeries: Array<{ date: string; ndvi: number; cloud_percentage?: number }> = data?.ndvi_series ?? [];
    const llm = data?.llm_analysis;
    const hasData =
      data?.success === true &&
      ((ndviSeries.length > 0 || data?.llm_success) || (data?.data?.length ?? 0) > 0);

    const farmerReport = buildCropCalendarReport({
      data,
      farmId: reportFieldId,
      farmName: reportFarmName,
      weather,
      cropStatus,
    });

    if (!hasData) {
      return (
        <div className={`${ccCard} text-center`}>
          <p className={ap.muted}>No data found</p>
        </div>
      );
    }

    return (
      <div className="space-y-2.5">
        {data?.llm_error && (
          <div
            className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm ${
              isDark ? 'bg-[#3a2c0e] text-[#f0b64a]' : 'bg-[#fff1d6] text-[#9a5b00]'
            }`}
          >
            <span>
              <strong>Crop story unavailable.</strong> Greenness is ready; stage is estimated by rules.
            </span>
            <button type="button" className={ccBtn} onClick={handleLoadCalendar}>
              Retry
            </button>
          </div>
        )}
        <CropCalendarReportCard
          report={farmerReport}
          isDemo={isDemo}
          chartRows={ndviSeries.map((row) => ({
            date: row.date,
            ndvi: row.ndvi,
            cloudPct: row.cloud_percentage ?? null,
          }))}
          onDownload={() => downloadCropCalendarPdf(farmerReport)}
          onAsk={
            onAskPandoy
              ? () =>
                  onAskPandoy(buildPandoyCropCalendarPaste(farmerReport), {
                    farmId: farmerReport.farmId,
                    farmName: farmerReport.farmName,
                    cropType: farmerReport.cropType,
                    report: farmerReport,
                  })
              : undefined
          }
        />
        <CropCalendarFieldReadings ndviSeries={ndviSeries} llm={llm} />
      </div>
    );
  };

  const hasLiveReport = viewMode === 'live' && formComplete && !!cropCalendarData && !loading;
  const showExploreSamples =
    viewMode === 'live' && !hasLiveReport && !loading && introDone;
  const showIntro =
    viewMode === 'live' &&
    !introDone &&
    !hasLiveReport &&
    !loading &&
    !(formComplete && !!cropCalendarData);

  const completeIntro = () => {
    writeCropCalendarIntroSeen();
    setIntroDone(true);
  };

  return (
    <div
      className={`space-y-2.5 rounded-[18px] border p-2.5 ${
        isDark ? 'border-[#2d3748] bg-[#161b22]' : 'border-[#e5e7eb] bg-[#f3f4f6]'
      }`}
    >
      {renderModeSwitch()}
      {viewMode === 'sample' && (
        <>
          {renderSampleBanner()}
          {renderCalendarReport(
            activeSample.response,
            activeSample.response.farm_id,
            activeSample.farmName,
            null,
            activeSample.response.crop_status,
            true
          )}
        </>
      )}
      {viewMode === 'live' && showIntro && (
        <CropCalendarIntro
          isDark={isDark}
          onComplete={completeIntro}
          onTrySample={() => {
            completeIntro();
            handleSelectSample('rice');
          }}
        />
      )}
      {viewMode === 'live' && !showIntro && (
        <>
          {!(formComplete && loading) && renderYourFieldSection()}
          {renderLiveStatus()}
        </>
      )}
      {showExploreSamples && renderSamplePicker()}
    </div>
  );
};

function isRequestAbortError(error: unknown): boolean {
  if (axios.isCancel(error)) return true;
  if (!error || typeof error !== 'object') return false;
  const name = (error as { name?: string }).name;
  const code = (error as { code?: string }).code;
  return name === 'AbortError' || name === 'CanceledError' || code === 'ERR_CANCELED';
}

export default function AnalysisPanel({
  isOpen,
  onToggle,
  selectedField,
  fieldGeoJson,
  onAnalysisRun,
  onZoomToField,
  onCloseAnalysis,
  onBackToFields,
  processing = false,
  processingType = '',
  analysisResults,
  indexData,
  vhiData,
  indexHeatMapData,
  timeSeriesData,
  filteredTimeSeriesData,
  onTimeSeriesSliderChange,
  externalTimeSeriesDate,
  // Cloud filtering props
  showCloudFilter = false,
  maxCloudCoverage = DEFAULT_MAX_CLOUD_COVERAGE_PERCENT,
  onCloudFilterChange,
  // Analysis mode
  analysisMode = 'field-level',
  // Layer control props for large-scale mode
  layerVisibility,
  layerOpacity,
  onLayerToggle,
  onOpacityChange,
  legendUrls,
  cropAreaLegends,
  loadingLayers,
  onForecastDataUpdate,
  onForecastResultsUpdate,
  onStrawBurnResultsUpdate,
  onFloodLayerShow,
  onBSRIDateRangeUpdate,
  bsriIsLoading,
  riceGrowthStageLoading,
  irrigationTypeLoading,
  onBSRIAnalyseRequest,
  onFarmForecastResultsUpdate,
  onStrawBurnDataFetch,
  strawBurnIsLoading,
  strawBurnData,
  onTreeDetectionDataUpdate,
  onCrownSegmentationResultsUpdate,
  onCropfieldDelineationUpdate,
  mapCenter,
  mapZoom,
  onExpandedTabChange,
  onFloodDamageSelect,
  selectedFloodDamageId = null,
  multiFieldMode = false,
  multiFieldSelection = [],
  onMultiFieldProcessingChange,
  onLSTPHDateRangeUpdate,
  lstIsLoading,
  onVHIPHDateRangeUpdate,
  vhiIsLoading,
  clearMultiTemporalAPITiles,
  getOlMap,
  pestRiskPestKey,
  onPestRiskPestChange,
  onRequestDronePrescriptionStats,
  onClearDronePrescriptionStats,
  droneSites,
  zonalState,
  zonalLayer,
  zonalFrameLabel,
  zonalPestLabel,
  zonalProvinces,
  zonalSelectedProvince,
  onZonalProvinceChange,
  onZonalMunicipalitySelect,
  onShowDroneOrthoIndexMap,
  onClearDroneOrthoIndexMap,
  cropCalendarFarmerInput = null,
  cropCalendarOpenRequest = 0,
  droneDataOpenRequest = 0,
  onCropCalendarAskPandoy,
  mapScreenshotRef: mapScreenshotRefProp,
  allowedLayerKeys,
  displayMode = 'drawer',
  controlledLargeScaleTab,
  onControlledLargeScaleTabChange,
  onMinimize,
  onActiveFarmTabChange,
  farmTabsInHeader = false,
  onFarmHeaderTabsChange,
  requestedFarmTab = null,
  initialFarmTab = null,
  cancelFarmWorkEpoch = 0,
  pinnedLargeScaleSection = null,
  infoSectionId = null,
  onInfoSectionHandled,
  onClosePinnedSection,
  externalHeatMapObservationDate = null,
  onHeatMapIndexChange,
  heatMapSatellite = 'S2',
}: AnalysisPanelProps) {
  /** Saved farm blocks open Alerts & Forecasts; demo/unsaved fields keep Index Map. */
  const defaultAnalysisTabForField = (farmId: unknown): string => {
    const id = farmId == null || farmId === '' ? '' : String(farmId).trim();
    if (!id || id === 'demo_field' || id.toLowerCase().includes('demo')) return 'heatmap';
    return 'alerts';
  };

  const layerAllowedEntitlementSet = useMemo(
    () => buildAllowedEntitlementSet(allowedLayerKeys),
    [allowedLayerKeys]
  );

  const canFarmHeatMap = useMemo(
    () => isEntitlementAllowed(layerAllowedEntitlementSet, 'heatMap'),
    [layerAllowedEntitlementSet]
  );
  const canFarmTimeSeries = useMemo(
    () => isEntitlementAllowed(layerAllowedEntitlementSet, 'timeSeries'),
    [layerAllowedEntitlementSet]
  );
  const canFarmCropCalendar = useMemo(
    () => isEntitlementAllowed(layerAllowedEntitlementSet, 'cropCalendar'),
    [layerAllowedEntitlementSet]
  );
  const canFarmClimate = useMemo(
    () => isEntitlementAllowed(layerAllowedEntitlementSet, 'climate'),
    [layerAllowedEntitlementSet]
  );
  const canFarmRiceForecast = useMemo(
    () => isFarmRiceYieldForecastAllowed(layerAllowedEntitlementSet),
    [layerAllowedEntitlementSet]
  );
  const canFarmBlsForecast = useMemo(
    () =>
      isEntitlementAllowed(layerAllowedEntitlementSet, 'blsForecast') ||
      isEntitlementAllowed(layerAllowedEntitlementSet, 'bls_forecast') ||
      isEntitlementAllowed(layerAllowedEntitlementSet, 'bsri'),
    [layerAllowedEntitlementSet]
  );
  const canFarmDroneData = useMemo(
    () => isPanelTabUnlocked(layerAllowedEntitlementSet, 'droneData'),
    [layerAllowedEntitlementSet]
  );
  const canFarmFieldReport = useMemo(
    () =>
      isEntitlementAllowed(layerAllowedEntitlementSet, 'fieldReport') ||
      isEntitlementAllowed(layerAllowedEntitlementSet, 'field_report'),
    [layerAllowedEntitlementSet]
  );

  const { theme, isDark } = useTheme();
  const isInlineCardLayout = displayMode === 'inline-card';
  const ap = getAnalysisPanelTheme(isDark, isInlineCardLayout);
  const rsEmerald = getAnalysisResultStripTheme(isDark, 'emerald');
  const panelBodyBg = isInlineCardLayout ? ap.inlineBodyBg : isDark ? 'bg-gray-900' : 'bg-white';
  const panelMutedText = isDark ? 'text-gray-400' : 'text-gray-600';
  const panelTabInactiveClass = ap.tabInactive;
  const panelTabActiveClass = ap.tabActive;
  const panelHeaderActionHover = ap.headerActionHover;

  const [selectedIndex, setSelectedIndex] = useState('ndvi');
  const [selectedTimeRange, setSelectedTimeRange] = useState('Last Month');
  const [startDate, setStartDate] = useState('2025-05-01');
  const [endDate, setEndDate] = useState('2025-08-06');
  const [activeAnalysisTab, setActiveAnalysisTab] = useState(
    () => (initialFarmTab && initialFarmTab.trim()) || 'alerts'
  );
  const skipDefaultTabOnceRef = useRef(Boolean(initialFarmTab && initialFarmTab.trim()));
  const skipAlertsAutoOpenRef = useRef(
    Boolean(initialFarmTab && initialFarmTab.trim() && initialFarmTab !== 'alerts')
  );
  const activeAnalysisTabRef = useRef(activeAnalysisTab);
  activeAnalysisTabRef.current = activeAnalysisTab;
  const invalidateFarmJobsRef = useRef<() => void>(() => {});
  const selectAnalysisTab = (tab: string) => {
    if (activeAnalysisTabRef.current !== tab) {
      invalidateFarmJobsRef.current();
    }
    activeAnalysisTabRef.current = tab;
    setActiveAnalysisTab(tab);
  };
  const [currentLayerTab, setCurrentLayerTab] = useState<string | null>(null);
  const [observationDate, setObservationDate] = useState('');
  const [observationDates, setObservationDates] = useState<string[]>([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const heatMapReqGenRef = useRef(0);
  const farmJobGenRef = useRef(0);
  const sseAbortRef = useRef<AbortController | null>(null);
  const heatMapAbortRef = useRef<AbortController | null>(null);
  const climateAbortRef = useRef<AbortController | null>(null);
  const farmForecastAbortRef = useRef<AbortController | null>(null);
  const burnAbortRef = useRef<AbortController | null>(null);
  const lastAutoIndexMapKeyRef = useRef<string | null>(null);

  const beginFarmJob = () => {
    const jobGen = ++farmJobGenRef.current;
    heatMapReqGenRef.current += 1;
    heatMapAbortRef.current?.abort();
    sseAbortRef.current?.abort();
    climateAbortRef.current?.abort();
    farmForecastAbortRef.current?.abort();
    burnAbortRef.current?.abort();
    return jobGen;
  };
  const feedbackLocationOverlayRef = useRef<Overlay | null>(null);

  useEffect(() => {
    return () => {
      const overlay = feedbackLocationOverlayRef.current;
      if (!overlay) return;
      overlay.getMap()?.removeOverlay(overlay);
      overlay.getElement()?.remove();
      feedbackLocationOverlayRef.current = null;
    };
  }, []);

  useEffect(() => {
    onActiveFarmTabChange?.(activeAnalysisTab);
  }, [activeAnalysisTab, onActiveFarmTabChange]);

  useEffect(() => {
    if (!requestedFarmTab?.id) return;
    selectAnalysisTab(requestedFarmTab.id);
  }, [requestedFarmTab]);

  // Index Map cloud / load errors must not linger on Timeseries, Climate, etc.
  useEffect(() => {
    if (activeAnalysisTab !== 'heatmap') {
      setProcessingError('');
      setProcessingStage('');
    }
  }, [activeAnalysisTab]);

  // Notify parent of index for the observation bar — only when the index value changes
  const onHeatMapIndexChangeRef = useRef(onHeatMapIndexChange);
  onHeatMapIndexChangeRef.current = onHeatMapIndexChange;
  useEffect(() => {
    if (activeAnalysisTab !== 'heatmap') return;
    onHeatMapIndexChangeRef.current?.(selectedIndex);
  }, [selectedIndex, activeAnalysisTab]);

  useEffect(() => {
    if (externalHeatMapObservationDate) {
      setObservationDate(externalHeatMapObservationDate);
    }
  }, [externalHeatMapObservationDate]);

  // Report states
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportKind, setReportKind] = useState<'brief' | 'fieldHealth'>('brief');
  const [briefLang, setBriefLang] = useState<FarmBriefLang>('en');
  const [selectedReportIndices, setSelectedReportIndices] = useState(['ndvi', 'evi', 'msavi2']);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Enhanced processing states
  const [isProcessingHeatMap, setIsProcessingHeatMap] = useState(false);
  const [isProcessingTimeSeries, setIsProcessingTimeSeries] = useState(false);
  const [isProcessingCropCalendar, setIsProcessingCropCalendar] = useState(false);
  const [isProcessingClimate, setIsProcessingClimate] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState('');
  const [processingError, setProcessingError] = useState('');

  // Farm-level rice yield forecast — same rolling season as large-scale (Aug → Jun, incl. May/Jun)
  const farmForecastMonths = buildFarmRiceForecastMonthOptions(new Date());
  const [farmForecastMonth, setFarmForecastMonth] = useState<string>('');
  const [isFarmForecastLoading, setIsFarmForecastLoading] = useState(false);
  const [farmForecastError, setFarmForecastError] = useState<string | null>(null);
  const [farmForecastResult, setFarmForecastResult] = useState<any | null>(null);
  const [isGeneratingFarmForecastPDF, setIsGeneratingFarmForecastPDF] = useState(false);

  const [farmNdviAlert, setFarmNdviAlert] = useState<FarmIndexAlertRecord | null>(null);
  const [farmNdmiAlert, setFarmNdmiAlert] = useState<FarmIndexAlertRecord | null>(null);
  const [farmForecastAlerts, setFarmForecastAlerts] = useState<FarmForecastAlertsResponse | null>(
    null
  );
  const [farmAlertsLoading, setFarmAlertsLoading] = useState(false);

  // CADI drought timeseries — fetched once per field, lives here so it survives tab switches
  const [cadiState, setCADIState] = useState<FarmCADITimeseriesState>({ status: 'idle' });
  const cadiAbortRef = useRef<AbortController | null>(null);
  const [cadiSending, setCADISending] = useState(false);
  const [cadiSendFeedback, setCADISendFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [cadiFeedbackOpen, setCADIFeedbackOpen] = useState(false);
  const [cadiFeedbackLoading, setCADIFeedbackLoading] = useState(false);
  const [cadiFeedbackItems, setCADIFeedbackItems] = useState<AlertFeedbackRecord[]>([]);
  const [cadiFeedbackError, setCADIFeedbackError] = useState<string | null>(null);

  // Pest risk timeseries — quarterly COGs, CADI-style cache; last_updated bumped daily
  const [pestState, setPestState] = useState<FarmPestTimeseriesState>({ status: 'idle' });
  const pestAbortRef = useRef<AbortController | null>(null);
  const [pestSending, setPestSending] = useState(false);
  const [pestSendFeedback, setPestSendFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [pestFeedbackOpen, setPestFeedbackOpen] = useState(false);
  const [pestFeedbackLoading, setPestFeedbackLoading] = useState(false);
  const [pestFeedbackItems, setPestFeedbackItems] = useState<AlertFeedbackRecord[]>([]);
  const [pestFeedbackError, setPestFeedbackError] = useState<string | null>(null);

  // Alerts panel filter: which alert category to show
  const [alertsFilter, setAlertsFilter] = useState<'all' | 'drought' | 'pest' | 'cropHealth' | 'weather'>('all');
  const [alertSendingKind, setAlertSendingKind] = useState<FarmAlertKind | null>(null);
  const [alertSendFeedbackByKind, setAlertSendFeedbackByKind] = useState<
    Partial<Record<FarmAlertKind, { type: 'success' | 'error'; message: string }>>
  >({});
  const [farmFeedbackCache, setFarmFeedbackCache] = useState<AlertFeedbackRecord[] | null>(null);
  const [alertFeedbackByKind, setAlertFeedbackByKind] = useState<
    Record<FarmAlertKind, FarmAlertCardFeedbackState>
  >({
    ndvi: { ...EMPTY_ALERT_FEEDBACK_STATE },
    ndmi: { ...EMPTY_ALERT_FEEDBACK_STATE },
  });
  const [forecastAlertSendingId, setForecastAlertSendingId] = useState<number | null>(null);
  const [forecastAlertSendFeedbackById, setForecastAlertSendFeedbackById] = useState<
    Partial<Record<number, { type: 'success' | 'error'; message: string }>>
  >({});
  const [forecastAlertFeedbackById, setForecastAlertFeedbackById] = useState<
    Record<number, FarmAlertCardFeedbackState>
  >({});
  /** Weather detail charts (rain probability / max temp) are collapsed by default. */

  // Climate data states
  const [climateData, setClimateData] = useState<any>(null);
  const [climateParameter, setClimateParameter] = useState('temperature_2m');
  /** `era5_land` = historical ERA5-Land; `ecmwf_ifs_nrt` = ECMWF IFS 15-day NRT forecast */
  const [climateDataset, setClimateDataset] = useState<'era5_land' | 'ecmwf_ifs_nrt'>('era5_land');
  const [climateAvailability, setClimateAvailability] = useState<{
    available_start?: string;
    available_end?: string;
    suggested_start?: string;
    suggested_end?: string;
    creation_time_utc?: string;
    notes?: string;
  } | null>(null);
  const [climateAvailabilityLoading, setClimateAvailabilityLoading] = useState(false);
  const [showClimateFullscreen, setShowClimateFullscreen] = useState(false);
  const [hoveredDataPoint, setHoveredDataPoint] = useState<any>(null);
  const [climateDataView, setClimateDataView] = useState<'daily' | 'weekly'>('daily');

  const climateDatasetMeta =
    climateDataset === 'ecmwf_ifs_nrt'
      ? {
          title: 'ECMWF IFS Near-Realtime Forecast',
          detail: '15-day atmospheric forecast · 0.25° (~28 km) · updates every 12 hours',
          loading: 'Loading latest ECMWF IFS forecast…',
        }
      : {
          title: 'ECMWF ERA5-Land Daily Aggregated',
          detail: 'Historical reanalysis · 0.1° (~11 km)',
          loading: 'Loading climate data from ECMWF ERA5-Land…',
        };

  const applyClimateAvailabilityDates = (availability: {
    suggested_start?: string;
    suggested_end?: string;
    available_start?: string;
    available_end?: string;
  }) => {
    // Prefer full available window end (15+ day IFS horizon / latest ERA5 day).
    const start = availability.suggested_start || availability.available_start;
    const end = availability.available_end || availability.suggested_end;
    if (start) setStartDate(start);
    if (end) setEndDate(end);
  };

  const fetchClimateAvailability = async (dataset: 'era5_land' | 'ecmwf_ifs_nrt') => {
    setClimateAvailabilityLoading(true);
    setClimateAvailability(null);
    try {
      const response = await apiClient.get('/climate-availability/', {
        params: { dataset },
      });
      const availability = response.data?.availability || null;
      setClimateAvailability(availability);
      if (availability) {
        applyClimateAvailabilityDates(availability);
      }
      return availability;
    } catch (error) {
      console.error('Failed to load climate availability:', error);
      setClimateAvailability(null);
      return null;
    } finally {
      setClimateAvailabilityLoading(false);
    }
  };

  const handleClimateDatasetChange = (next: 'era5_land' | 'ecmwf_ifs_nrt') => {
    setClimateDataset(next);
    setClimateData(null);
    setProcessingError('');
    const era5Only = new Set(['total_evaporation_sum']);
    const ifsOnly = new Set([
      'temperature_2m_max',
      'temperature_2m_min',
      'dewpoint_temperature_2m',
      'runoff',
      'surface_solar_radiation_downwards',
      'soil_moisture_0_7cm',
      'soil_moisture_7_28cm',
      'soil_moisture_28_100cm',
      'soil_temperature_0_7cm',
      'wind_speed_10m',
    ]);
    if (next === 'ecmwf_ifs_nrt') {
      if (era5Only.has(climateParameter)) {
        setClimateParameter('temperature_2m');
      }
    } else if (ifsOnly.has(climateParameter)) {
      setClimateParameter('temperature_2m');
    }
  };

  // When Climate tab opens, load live GEE availability and auto-fit end/start dates.
  useEffect(() => {
    if (activeAnalysisTab !== 'climate' || !canFarmClimate) return;
    void fetchClimateAvailability(climateDataset);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refresh when tab/dataset/entitlement changes
  }, [activeAnalysisTab, climateDataset, canFarmClimate]);
  const [timeSeriesSliderIndex, setTimeSeriesSliderIndex] = useState(0);
  // Track last generated time series request to disable redundant runs
  const [lastTimeSeriesRequest, setLastTimeSeriesRequest] = useState<{
    index: string;
    startDate: string;
    endDate: string;
  } | null>(null);
  const [feedbackTimeSeriesContext, setFeedbackTimeSeriesContext] = useState<{
    plan: FeedbackTimeSeriesPlan;
    actionsTaken: string[];
    effect?: string;
    pair?: InterventionComparePair | null;
    pairError?: string | null;
  } | null>(null);

  // Sync external farm-level player (MapView) with this chart using the date
  useEffect(() => {
    if (!externalTimeSeriesDate) return;
    const source = filteredTimeSeriesData || timeSeriesData;
    if (!source?.results) return;

    const idx = source.results.findIndex((r: any) => r.date === externalTimeSeriesDate);
    if (idx !== -1 && idx !== timeSeriesSliderIndex) {
      setTimeSeriesSliderIndex(idx);
    }
  }, [externalTimeSeriesDate, filteredTimeSeriesData, timeSeriesData, timeSeriesSliderIndex]);
  const [isPlayingTimeSeries, setIsPlayingTimeSeries] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1000);
  const [showNullTooltip, setShowNullTooltip] = useState(false);

  const timeSeriesRequestUnchanged =
    !!timeSeriesData &&
    !!lastTimeSeriesRequest &&
    lastTimeSeriesRequest.index === selectedIndex &&
    lastTimeSeriesRequest.startDate === startDate &&
    lastTimeSeriesRequest.endDate === endDate;

  // Disable Generate Time Series when already processing, playing, or request unchanged
  const timeSeriesGenerateDisabled =
    !selectedField ||
    processing ||
    isProcessingTimeSeries ||
    isPlayingTimeSeries ||
    timeSeriesRequestUnchanged;

  // Soil moisture specific states
  const [spatialResolutionInfoOpen, setSpatialResolutionInfoOpen] = useState(false);
  const [indexGuideOpen, setIndexGuideOpen] = useState(false);
  const [soilMoistureStats, setSoilMoistureStats] = useState<{
    mean: number;
    std: number;
    metadata: {
      algorithm: string;
      memory_days: number;
      frequency_band: string;
      paper_reference: string;
      units: string;
    };
  } | null>(null);

  // Burn detection states
  const [isProcessingBurnDetection, setIsProcessingBurnDetection] = useState(false);
  const [burnDetectionData, setBurnDetectionData] = useState<any>(null);
  const [burnDetectionStartDate, setBurnDetectionStartDate] = useState('2026-03-01');
  const [burnDetectionEndDate, setBurnDetectionEndDate] = useState('2026-04-30');

  invalidateFarmJobsRef.current = () => {
    farmJobGenRef.current += 1;
    heatMapReqGenRef.current += 1;
    heatMapAbortRef.current?.abort();
    heatMapAbortRef.current = null;
    sseAbortRef.current?.abort();
    sseAbortRef.current = null;
    climateAbortRef.current?.abort();
    climateAbortRef.current = null;
    farmForecastAbortRef.current?.abort();
    farmForecastAbortRef.current = null;
    burnAbortRef.current?.abort();
    burnAbortRef.current = null;
    setIsProcessingHeatMap(false);
    setIsProcessingTimeSeries(false);
    setIsProcessingClimate(false);
    setIsFarmForecastLoading(false);
    setIsProcessingBurnDetection(false);
    setIsPlayingTimeSeries(false);
    setIsProcessingMultiField(false);
  };

  const cancelEpochSeenRef = useRef<number | null>(null);
  useEffect(() => {
    if (cancelEpochSeenRef.current == null) {
      cancelEpochSeenRef.current = cancelFarmWorkEpoch;
      return;
    }
    if (cancelEpochSeenRef.current === cancelFarmWorkEpoch) return;
    cancelEpochSeenRef.current = cancelFarmWorkEpoch;
    invalidateFarmJobsRef.current();
  }, [cancelFarmWorkEpoch]);

  useEffect(() => () => { invalidateFarmJobsRef.current(); }, []);

  // Multi-field time series states
  const [isProcessingMultiField, setIsProcessingMultiField] = useState(false);
  const [multiFieldResult, setMultiFieldResult] = useState<any[] | null>(null);
  const [multiFieldError, setMultiFieldError] = useState<string | null>(null);
  const [multiFieldResponse, setMultiFieldResponse] = useState<any>(null);
  const [showFullscreenChart, setShowFullscreenChart] = useState(false);

  useEffect(() => {
    onMultiFieldProcessingChange?.(isProcessingMultiField);
  }, [isProcessingMultiField, onMultiFieldProcessingChange]);

  const previousFarmToolRef = useRef(activeAnalysisTab);
  const layerVisibilityRef = useRef(layerVisibility);
  layerVisibilityRef.current = layerVisibility;
  const onLayerToggleRef = useRef(onLayerToggle);
  onLayerToggleRef.current = onLayerToggle;
  const onClearDronePrescriptionStatsRef = useRef(onClearDronePrescriptionStats);
  onClearDronePrescriptionStatsRef.current = onClearDronePrescriptionStats;
  const onClearDroneOrthoIndexMapRef = useRef(onClearDroneOrthoIndexMap);
  onClearDroneOrthoIndexMapRef.current = onClearDroneOrthoIndexMap;

  useEffect(() => {
    const previous = previousFarmToolRef.current;
    previousFarmToolRef.current = activeAnalysisTab;
    if (previous === activeAnalysisTab) return;

    if (previous === 'climate') {
      setClimateData(null);
      setShowClimateFullscreen(false);
      setHoveredDataPoint(null);
    }
    if (previous === 'report') {
      setShowReportModal(false);
      setReportData(null);
    }
    if (previous === 'farmForecast') {
      setFarmForecastResult(null);
      setFarmForecastError(null);
    }
    if (previous === 'timeseries') {
      setShowFullscreenChart(false);
      setMultiFieldResult(null);
      setMultiFieldResponse(null);
      setMultiFieldError(null);
    }
    if (previous === 'droneData') {
      const droneKeys = [
        'isabelaPhilriceNDVI',
        'isabelaPhilriceOrtho',
        'caviteSilangNDVI',
        'caviteSilangOrtho',
      ] as const;
      const visibility = layerVisibilityRef.current;
      droneKeys.forEach((key) => {
        if (visibility?.[key]) onLayerToggleRef.current?.(key, false);
      });
      onClearDronePrescriptionStatsRef.current?.();
      onClearDroneOrthoIndexMapRef.current?.();
    }
  }, [activeAnalysisTab]);

  useEffect(() => {
    return () => {
      onMultiFieldProcessingChange?.(false);
    };
  }, [onMultiFieldProcessingChange]);


  // Heat map index options — Landsat cannot use S2 red-edge indices; S1 is SAR-only
  const heatMapIndexOptions = INDEX_MAP_OPTIONS;

  // Time series index options with descriptions
  const timeSeriesIndexOptions = [
    { value: 'ndvi', label: 'NDVI: crop greenness' },
    { value: 'evi', label: 'EVI: crop greenness' },
    { value: 'msavi2', label: 'MSAVI2: greenness on bare soil' },
    { value: 'sipi', label: 'SIPI: leaf pigment stress' },
    { value: 'savi', label: 'SAVI: greenness, soil adjusted' },
    { value: 'vari', label: 'VARI: visible greenness' },
    { value: 'arvi', label: 'ARVI: greenness through haze' },
    { value: 'rgr', label: 'RGR: red-to-green ratio' },
    { value: 'psri', label: 'PSRI: crop aging' },
    { value: 'ndii', label: 'NDII: canopy water' },
    { value: 'ndmi', label: 'NDMI: crop moisture' },
    { value: 'ndwi', label: 'NDWI: surface water' },
    { value: 'rendvi', label: 'RENDVI: red-edge greenness' },
    { value: 'ireci', label: 'IRECI: chlorophyll' },
    { value: 's2rep', label: 'S2REP: red-edge position' },
    { value: 'rvi', label: 'RVI: radar vegetation' },
    { value: 'nbr', label: 'NBR: burn signal' },
    { value: 'ndpi', label: 'NDPI: radar surface' },
    { value: 'sccci', label: 'SCCCI: canopy chlorophyll' },
    { value: 'sentinel1', label: 'Sentinel-1: radar signal' },
  ];

  // Set default date range based on current date
  useEffect(() => {
    const currentDate = new Date();
    const threeMonthsAgo = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);

    setStartDate(threeMonthsAgo.toISOString().split('T')[0]);
    setEndDate(currentDate.toISOString().split('T')[0]);
  }, []);

  // Plain status lines while a time series is generating. Avoid tiles, dates, and satellite names.
  const timeSeriesProcessingStages = [
    'Preparing your field history…',
    'Looking through past photos of your field…',
    'Reading how the crop changed over time…',
    'Putting your chart together…',
    'Almost done…',
  ];

  const timeSeriesProgressLabel = (current: number, total: number) => {
    if (!total || total <= 0) return 'Preparing your field history…';
    const ratio = current / total;
    if (ratio < 0.34) return 'Looking through past photos of your field…';
    if (ratio < 0.67) return 'Reading how the crop changed over time…';
    return 'Putting your chart together…';
  };


  // Function to simulate processing stages
  const simulateTimeSeriesProcessing = () => {
    let currentStage = 0;
    const stageInterval = setInterval(() => {
      if (currentStage < timeSeriesProcessingStages.length) {
        setProcessingStage(timeSeriesProcessingStages[currentStage]);
        setProcessingProgress((currentStage + 1) * 20);
        currentStage++;
      } else {
        clearInterval(stageInterval);
      }
    }, 1000);

    return stageInterval;
  };

  // Function to simulate crop calendar processing

  // Function to fetch observation dates (rolling 2-month window — frontend-cached)
  const fetchObservationDates = async (geometry: any) => {
    setLoadingDates(true);
    try {
      const end = new Date();
      const start = new Date(end.getFullYear(), end.getMonth() - 2, end.getDate());
      const ymd = (d: Date) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };
      const data = await fetchSentinel2ObservationDatesCached({
        geometry,
        start_date: ymd(start),
        end_date: ymd(end),
      });

      const dates = data.dates ?? [];
      setObservationDates(dates);

      if (dates && dates.length > 0 && !externalHeatMapObservationDate) {
        setObservationDate(dates[dates.length - 1]);
      }
    } catch (error) {
      console.error('Error fetching observation dates:', error);
      setObservationDates([]);
    } finally {
      setLoadingDates(false);
    }
  };

  // NDVI/NDMI satellite alerts + forecast alerts for saved fields (farm_id)
  useEffect(() => {
    setFarmNdviAlert(null);
    setFarmNdmiAlert(null);
    setFarmForecastAlerts(null);

    const farmId = selectedField?.farm_id;
    if (!farmId || analysisMode !== 'field-level') {
      setFarmAlertsLoading(false);
      return;
    }

    let cancelled = false;
    const headers = { ...digisakaMeteredAuthHeaders(), 'Content-Type': 'application/json' };

    const load = async () => {
      setFarmAlertsLoading(true);
      try {
        const base = DIGISAKA_API_ORIGIN + '/api';
        const id = encodeURIComponent(String(farmId));
        // Satellite Vegetation/Moisture snapshot alerts paused for now.
        // Keep forecast alerts (ndvi_forecast / ndmi_forecast / weather).
        const [forecastAlerts] = await Promise.all([
          fetchFarmForecastAlerts(farmId, headers),
        ]);

        if (cancelled) return;

        setFarmNdviAlert(null);
        setFarmNdmiAlert(null);
        setFarmForecastAlerts(forecastAlerts);
      } catch (e) {
        console.error('Farm alerts:', e);
        if (!cancelled) {
          setFarmNdviAlert(null);
          setFarmNdmiAlert(null);
          setFarmForecastAlerts(null);
        }
      } finally {
        if (!cancelled) setFarmAlertsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedField?.farm_id, analysisMode]);

  const canShowFarmAlertsTab =
    !!selectedField?.farm_id && analysisMode === 'field-level' && !multiFieldMode;

  // CADI works for both saved farms AND unsaved/demo fields (just needs geometry).
  const canShowCADI =
    analysisMode === 'field-level' && !multiFieldMode && !!fieldGeoJson;

  const cadiGeometryKey = fieldGeoJson ? JSON.stringify(fieldGeoJson) : null;
  // Only treat farm_id as valid if it's a positive integer — non-numeric strings like
  // "demo_field" fall through to the direct TiTiler path (no DB write).
  const _rawFarmId = selectedField?.farm_id ? String(selectedField.farm_id) : '';
  const cadiFarmId =
    _rawFarmId && /^\d+$/.test(_rawFarmId) && Number(_rawFarmId) > 0 ? _rawFarmId : null;

  useEffect(() => {
    if (!canShowCADI) {
      setCADIState({ status: 'idle' });
      return;
    }

    cadiAbortRef.current?.abort();
    const ac = new AbortController();
    cadiAbortRef.current = ac;

    setCADIState({
      status: 'loading',
      progress: 0,
      total: 1,
    });

    const load = async () => {
      if (!fieldGeoJson) {
        setCADIState({ status: 'error', message: 'No farm geometry available.' });
        return;
      }

      if (cadiFarmId) {
        // ── Saved farm: backend endpoint (compute once, cache in DB) ──────────
        // credentials: 'include' sends the httpOnly auth cookie the FastAPI middleware requires.
        const res = await fetch(
          `${DIGISAKA_API_ORIGIN}/api/farm-cadi-timeseries/${encodeURIComponent(cadiFarmId)}/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ geojson: fieldGeoJson }),
            signal: ac.signal,
          }
        );

        if (ac.signal.aborted) return;

        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          const msg = (detail as Record<string, unknown>)?.detail as string ?? '';
          // 422 "no statistics" = farm polygon outside CADI coverage → hide silently
          if (res.status === 422 || msg.toLowerCase().includes('no cadi')) {
            setCADIState({ status: 'unavailable' });
          } else {
            setCADIState({ status: 'error', message: msg || `Server error ${res.status}` });
          }
          return;
        }

        const data = await res.json();

        if (data?.status === 'no_coverage') {
          setCADIState({
            status: 'no_coverage',
            message:
              (typeof data.message === 'string' && data.message.trim()) ||
              'We could not read drought outlook data inside this saved field boundary.',
          });
          return;
        }

        const points = ((data?.points ?? []) as Record<string, unknown>[]).map((p) => ({
          date: String(p.cadi_date ?? ''),
          label: String(p.cadi_label ?? ''),
          mean: Number(p.mean_value ?? 0),
          min: Number(p.min_value ?? 0),
          max: Number(p.max_value ?? 0),
          validPercent: Number(p.valid_percent ?? 0),
        }));

        // Filter out points with 0 valid pixels (farm outside CADI coverage)
        const validPoints = filterCadiPointsForAlertsTab(
          points.filter((p) => p.validPercent > 0 && !isNaN(p.mean)),
          1,
        );
        if (validPoints.length === 0) {
          setCADIState({
            status: 'no_coverage',
            message:
              'We could not read drought outlook data inside this saved field boundary. This usually means the field is outside the Philippines forecast map, or the drawn area is too small for the satellite layer to sample.',
          });
        } else {
          setCADIState({ status: 'loaded', points: validPoints });
        }
      } else {
        // ── Demo / unsaved field: call TiTiler directly (no DB write) ─────────
        const points = await fetchCADITimeseries(
          fieldGeoJson as Record<string, unknown>,
          (progress, total) => {
            if (!ac.signal.aborted) setCADIState({ status: 'loading', progress, total });
          },
          ac.signal
        );
        if (ac.signal.aborted) return;
        if (points.length === 0) {
          setCADIState({ status: 'unavailable' });
        } else {
          setCADIState({ status: 'loaded', points: filterCadiPointsForAlertsTab(points, 1) });
        }
      }
    };

    load().catch((err: unknown) => {
      if (ac.signal.aborted) return;
      setCADIState({ status: 'error', message: (err as Error)?.message ?? 'Failed to load CADI data.' });
    });

    return () => ac.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadiFarmId, cadiGeometryKey, canShowCADI]);

  // Pest risk — same farm geometry / farm_id gate as CADI
  const canShowPest = canShowCADI;
  const pestFarmId = cadiFarmId;

  useEffect(() => {
    if (!canShowPest) {
      setPestState({ status: 'idle' });
      return;
    }

    pestAbortRef.current?.abort();
    const ac = new AbortController();
    pestAbortRef.current = ac;

    setPestState({
      status: 'loading',
      progress: 0,
      total: Math.max(getPestAlertFrames().length, 1),
    });

    const load = async () => {
      if (!fieldGeoJson) {
        setPestState({ status: 'error', message: 'No farm geometry available.' });
        return;
      }

      if (pestFarmId) {
        const res = await fetch(
          `${DIGISAKA_API_ORIGIN}/api/farm-pest-timeseries/${encodeURIComponent(pestFarmId)}/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ geojson: fieldGeoJson }),
            signal: ac.signal,
          }
        );

        if (ac.signal.aborted) return;

        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          const msg = (detail as Record<string, unknown>)?.detail as string ?? '';
          if (res.status === 422 || msg.toLowerCase().includes('no pest')) {
            setPestState({ status: 'unavailable' });
          } else {
            setPestState({ status: 'error', message: msg || `Server error ${res.status}` });
          }
          return;
        }

        const data = await res.json();

        if (data?.status === 'no_coverage') {
          setPestState({ status: 'unavailable' });
          return;
        }

        const points = ((data?.points ?? []) as Record<string, unknown>[]).map((p) => ({
          pestKey: String(p.pest_key ?? ''),
          pestLabel: String(p.pest_label ?? ''),
          quarter: String(p.quarter ?? ''),
          quarterLabel: String(p.quarter_label ?? ''),
          mean: Number(p.mean_value ?? 0),
          min: Number(p.min_value ?? 0),
          max: Number(p.max_value ?? 0),
          validPercent: Number(p.valid_percent ?? 0),
        }));

        const validPoints = points.filter((p) => p.validPercent > 0 && !Number.isNaN(p.mean));
        if (validPoints.length === 0) {
          setPestState({ status: 'unavailable' });
        } else {
          setPestState({
            status: 'loaded',
            points: validPoints,
            lastUpdated:
              (typeof data.last_updated === 'string' && data.last_updated) || todayIsoDate(),
          });
        }
      } else {
        const points = await fetchPestAlertTimeseries(
          fieldGeoJson as Record<string, unknown>,
          (progress, total) => {
            if (!ac.signal.aborted) setPestState({ status: 'loading', progress, total });
          },
          ac.signal
        );
        if (ac.signal.aborted) return;
        if (points.length === 0) {
          setPestState({ status: 'unavailable' });
        } else {
          setPestState({ status: 'loaded', points, lastUpdated: todayIsoDate() });
        }
      }
    };

    load().catch((err: unknown) => {
      if (ac.signal.aborted) return;
      setPestState({ status: 'error', message: (err as Error)?.message ?? 'Failed to load pest risk data.' });
    });

    return () => ac.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pestFarmId, cadiGeometryKey, canShowPest]);

  const forecastAlertCount = useMemo(
    () => countFarmForecastAlerts(farmForecastAlerts),
    [farmForecastAlerts]
  );

  const hasSatelliteFarmAlerts = !!(farmNdviAlert || farmNdmiAlert);

  const sortedForecastAlerts = useMemo(() => {
    if (!farmForecastAlerts) {
      return { ndvi: [] as FarmForecastAlertRecord[], ndmi: [] as FarmForecastAlertRecord[], weather: [] as FarmForecastAlertRecord[] };
    }
    const byDate = (a: FarmForecastAlertRecord, b: FarmForecastAlertRecord) =>
      (a.forecast_date || '').localeCompare(b.forecast_date || '');
    return {
      ndvi: [...(farmForecastAlerts.ndvi_forecast || [])].sort(byDate),
      ndmi: [...(farmForecastAlerts.ndmi_forecast || [])].sort(byDate),
      weather: [...(farmForecastAlerts.weather_risk || [])].sort(byDate),
    };
  }, [farmForecastAlerts]);

  const forecastChartVisibility = useMemo(() => {
    return {
      ndvi: hasForecastTrendChart(sortedForecastAlerts.ndvi),
      ndmi: hasForecastTrendChart(sortedForecastAlerts.ndmi),
    };
  }, [sortedForecastAlerts]);

  const cropHealthForecastCount =
    actionableForecastAlerts(sortedForecastAlerts.ndvi).length +
    actionableForecastAlerts(sortedForecastAlerts.ndmi).length;
  const weatherForecastCount = actionableForecastAlerts(sortedForecastAlerts.weather).length;
  const hasCropHealthAlerts = hasSatelliteFarmAlerts || cropHealthForecastCount > 0;
  const hasWeatherAlerts = weatherForecastCount > 0;
  const hasCropHealthForecastCharts =
    forecastChartVisibility.ndvi || forecastChartVisibility.ndmi;
  const hasCropHealthForecastActions =
    actionableForecastAlerts(sortedForecastAlerts.ndvi).length > 0 ||
    actionableForecastAlerts(sortedForecastAlerts.ndmi).length > 0;

  const hasCADIData =
    canShowCADI &&
    cadiState.status !== 'idle' &&
    cadiState.status !== 'unavailable';
  // Alerts tab: one CADI card (current month); timeseries chart is hidden.
  const cadiAlertPoint =
    cadiState.status === 'loaded'
      ? (() => {
          const now = new Date();
          const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          return (
            cadiState.points.find((p) => p.date === nowKey) ?? cadiState.points[0] ?? null
          );
        })()
      : null;

  const hasPestData =
    canShowPest &&
    pestState.status === 'loaded' &&
    pestState.points.length > 0;
  const pestAlertPoint =
    pestState.status === 'loaded' ? pickTopPestAlertPoint(pestState.points) : null;
  const pestLastUpdated =
    pestState.status === 'loaded' ? pestState.lastUpdated : null;

  const hasAnyFarmAlerts =
    hasSatelliteFarmAlerts || forecastAlertCount > 0 || hasCADIData || hasPestData;
  const farmAlertsTabVisible = canShowFarmAlertsTab && !farmAlertsLoading && hasAnyFarmAlerts;

  const farmHeaderTabs = useMemo(() => {
    if (multiFieldMode || analysisMode !== 'field-level') return [];
    const tabs: { id: string; label: string }[] = [];
    if (canShowFarmAlertsTab || canShowCADI || canShowPest) tabs.push({ id: 'alerts', label: 'Alerts' });
    if (canFarmHeatMap) tabs.push({ id: 'heatmap', label: 'Index map' });
    if (canFarmTimeSeries) tabs.push({ id: 'timeseries', label: 'Time series' });
    if (canFarmCropCalendar) tabs.push({ id: 'cropcalendar', label: 'Crop calendar' });
    if (canFarmClimate) tabs.push({ id: 'climate', label: 'Climate' });
    if (canFarmRiceForecast) tabs.push({ id: 'farmForecast', label: 'Yield forecast' });
    if (canFarmBlsForecast) tabs.push({ id: 'blsForecast', label: 'Black sigatoka' });
    if (canFarmDroneData) tabs.push({ id: 'droneData', label: 'Sample Data' });
    if (canFarmFieldReport) tabs.push({ id: 'report', label: 'Report' });
    return tabs;
  }, [
    multiFieldMode,
    analysisMode,
    canShowFarmAlertsTab,
    canShowCADI,
    canShowPest,
    canFarmHeatMap,
    canFarmTimeSeries,
    canFarmCropCalendar,
    canFarmClimate,
    canFarmRiceForecast,
    canFarmBlsForecast,
    canFarmDroneData,
    canFarmFieldReport,
  ]);

  useEffect(() => {
    onFarmHeaderTabsChange?.(farmHeaderTabs);
  }, [farmHeaderTabs, onFarmHeaderTabsChange]);
  const activeFarmAlertCount =
    (farmNdviAlert ? 1 : 0) + (farmNdmiAlert ? 1 : 0) + forecastAlertCount;

  const showAlertsDrought = alertsFilter === 'all' || alertsFilter === 'drought';
  const showAlertsPest = alertsFilter === 'all' || alertsFilter === 'pest';
  const showAlertsCropHealth = alertsFilter === 'all' || alertsFilter === 'cropHealth';
  const showAlertsWeather = alertsFilter === 'all' || alertsFilter === 'weather';

  useEffect(() => {
    if (alertsFilter === 'pest' && !hasPestData) {
      setAlertsFilter('all');
    }
  }, [alertsFilter, hasPestData]);

  const resolveAlertActionError = (e: unknown, fallback: string): string => {
    let message = fallback;
    if (axios.isAxiosError(e)) {
      const detail = e.response?.data as { detail?: string; message?: string } | undefined;
      message =
        (typeof detail?.detail === 'string' && detail.detail) ||
        (typeof detail?.message === 'string' && detail.message) ||
        e.message ||
        message;
    } else if (e instanceof Error && e.message) {
      message = e.message;
    }
    return message;
  };

  const handleSendSingleAlert = async (kind: FarmAlertKind) => {
    const farmId = selectedField?.farm_id;
    const record = kind === 'ndvi' ? farmNdviAlert : farmNdmiAlert;
    if (!farmId || !record) return;

    setAlertSendingKind(kind);
    setAlertSendFeedbackByKind((prev) => {
      const next = { ...prev };
      delete next[kind];
      return next;
    });

    try {
      const payload = buildOutboundMobileAlertPayload(kind, record, farmId);
      await sendFarmAlertsToMobileApp([payload]);
      const label = kind === 'ndvi' ? 'Vegetation' : 'Moisture';
      setAlertSendFeedbackByKind((prev) => ({
        ...prev,
        [kind]: {
          type: 'success',
          message: `${label} alert sent to the mobile app.`,
        },
      }));
    } catch (e: unknown) {
      setAlertSendFeedbackByKind((prev) => ({
        ...prev,
        [kind]: {
          type: 'error',
          message: resolveAlertActionError(e, 'Failed to send alert to the mobile app.'),
        },
      }));
      console.error('Send mobile farm alert:', kind, e);
    } finally {
      setAlertSendingKind(null);
    }
  };

  const handleToggleCardFeedback = async (kind: FarmAlertKind) => {
    const farmId = selectedField?.farm_id;
    const record = kind === 'ndvi' ? farmNdviAlert : farmNdmiAlert;
    if (!farmId || !record) return;

    const current = alertFeedbackByKind[kind];
    if (current.open) {
      setAlertFeedbackByKind((prev) => ({
        ...prev,
        [kind]: { ...prev[kind], open: false },
      }));
      return;
    }

    setAlertFeedbackByKind((prev) => ({
      ...prev,
      [kind]: { ...prev[kind], loading: true, error: null },
    }));

    try {
      let feedbacks = farmFeedbackCache;
      if (!feedbacks) {
        feedbacks = await fetchAlertFeedbacksByFarmId(farmId);
        setFarmFeedbackCache(feedbacks);
      }

      const items = filterFeedbacksForAlert(feedbacks, kind, record);
      setAlertFeedbackByKind((prev) => ({
        ...prev,
        [kind]: {
          open: true,
          loading: false,
          error: null,
          items,
        },
      }));
    } catch (e: unknown) {
      setAlertFeedbackByKind((prev) => ({
        ...prev,
        [kind]: {
          open: true,
          loading: false,
          error: resolveAlertActionError(e, 'Failed to load farmer feedback.'),
          items: [],
        },
      }));
      console.error('Fetch alert feedbacks:', kind, e);
    }
  };

  const getForecastAlertFeedbackState = (alertId: number): FarmAlertCardFeedbackState =>
    forecastAlertFeedbackById[alertId] ?? EMPTY_ALERT_FEEDBACK_STATE;

  const handleSendForecastAlert = async (alert: FarmForecastAlertRecord) => {
    const farmId = selectedField?.farm_id;
    if (!farmId || !alert?.id) return;

    setForecastAlertSendingId(alert.id);
    setForecastAlertSendFeedbackById((prev) => {
      const next = { ...prev };
      delete next[alert.id];
      return next;
    });

    try {
      const payload = buildOutboundForecastAlertPayload(alert, farmId);
      await sendFarmAlertsToMobileApp([payload]);
      setForecastAlertSendFeedbackById((prev) => ({
        ...prev,
        [alert.id]: {
          type: 'success',
          message: `${forecastAlertTitle(alert)} sent to the mobile app.`,
        },
      }));
    } catch (e: unknown) {
      setForecastAlertSendFeedbackById((prev) => ({
        ...prev,
        [alert.id]: {
          type: 'error',
          message: resolveAlertActionError(e, 'Failed to send alert to the mobile app.'),
        },
      }));
      console.error('Send mobile forecast alert:', alert.id, e);
    } finally {
      setForecastAlertSendingId(null);
    }
  };

  const handleToggleForecastFeedback = async (alert: FarmForecastAlertRecord) => {
    const farmId = selectedField?.farm_id;
    if (!farmId || !alert?.id) return;

    const current = getForecastAlertFeedbackState(alert.id);
    if (current.open) {
      setForecastAlertFeedbackById((prev) => ({
        ...prev,
        [alert.id]: { ...current, open: false },
      }));
      return;
    }

    setForecastAlertFeedbackById((prev) => ({
      ...prev,
      [alert.id]: { ...getForecastAlertFeedbackState(alert.id), loading: true, error: null },
    }));

    try {
      let feedbacks = farmFeedbackCache;
      if (!feedbacks) {
        feedbacks = await fetchAlertFeedbacksByFarmId(farmId);
        setFarmFeedbackCache(feedbacks);
      }

      const items = filterFeedbacksForForecastAlert(feedbacks, alert);
      setForecastAlertFeedbackById((prev) => ({
        ...prev,
        [alert.id]: {
          open: true,
          loading: false,
          error: null,
          items,
        },
      }));
    } catch (e: unknown) {
      setForecastAlertFeedbackById((prev) => ({
        ...prev,
        [alert.id]: {
          open: true,
          loading: false,
          error: resolveAlertActionError(e, 'Failed to load farmer feedback.'),
          items: [],
        },
      }));
      console.error('Fetch forecast alert feedbacks:', alert.id, e);
    }
  };

  // ── CADI send handler ──────────────────────────────────────────────────────
  const handleSendCADIAlert = async (point?: CADITimeseriesPoint) => {
    const farmId = selectedField?.farm_id;
    if (!farmId || cadiState.status !== 'loaded') return;
    const now = new Date();
    const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const highlight =
      point ??
      cadiState.points.find((p) => p.date === nowKey) ??
      cadiState.points[0];
    if (!highlight) return;

    setCADISending(true);
    setCADISendFeedback(null);
    try {
      const payload = buildCADIAlertPayload(farmId, highlight.mean, highlight.label);
      await sendFarmAlertsToMobileApp([payload]);
      setCADISendFeedback({ type: 'success', message: 'Drought alert sent to farmer.' });
    } catch (e: unknown) {
      setCADISendFeedback({ type: 'error', message: resolveAlertActionError(e, 'Failed to send alert.') });
    } finally {
      setCADISending(false);
    }
  };

  // ── CADI feedback handler ─────────────────────────────────────────────────
  const handleToggleCADIFeedback = async () => {
    const farmId = selectedField?.farm_id;
    if (!farmId) return;
    if (cadiFeedbackOpen) {
      setCADIFeedbackOpen(false);
      return;
    }
    setCADIFeedbackOpen(true);
    setCADIFeedbackLoading(true);
    setCADIFeedbackError(null);
    try {
      let feedbacks = farmFeedbackCache;
      if (!feedbacks) {
        feedbacks = await fetchAlertFeedbacksByFarmId(farmId);
        setFarmFeedbackCache(feedbacks);
      }
      setCADIFeedbackItems(
        feedbacks.filter(
          (f) =>
            (f.alert_type ?? f.alert?.alert_type) === 'drought' ||
            f.source === 'drought_intervention'
        )
      );
      setCADIFeedbackLoading(false);
    } catch (e: unknown) {
      setCADIFeedbackError(resolveAlertActionError(e, 'Failed to load feedback.'));
      setCADIFeedbackLoading(false);
    }
  };

  // ── Pest send handler ─────────────────────────────────────────────────────
  const handleSendPestAlert = async (point?: PestTimeseriesPoint) => {
    const farmId = selectedField?.farm_id;
    if (!farmId || pestState.status !== 'loaded') return;
    const highlight = point ?? pickTopPestAlertPoint(pestState.points);
    if (!highlight) return;

    setPestSending(true);
    setPestSendFeedback(null);
    try {
      const payload = buildPestAlertPayload(
        farmId,
        highlight.pestLabel,
        highlight.mean,
        highlight.quarterLabel
      );
      await sendFarmAlertsToMobileApp([payload]);
      setPestSendFeedback({ type: 'success', message: 'Pest alert sent to farmer.' });
    } catch (e: unknown) {
      setPestSendFeedback({ type: 'error', message: resolveAlertActionError(e, 'Failed to send alert.') });
    } finally {
      setPestSending(false);
    }
  };

  // ── Pest feedback handler ─────────────────────────────────────────────────
  const handleTogglePestFeedback = async () => {
    const farmId = selectedField?.farm_id;
    if (!farmId) return;
    if (pestFeedbackOpen) {
      setPestFeedbackOpen(false);
      return;
    }
    setPestFeedbackOpen(true);
    setPestFeedbackLoading(true);
    setPestFeedbackError(null);
    try {
      let feedbacks = farmFeedbackCache;
      if (!feedbacks) {
        feedbacks = await fetchAlertFeedbacksByFarmId(farmId);
        setFarmFeedbackCache(feedbacks);
      }
      setPestFeedbackItems(
        feedbacks.filter(
          (f) =>
            (f.alert_type ?? f.alert?.alert_type) === 'pest' ||
            f.source === 'pest_intervention'
        )
      );
      setPestFeedbackLoading(false);
    } catch (e: unknown) {
      setPestFeedbackError(resolveAlertActionError(e, 'Failed to load feedback.'));
      setPestFeedbackLoading(false);
    }
  };

  useEffect(() => {
    setAlertSendingKind(null);
    setAlertSendFeedbackByKind({});
    setFarmFeedbackCache(null);
    setAlertFeedbackByKind({
      ndvi: { ...EMPTY_ALERT_FEEDBACK_STATE },
      ndmi: { ...EMPTY_ALERT_FEEDBACK_STATE },
    });
    setForecastAlertSendingId(null);
    setForecastAlertSendFeedbackById({});
    setForecastAlertFeedbackById({});
    setCADISending(false);
    setCADISendFeedback(null);
    setCADIFeedbackOpen(false);
    setCADIFeedbackItems([]);
    setCADIFeedbackError(null);
    setPestSending(false);
    setPestSendFeedback(null);
    setPestFeedbackOpen(false);
    setPestFeedbackItems([]);
    setPestFeedbackError(null);
    setFeedbackTimeSeriesContext(null);
  }, [selectedField?.farm_id, farmNdviAlert, farmNdmiAlert, farmForecastAlerts]);

  useEffect(() => {
    if (multiFieldMode) return;
    if (analysisMode !== 'field-level') return;
    if (layerAllowedEntitlementSet === null) return;
    if (activeAnalysisTab === 'alerts') return;

    const tabAllowed: [string, boolean][] = [
      ['heatmap', canFarmHeatMap],
      ['timeseries', canFarmTimeSeries],
      ['cropcalendar', canFarmCropCalendar],
      ['climate', canFarmClimate],
      ['farmForecast', canFarmRiceForecast],
      ['blsForecast', canFarmBlsForecast],
      ['droneData', canFarmDroneData],
      ['report', canFarmFieldReport],
    ];
    const row = tabAllowed.find(([t]) => t === activeAnalysisTab);
    if (row && !row[1]) {
      const preferred = defaultAnalysisTabForField(selectedField?.farm_id);
      const next =
        tabAllowed.find(([t, ok]) => t === preferred && ok)?.[0] ??
        tabAllowed.find(([, ok]) => ok)?.[0] ??
        'heatmap';
      selectAnalysisTab(next);
    }
  }, [
    multiFieldMode,
    analysisMode,
    layerAllowedEntitlementSet,
    activeAnalysisTab,
    canFarmHeatMap,
    canFarmTimeSeries,
    canFarmCropCalendar,
    canFarmClimate,
    canFarmRiceForecast,
    canFarmBlsForecast,
    canFarmDroneData,
    canFarmFieldReport,
  ]);

  useEffect(() => {
    if (cropCalendarOpenRequest > 0) {
      selectAnalysisTab('cropcalendar');
    }
  }, [cropCalendarOpenRequest]);

  useEffect(() => {
    if (droneDataOpenRequest > 0) {
      selectAnalysisTab('droneData');
    }
  }, [droneDataOpenRequest]);

  const prevFarmAlertsTabVisibleRef = useRef(false);
  useEffect(() => {
    if (multiFieldMode) {
      prevFarmAlertsTabVisibleRef.current = farmAlertsTabVisible;
      return;
    }
    const v = farmAlertsTabVisible;
    if (v && !prevFarmAlertsTabVisibleRef.current) {
      if (skipAlertsAutoOpenRef.current) {
        skipAlertsAutoOpenRef.current = false;
      } else {
        selectAnalysisTab('alerts');
      }
    }
    prevFarmAlertsTabVisibleRef.current = v;
  }, [farmAlertsTabVisible, multiFieldMode]);

  const prevFarmIdForDefaultTabRef = useRef<string | undefined>(undefined);
  const prevMultiFieldForDefaultTabRef = useRef(multiFieldMode);

  useEffect(() => {
    const farmId = selectedField?.farm_id;
    if (multiFieldMode) {
      prevFarmIdForDefaultTabRef.current = farmId;
      prevMultiFieldForDefaultTabRef.current = true;
      return;
    }

    if (prevMultiFieldForDefaultTabRef.current) {
      prevMultiFieldForDefaultTabRef.current = false;
      if (farmId) selectAnalysisTab(defaultAnalysisTabForField(farmId));
      prevFarmIdForDefaultTabRef.current = farmId;
      return;
    }

    if (farmId !== prevFarmIdForDefaultTabRef.current) {
      if (skipDefaultTabOnceRef.current) {
        skipDefaultTabOnceRef.current = false;
      } else {
        selectAnalysisTab(defaultAnalysisTabForField(farmId));
      }
    }
    prevFarmIdForDefaultTabRef.current = farmId;
  }, [selectedField?.farm_id, multiFieldMode]);

  // Observation dates are only needed for the Index Map tab — load when that tab is active.
  useEffect(() => {
    if (!selectedField || !fieldGeoJson) return;
    if (multiFieldMode) {
      fetchObservationDates(fieldGeoJson);
      return;
    }
    if (analysisMode !== 'field-level') {
      fetchObservationDates(fieldGeoJson);
      return;
    }
    const farmId = selectedField.farm_id;
    if (!farmId) {
      fetchObservationDates(fieldGeoJson);
      return;
    }
    if (farmAlertsLoading) return;
    if (activeAnalysisTab !== 'heatmap') return;
    fetchObservationDates(fieldGeoJson);
  }, [
    selectedField,
    fieldGeoJson,
    multiFieldMode,
    analysisMode,
    farmAlertsLoading,
    activeAnalysisTab,
  ]);

  // Function to process crop calendar
  const processCropCalendar = async () => {
    if (!selectedField) {
      console.error('No field selected');
      return;
    }
    // No processing animation needed - data loads directly
    setIsProcessingCropCalendar(false);
  };

  // Function to fetch time series data (uses SSE streaming for real-time progress)
  const fetchTimeSeriesData = async (opts?: {
    index?: string;
    startDate?: string;
    endDate?: string;
    interventionActionDate?: string;
  }) => {
    if (!selectedField || !fieldGeoJson) {
      console.error('No field selected or geometry missing');
      return;
    }

    const runIndex = (opts?.index ?? selectedIndex).toLowerCase();
    const { start_date: tsStart, end_date: tsEnd } = normalizeDateRange(
      opts?.startDate ?? startDate,
      opts?.endDate ?? endDate
    );
    const interventionActionDate = opts?.interventionActionDate;

    const jobGen = beginFarmJob();
    const abortController = new AbortController();
    sseAbortRef.current = abortController;
    const stillCurrent = () => jobGen === farmJobGenRef.current;

    setIsProcessingTimeSeries(true);
    setProcessingProgress(0);
    setProcessingError('');
    setProcessingStage('Preparing your field history…');

    // Sentinel-1 graph endpoint — no SSE, keep the original flow
    if (runIndex === 'sentinel1') {
      const stageInterval = simulateTimeSeriesProcessing();
      try {
        const response = await apiClient.post(
          "/sentinel1-timeseries/",
          { geometry: fieldGeoJson, start_date: tsStart, end_date: tsEnd },
          { signal: abortController.signal }
        );
        clearInterval(stageInterval);
        if (!stillCurrent()) return;
        setProcessingProgress(100);
        setProcessingStage('Your field history is ready.');
        setTimeSeriesSliderIndex(0);
        setLastTimeSeriesRequest({ index: runIndex, startDate: tsStart, endDate: tsEnd });
        if (onAnalysisRun) {
          onAnalysisRun('timeseries', {
            type: 'timeseries', ...response.data,
            index: runIndex, startDate: tsStart, endDate: tsEnd,
            geometry: fieldGeoJson, isSentinel1: true,
          });
        }
        setTimeout(() => {
          if (!stillCurrent()) return;
          setIsProcessingTimeSeries(false);
          setProcessingProgress(0);
          setProcessingStage('');
        }, 2000);
      } catch (error) {
        clearInterval(stageInterval);
        if (isRequestAbortError(error) || !stillCurrent()) return;
        console.error('Error fetching Sentinel-1 time series:', error);
        setProcessingError('Failed to generate time series. Please try again.');
        setProcessingStage('Error occurred');
        setProcessingProgress(0);
        if (axios.isAxiosError(error)) setProcessingError(error.response?.data?.message || 'API Error occurred');
        setTimeout(() => { setIsProcessingTimeSeries(false); setProcessingError(''); setProcessingStage(''); }, 5000);
      }
      return;
    }

    // All other indices — SSE streaming via native fetch (no extra npm packages)
    const requestData = {
      start_date: tsStart,
      end_date: tsEnd,
      geometry: fieldGeoJson,
      index: runIndex,
    };

    const accumulatedResults: any[] = [];
    let visParams: any = null;
    let centroid: any = null;

    try {
      const response = await fetch(
        DIGISAKA_API_ORIGIN + "/api/timeseriesimages/stream/",
        digisakaAuthRequestInit({
          method: 'POST',
          headers: { ...gptInferenceJsonHeaders() },
          body: JSON.stringify(requestData),
          signal: abortController.signal,
        })
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      notifyInferenceComputeUsage(response);

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const handleSSEEvent = (eventType: string, rawData: string) => {
        if (!stillCurrent()) return;
        let data: any;
        try { data = JSON.parse(rawData); } catch { return; }

        console.log('[SSE] Event:', eventType, data);

        switch (eventType) {
          case 'init':
            visParams = data.vis_params;
            setProcessingStage('Looking through past photos of your field…');
            setProcessingProgress(5);
            break;

          case 'phase':
            setProcessingStage('Reading how the crop changed over time…');
            setProcessingProgress(10);
            break;

          case 'progress': {
            const result: any = {
              date: data.date,
              tile_url: data.tile_url,
              mean_index_value: data.mean_index_value,
            };
            if (data.cloud_percentage !== undefined) result.cloud_percentage = data.cloud_percentage;
            accumulatedResults.push(result);

            const pct = Math.round(10 + (data.current / data.total) * 85);
            setProcessingProgress(pct);
            setProcessingStage(timeSeriesProgressLabel(Number(data.current) || 0, Number(data.total) || 0));

            if (onAnalysisRun && !interventionActionDate && accumulatedResults.length % 3 === 0) {
              onAnalysisRun('timeseries', {
                type: 'timeseries',
                success: true,
                vis_params: visParams,
                results: sortTimeSeriesResultsByDate(accumulatedResults),
                index: runIndex,
                centroid,
                startDate: tsStart,
                endDate: tsEnd,
                geometry: fieldGeoJson,
                isSentinel1: false,
                _streaming: true,
              });
            }
            break;
          }

          case 'complete':
            centroid = data.centroid;
            visParams = data.vis_params || visParams;

            setProcessingProgress(100);
            setProcessingStage('Your field history is ready.');
            setTimeSeriesSliderIndex(0);
            setLastTimeSeriesRequest({ index: runIndex, startDate: tsStart, endDate: tsEnd });

            const sortedResults = sortTimeSeriesResultsByDate(accumulatedResults);
            let resultsToShow = sortedResults;
            let interventionCompare: {
              actionDate: string;
              beforeIndex: number;
              afterIndex: number;
              nearCloud: number | null;
              afterCloud: number | null;
            } | undefined;

            if (interventionActionDate) {
              const pair = pickInterventionComparePair(sortedResults, interventionActionDate);
              if (pair) {
                resultsToShow = [pair.near, pair.after];
                interventionCompare = {
                  actionDate: interventionActionDate,
                  beforeIndex: 0,
                  afterIndex: 1,
                  nearCloud: pair.nearCloud,
                  afterCloud: pair.afterCloud,
                };
                setFeedbackTimeSeriesContext((prev) =>
                  prev ? { ...prev, pair, pairError: null } : prev
                );
              } else {
                setFeedbackTimeSeriesContext((prev) =>
                  prev
                    ? {
                        ...prev,
                        pair: null,
                        pairError:
                          'Need one low-cloud scene near the action date and one later scene after the action. None were clear enough yet — try again after the next satellite pass.',
                      }
                    : prev
                );
              }
            }

            if (onAnalysisRun) {
              onAnalysisRun('timeseries', {
                type: 'timeseries',
                success: true,
                vis_params: visParams,
                results: resultsToShow,
                index: runIndex,
                centroid,
                startDate: tsStart,
                endDate: tsEnd,
                geometry: fieldGeoJson,
                isSentinel1: false,
                ...(interventionCompare ? { interventionCompare } : {}),
              });
            }

            setTimeout(() => {
              if (!stillCurrent()) return;
              setIsProcessingTimeSeries(false);
              setProcessingProgress(0);
              setProcessingStage('');
            }, 2000);

            // Signal that we're done — abort the reader so it stops waiting for pings
            abortController.abort();
            break;

          case 'error':
            setProcessingError(data.detail || 'Server error occurred');
            setProcessingStage('Error occurred');
            setProcessingProgress(0);
            setTimeout(() => {
              if (!stillCurrent()) return;
              setIsProcessingTimeSeries(false);
              setProcessingError('');
              setProcessingStage('');
            }, 5000);
            abortController.abort();
            break;
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Normalize \r\n to \n so the parser works on all platforms
        buffer = buffer.replace(/\r\n/g, '\n');

        // SSE events are separated by double-newline
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          const trimmed = part.trim();
          // Skip empty blocks and SSE comments (: ping keepalives)
          if (!trimmed || trimmed.startsWith(':')) continue;

          let eventType = 'message';
          let eventData = '';

          for (const line of trimmed.split('\n')) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              eventData = line.slice(5).trim();
            }
            // skip comment lines starting with ':'
          }

          if (eventData) {
            handleSSEEvent(eventType, eventData);
          }
        }
      }
    } catch (error: any) {
      // AbortError is expected after 'complete', 'error', or when the user leaves this tool.
      if (error?.name === 'AbortError' || !stillCurrent()) return;
      console.error('Error fetching time series (SSE):', error);
      setProcessingError('Failed to generate time series. Please try again.');
      setProcessingStage('Error occurred');
      setProcessingProgress(0);
      setTimeout(() => { setIsProcessingTimeSeries(false); setProcessingError(''); setProcessingStage(''); }, 5000);
    }
  };

  const handleZoomToFeedbackLocation = (lon: number, lat: number) => {
    const map = getOlMap?.();
    if (!map) return;
    const coordinate = fromLonLat([lon, lat]);
    const overlay = ensureFeedbackLocationOverlay(map, feedbackLocationOverlayRef);
    overlay.setPosition(coordinate);
    const view = map.getView();
    const currentZoom = view.getZoom() ?? 16;
    view.animate({
      center: coordinate,
      zoom: Math.max(currentZoom, 17),
      duration: 800,
    });
  };

  const handleCheckFeedbackTimeSeries = (feedback: AlertFeedbackRecord) => {
    if (!canFarmTimeSeries) return;
    const plan = buildFeedbackTimeSeriesPlan(feedback);
    const actionsTaken = getFeedbackActionsTaken(feedback).map((action) =>
      formatInterventionLabel(action)
    );
    const effect =
      formatInterventionLabel(parseFeedbackInterventionData(feedback)?.effect) || undefined;
    setFeedbackTimeSeriesContext({ plan, actionsTaken, effect, pair: null, pairError: null });
    setSelectedIndex(plan.index);
    setStartDate(plan.startDate);
    setEndDate(plan.endDate);
    selectAnalysisTab('timeseries');
    const alreadyLoaded =
      lastTimeSeriesRequest?.index === plan.index &&
      lastTimeSeriesRequest.startDate === plan.startDate &&
      lastTimeSeriesRequest.endDate === plan.endDate &&
      timeSeriesData?.interventionCompare?.actionDate === plan.actionDate &&
      Array.isArray(timeSeriesData?.results) &&
      timeSeriesData.results.length === 2;
    if (!alreadyLoaded) {
      void fetchTimeSeriesData({
        index: plan.index,
        startDate: plan.startDate,
        endDate: plan.endDate,
        interventionActionDate: plan.actionDate,
      });
    }
  };
  const runMultiFieldTimeSeries = async () => {
    if (!multiFieldMode || !multiFieldSelection || multiFieldSelection.length === 0) {
      return;
    }

    const jobGen = beginFarmJob();
    const abortController = new AbortController();
    sseAbortRef.current = abortController;
    const stillCurrent = () => jobGen === farmJobGenRef.current;

    setIsProcessingMultiField(true);
    setMultiFieldError(null);
    setMultiFieldResult(null);
    setMultiFieldResponse(null);

    try {
      // Extract field IDs from selected fields
      const fieldIds = multiFieldSelection.map((field: any) => {
        // Handle both numeric and string IDs
        const id = field.farm_id;
        return typeof id === 'string' && id.startsWith('uploaded_field_') ? 0 : parseInt(id, 10);
      }).filter((id: number) => !isNaN(id));

      if (fieldIds.length === 0) {
        setMultiFieldError('No valid field IDs found in selection');
        return;
      }

      const { start_date: mfStart, end_date: mfEnd } = normalizeDateRange(startDate, endDate);

      // Call the real API endpoint
      const response = await apiClient.post('/multiple-field-analysis/', {
        field_ids: fieldIds,
        index: selectedIndex.toLowerCase(),
        start_date: mfStart,
        end_date: mfEnd
      }, { signal: abortController.signal });

      if (!stillCurrent()) return;

      if (!response.data || !response.data.success) {
        setMultiFieldError('API returned unsuccessful response');
        return;
      }

      // Transform the nested response into a flat array for CSV export
      // Format: [{ field_id, date, mean_index }, ...]
      const flatData: any[] = [];
      const fieldsData = response.data.fields || {};

      Object.keys(fieldsData).forEach((fieldId) => {
        const fieldData = fieldsData[fieldId];
        const timeSeriesData = fieldData.time_series_data || [];

        timeSeriesData.forEach((point: any) => {
          flatData.push({
            field_id: fieldId,
            date: point.date,
            mean_index: point.mean_index
          });
        });
      });

      // Store both the original response and flattened data
      if (!stillCurrent()) return;
      setMultiFieldResult(flatData);
      setMultiFieldResponse(response.data);
    } catch (error: any) {
      if (isRequestAbortError(error) || !stillCurrent()) return;
      console.error('Multi-field time series error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch multi-field time series data';
      setMultiFieldError(errorMessage);
    } finally {
      if (stillCurrent()) setIsProcessingMultiField(false);
    }
  };

  // Helper function to process chart data from multiFieldResponse
  const getChartData = () => {
    if (!multiFieldResponse || !multiFieldResponse.fields) return [];

    const fieldsData = multiFieldResponse.fields;
    const chartData: any[] = [];

    Object.keys(fieldsData).forEach((fieldId) => {
      const fieldData = fieldsData[fieldId];
      const timeSeriesData = fieldData.time_series_data || [];

      // Get field name from selection
      const fieldInfo = multiFieldSelection.find((f: any) => f.farm_id.toString() === fieldId);
      const fieldName = fieldInfo?.farm_name || `Field ${fieldId}`;

      chartData.push({
        fieldId,
        fieldName,
        data: timeSeriesData.map((point: any) => ({
          date: new Date(point.date),
          value: point.mean_index
        })).sort((a: any, b: any) => a.date.getTime() - b.date.getTime())
      });
    });

    return chartData;
  };

  // Function to render a simple line chart
  const renderTimeSeriesChart = (chartData: any[], width: number = 400, height: number = 200, showFull: boolean = false) => {
    if (!chartData || chartData.length === 0) return null;

    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Get all dates and values for scaling
    const allDates: Date[] = [];
    const allValues: number[] = [];
    chartData.forEach(field => {
      field.data.forEach((point: any) => {
        allDates.push(point.date);
        allValues.push(point.value);
      });
    });

    if (allDates.length === 0) return null;

    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const valueRange = maxValue - minValue || 1;

    // Color palette for different fields
    const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

    const xScale = (date: Date) => {
      const time = date.getTime() - minDate.getTime();
      const range = maxDate.getTime() - minDate.getTime();
      return (time / range) * chartWidth;
    };

    const yScale = (value: number) => {
      return chartHeight - ((value - minValue) / valueRange) * chartHeight;
    };

    return (
      <svg width={width} height={height} className="bg-gray-800 rounded">
        <g transform={`translate(${padding.left}, ${padding.top})`}>
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = chartHeight * ratio;
            return (
              <line
                key={`grid-${ratio}`}
                x1={0}
                y1={y}
                x2={chartWidth}
                y2={y}
                stroke="#374151"
                strokeWidth="0.5"
                strokeDasharray="2,2"
              />
            );
          })}

          {/* Y-axis labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const value = minValue + (maxValue - minValue) * (1 - ratio);
            const y = chartHeight * ratio;
            return (
              <text
                key={`y-label-${ratio}`}
                x={-10}
                y={y + 4}
                fill="#9ca3af"
                fontSize="10"
                textAnchor="end"
              >
                {value.toFixed(3)}
              </text>
            );
          })}

          {/* X-axis */}
          <line
            x1={0}
            y1={chartHeight}
            x2={chartWidth}
            y2={chartHeight}
            stroke="#6b7280"
            strokeWidth="1"
          />

          {/* X-axis labels (show first, middle, last) */}
          {chartData.length > 0 && chartData[0].data.length > 0 && (() => {
            const dates = chartData[0].data.map((p: any) => p.date);
            const indices = [0, Math.floor(dates.length / 2), dates.length - 1].filter(i => dates[i]);
            return indices.map((idx) => {
              const date = dates[idx];
              const x = xScale(date);
              return (
                <text
                  key={`x-label-${idx}`}
                  x={x}
                  y={chartHeight + 20}
                  fill="#9ca3af"
                  fontSize="9"
                  textAnchor="middle"
                >
                  {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </text>
              );
            });
          })()}

          {/* Plot lines for each field */}
          {chartData.map((field, fieldIdx) => {
            if (field.data.length === 0) return null;

            const points = field.data.map((point: any) =>
              `${xScale(point.date)},${yScale(point.value)}`
            ).join(' ');

            return (
              <g key={field.fieldId}>
                <polyline
                  points={points}
                  fill="none"
                  stroke={colors[fieldIdx % colors.length]}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Data points */}
                {field.data.map((point: any, idx: number) => (
                  <circle
                    key={`point-${field.fieldId}-${idx}`}
                    cx={xScale(point.date)}
                    cy={yScale(point.value)}
                    r="3"
                    fill={colors[fieldIdx % colors.length]}
                    stroke="white"
                    strokeWidth="1"
                  />
                ))}
              </g>
            );
          })}

          {/* Legend */}
          {showFull && (
            <g transform={`translate(${chartWidth - 150}, 10)`}>
              {chartData.map((field, idx) => (
                <g key={`legend-${field.fieldId}`} transform={`translate(0, ${idx * 20})`}>
                  <line
                    x1={0}
                    y1={0}
                    x2={20}
                    y2={0}
                    stroke={colors[idx % colors.length]}
                    strokeWidth="2"
                  />
                  <text
                    x={25}
                    y={4}
                    fill="#e5e7eb"
                    fontSize="10"
                  >
                    {field.fieldName}
                  </text>
                </g>
              ))}
            </g>
          )}
        </g>
      </svg>
    );
  };

  // Function to fetch index map data (optional dateOverride from observation bar click)
  const fetchHeatMapData = async (dateOverride?: string) => {
    if (!selectedField || !fieldGeoJson) {
      console.error('No field selected or geometry missing');
      return;
    }

    const dateToUse = dateOverride || observationDate;
    if (!dateToUse) {
      console.error('No observation date selected');
      return;
    }

    const jobGen = beginFarmJob();
    const abortController = new AbortController();
    heatMapAbortRef.current = abortController;
    const reqId = ++heatMapReqGenRef.current;
    const stillCurrent = () => jobGen === farmJobGenRef.current && reqId === heatMapReqGenRef.current;
    setIsProcessingHeatMap(true);
    setProcessingProgress(0);
    setProcessingError('');
    setProcessingStage('Loading index map…');
    setSoilMoistureStats(null); // Clear previous soil moisture stats

    const stageInterval = setInterval(() => {
      // Simulate progress for heat map
    }, 1000);

    try {
      // Check if radar soil moisture is selected
      const isRadarSoilMoisture = selectedIndex.toLowerCase() === 'radar_soil_moisture';
      const heatmapPath = isRadarSoilMoisture ? '/soil-moisture/indexheatmap/' : '/indexheatmap/';

      if (isRadarSoilMoisture && heatMapSatellite !== 'S1') {
        clearInterval(stageInterval);
        setIsProcessingHeatMap(false);
        setProcessingError('Waiting for Sentinel-1 observation dates…');
        return;
      }

      const response = await apiClient.post(heatmapPath, {
        geometry: fieldGeoJson,
        observation_date: dateToUse,
        index: selectedIndex.toLowerCase(),
        ...(!isRadarSoilMoisture ? { satellite: heatMapSatellite } : {}),
      }, { signal: abortController.signal });

      if (!stillCurrent()) {
        clearInterval(stageInterval);
        return;
      }

      clearInterval(stageInterval);

      const clearCloudMaskedMap = (message: string) => {
        setProcessingError(message);
        setSoilMoistureStats(null);
        if (onAnalysisRun) {
          onAnalysisRun('heatmap', {
            type: 'heatmap',
            tileUrl: null,
            success: false,
            index: selectedIndex,
            observationDate: dateToUse,
            fullyCloudMasked: true,
            errorMessage: message,
          });
        }
        setIsProcessingHeatMap(false);
        setProcessingProgress(0);
        setProcessingStage('');
      };

      // API may return HTTP 200 with success:false when the whole farm is cloud-masked
      if (
        !isRadarSoilMoisture &&
        (response.data?.success === false ||
          response.data?.code === 'farm_fully_cloud_masked' ||
          !response.data?.tile_url)
      ) {
        clearCloudMaskedMap(
          response.data?.message ||
            response.data?.detail ||
            'This date was cloudy over your farm, so we are holding this one photo. Open Time series to see every date together and spot which days were clear. A better view is often just the next observation date.'
        );
        return;
      }

      setProcessingProgress(100);
      setProcessingStage('Index map loaded');
      setProcessingError('');

      // Handle radar soil moisture response differently
      if (isRadarSoilMoisture) {
        if (!response.data?.success || !response.data?.heatmap?.tile_url) {
          setProcessingError('Failed to load radar soil moisture map. Try another Sentinel-1 date.');
          setIsProcessingHeatMap(false);
          return;
        }

        setSoilMoistureStats({
          mean: response.data.soil_moisture_mean,
          std: response.data.soil_moisture_std,
          metadata: response.data.metadata
        });

        if (onAnalysisRun) {
          onAnalysisRun('heatmap', {
            type: 'heatmap',
            tileUrl: response.data.heatmap.tile_url,
            visParams: response.data.heatmap.visualization,
            bounds: response.data.heatmap.bounds,
            success: response.data.success,
            index: selectedIndex,
            observationDate: dateToUse,
            firstImageDate: response.data.s1_image_date || response.data.first_image_date || dateToUse,
            geometry: fieldGeoJson,
            spatialResolution: response.data.spatial_resolution
              ?? response.data.metadata?.spatial_resolution
              ?? '10 m',
            spatialResolutionM: response.data.spatial_resolution_m
              ?? response.data.metadata?.spatial_resolution_m
              ?? 10,
            spatialResolutionNote: response.data.spatial_resolution_note
              ?? 'Derived from Sentinel-1 IW GRD (~10 m).',
            bandResolutions: response.data.band_resolutions ?? { VV: 10, VH: 10 },
            satellite: 'S1',
            soilMoisture: {
              mean: response.data.soil_moisture_mean,
              std: response.data.soil_moisture_std,
              metadata: response.data.metadata
            }
          });
        }
      } else {
        // Handle standard index response
        if (onAnalysisRun) {
          onAnalysisRun('heatmap', {
            type: 'heatmap',
            tileUrl: response.data.tile_url,
            visParams: response.data.vis_params,
            firstImageDate: response.data.first_image_date,
            success: response.data.success,
            index: selectedIndex,
            observationDate: dateToUse,
            geometry: fieldGeoJson,
            spatialResolution: response.data.spatial_resolution ?? null,
            spatialResolutionM: response.data.spatial_resolution_m ?? null,
            spatialResolutionNote: response.data.spatial_resolution_note ?? null,
            bandResolutions: response.data.band_resolutions ?? null,
            satellite: response.data.satellite ?? heatMapSatellite,
          });
        }
      }

      setTimeout(() => {
        if (reqId !== heatMapReqGenRef.current) return;
        setIsProcessingHeatMap(false);
        setProcessingProgress(0);
        setProcessingStage('');
      }, 800);

    } catch (error) {
      clearInterval(stageInterval);
      if (!stillCurrent() || isRequestAbortError(error)) return;
      console.error('Error fetching heat map data:', error);

      let apiDetail: string | undefined;
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        const message = error.response?.data?.message;
        if (typeof message === 'string' && message.trim()) {
          apiDetail = message;
        } else if (typeof detail === 'string') {
          apiDetail = detail;
        } else if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
          apiDetail = detail.message;
        }
      }

      const friendly =
        apiDetail?.trim() ||
        'Failed to load index map. Please try another date.';

      setProcessingError(friendly);
      setSoilMoistureStats(null);

      if (onAnalysisRun) {
        onAnalysisRun('heatmap', {
          type: 'heatmap',
          tileUrl: null,
          success: false,
          index: selectedIndex,
          observationDate: dateToUse,
          fullyCloudMasked:
            friendly.includes('whole farm was under cloud') ||
            friendly.includes('next observation date') ||
            friendly.includes('farm_fully_cloud_masked'),
          errorMessage: friendly,
        });
      }

      setIsProcessingHeatMap(false);
      setProcessingProgress(0);
      setProcessingStage('');
      // Keep message until the next date/index load clears it
    }
  };

  // Auto-load index map when user picks a date on the map bar (or changes index / satellite)
  useEffect(() => {
    if (activeAnalysisTab !== 'heatmap' || !canFarmHeatMap) return;
    if (!selectedField || !fieldGeoJson) return;
    if (!externalHeatMapObservationDate) return;

    const idx = selectedIndex.toLowerCase();
    const isSarIndex = idx === 'radar_soil_moisture' || idx === 'ndpi' || idx === 'rvi';
    // Do not fire soil-moisture / S1 heatmap against leftover optical dates
    if (isSarIndex && heatMapSatellite !== 'S1') return;

    const key = `${externalHeatMapObservationDate}|${selectedIndex}|${heatMapSatellite}|${selectedField?.farm_id ?? ''}`;
    if (lastAutoIndexMapKeyRef.current === key) return;
    lastAutoIndexMapKeyRef.current = key;

    void fetchHeatMapData(externalHeatMapObservationDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchHeatMapData closes over latest selectedIndex/field/satellite
  }, [
    externalHeatMapObservationDate,
    selectedIndex,
    heatMapSatellite,
    activeAnalysisTab,
    canFarmHeatMap,
    fieldGeoJson,
    selectedField?.farm_id,
  ]);

  // Function to fetch burn detection data
  const fetchBurnDetectionData = async () => {
    if (!selectedField || !fieldGeoJson) {
      console.error('No field selected or geometry missing');
      return;
    }

    const jobGen = beginFarmJob();
    const abortController = new AbortController();
    burnAbortRef.current = abortController;
    const stillCurrent = () => jobGen === farmJobGenRef.current;

    setIsProcessingBurnDetection(true);
    setProcessingProgress(0);
    setProcessingError('');
    setProcessingStage('Initializing burn detection analysis...');

    const stageInterval = setInterval(() => {
      setProcessingStage('Processing satellite imagery...');
    }, 1000);

    try {
      const { start_date: bdStart, end_date: bdEnd } = normalizeDateRange(
        burnDetectionStartDate,
        burnDetectionEndDate
      );

      const response = await apiClient.post('/burn-detection/', {
        geometry: fieldGeoJson,
        start_date: bdStart,
        end_date: bdEnd
      }, { signal: abortController.signal });
      if (!stillCurrent()) {
        clearInterval(stageInterval);
        return;
      }

      clearInterval(stageInterval);
      setProcessingProgress(100);
      setProcessingStage('Burn detection analysis completed!');

      if (response.data.success) {
        setBurnDetectionData(response.data);

        if (onAnalysisRun) {
          onAnalysisRun('burndetection', {
            type: 'burndetection',
            tileUrl: response.data.tile_url,
            visParams: response.data.vis_params,
            period: response.data.period,
            statistics: response.data.statistics,
            algorithm: response.data.algorithm,
            calibration: response.data.calibration,
            burned_rbi_min: response.data.burned_rbi_min,
            burned_rbi_max: response.data.burned_rbi_max,
            images_processed: response.data.images_processed,
            success: response.data.success,
            geometry: fieldGeoJson
          });
        }
      }

      setTimeout(() => {
        setIsProcessingBurnDetection(false);
        setProcessingProgress(0);
        setProcessingStage('');
      }, 2000);

    } catch (error) {
      if (isRequestAbortError(error) || !stillCurrent()) return;
      console.error('Error fetching burn detection data:', error);
      clearInterval(stageInterval);
      setProcessingError('Failed to generate burn detection analysis. Please try again.');

      setTimeout(() => {
        setIsProcessingBurnDetection(false);
        setProcessingError('');
        setProcessingStage('');
      }, 5000);
    }
  };

  const generateFarmBrief = async () => {
    if (!selectedField?.farm_id) return;
    setIsGeneratingReport(true);
    try {
      await downloadFarmBriefPdf(selectedField.farm_id, briefLang, selectedField.farm_name);
    } catch (error) {
      console.error('Error generating farm brief:', error);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Function to generate report data
  const generateReport = async (range?: { start: string; end: string }) => {
    if (!selectedField || !fieldGeoJson) {
      console.error('No field selected for report generation');
      return;
    }

    setIsGeneratingReport(true);

    try {
      const { start_date: reportStart, end_date: reportEnd } = normalizeDateRange(
        range?.start ?? startDate,
        range?.end ?? endDate
      );

      // Use the new fieldreport endpoint with correct payload structure
      const response = await apiClient.post('/fieldreport/', {
        geometry: fieldGeoJson,
        start_date: reportStart,
        end_date: reportEnd,
        indices: selectedReportIndices.map(index => index.toLowerCase()),
        crop_name: selectedField.crop_name || "Unknown",
        monitoring_period: `Analysis Period ${new Date().getFullYear()}`
      });

      // Handle the new API response format
      if (response.data.success && response.data.results) {
        const report: ReportData = {
          fieldInfo: selectedField,
          success: response.data.success,
          results: response.data.results,
          area_ha: response.data.area_ha,
          crop_name: response.data.crop_name,
          monitoring_period: response.data.monitoring_period,
          indices_returned: response.data.indices_returned,
          recommendations_and_insights: response.data.recommendations_and_insights,
          selectedIndices: selectedReportIndices,
          analysisDate: new Date().toISOString(),
          reportType: 'Field health over time',
          weather: response.data.weather ?? null,
          // Maintain backward compatibility
          timeSeriesData: {
            mean_ndvi: response.data.results.mean_ndvi || [],
            mean_evi: response.data.results.mean_evi || []
          }
        };

        setReportData(report);
        setShowReportModal(true);
      } else {
        console.error('Invalid response format from fieldreport API');
      }

    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleRunAnalysis = () => {
    if (activeAnalysisTab === 'heatmap') {
      fetchHeatMapData();
    } else if (activeAnalysisTab === 'timeseries') {
      fetchTimeSeriesData();
    } else if (activeAnalysisTab === 'report') {
      if (reportKind === 'brief') generateFarmBrief();
      else generateReport();
    } else if (activeAnalysisTab === 'cropcalendar') {
      processCropCalendar();
    } else if (activeAnalysisTab === 'farmForecast') {
      handleFarmRiceForecastSubmit();
    } else if (activeAnalysisTab === 'alerts') {
      return;
    } else if (onAnalysisRun) {
      const range = normalizeDateRange(startDate, endDate);
      const params = {
        index: selectedIndex,
        observationDate: observationDate,
        startDate: range.start_date,
        endDate: range.end_date,
        timeRange: selectedTimeRange,
        analysisType: activeAnalysisTab
      };
      onAnalysisRun(activeAnalysisTab, params);
    }
  };

  // Handle farm-level rice yield forecast (field-level analysis)
  const handleFarmRiceForecastSubmit = async () => {
    if (!selectedField?.farm_id) {
      setFarmForecastError('Please select a field with a valid farm_id first.');
      return;
    }

    if (!farmForecastMonth) {
      setFarmForecastError('Please select a forecast month.');
      return;
    }

    // Ensure farm_id is always sent as a string (API expects string)
    const farmIdString = String(selectedField.farm_id);

    const farmMonthOpt = farmForecastMonths.find((m) => m.value === farmForecastMonth);
    if (!farmMonthOpt) {
      setFarmForecastError('Invalid forecast month.');
      return;
    }

    const jobGen = beginFarmJob();
    const abortController = new AbortController();
    farmForecastAbortRef.current = abortController;
    const stillCurrent = () => jobGen === farmJobGenRef.current;

    setIsFarmForecastLoading(true);
    setFarmForecastError(null);
    setFarmForecastResult(null);

    try {
      const response = await fetch(
        DIGISAKA_API_ORIGIN + '/api/farm-rice-yield-forecast/',
        digisakaAuthRequestInit({
          method: 'POST',
          headers: {
            ...gptInferenceJsonHeaders(),
          },
          body: JSON.stringify({
            farm_id: farmIdString,
            // Calendar year matches the season labels; backend uses full date for MODIS window
            date: `${farmMonthOpt.year}-${farmForecastMonth}-15`,
            include_tile_url: true,
          }),
          signal: abortController.signal,
        })
      );
      if (!stillCurrent()) return;

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      notifyInferenceComputeUsage(response);

      const data = await response.json();
      if (!stillCurrent()) return;

      if (!data?.success) {
        setFarmForecastError(data?.message || 'Forecast API returned an unsuccessful response.');
        return;
      }

      setFarmForecastResult(data);
      if (onFarmForecastResultsUpdate) {
        onFarmForecastResultsUpdate(data);
      }

      // Update the map via parent callback (reuses large-scale forecast pipeline)
      if (onForecastDataUpdate) {
        onForecastDataUpdate({
          forecasts: {
            [data.month || 'selected']: {
              tile_url: data.tile_url,
              tile_bounds: data.tile_bounds,
              vis_params: data.vis_params,
              base_yield: data.base_yield,
              rice_price: data.rice_price,
              farm_statistics: data.farm_statistics,
            },
          },
          // Optional metadata to help distinguish farm-level forecasts downstream
          scope: 'farm',
          farm_id: data.farm_id,
          date: data.date,
        });
      }
    } catch (error: any) {
      if (isRequestAbortError(error) || !stillCurrent()) return;
      console.error('Error fetching farm rice yield forecast:', error);
      setFarmForecastError(
        error?.message || 'Failed to fetch farm-level rice yield forecast. Please try again.'
      );
    } finally {
      if (stillCurrent()) setIsFarmForecastLoading(false);
    }
  };

  const resolveMapScreenshotEl = (): HTMLElement | null => {
    if (mapScreenshotRefProp?.current) return mapScreenshotRefProp.current;
    const el = document.querySelector('.flex-1.relative') as HTMLElement | null;
    if (el) return el;
    const map = getOlMap?.();
    return map?.getTargetElement() ?? null;
  };

  /** PDF export aligned with large-scale rice forecast / straw reports: map capture + templated narrative */
  const handleFarmRiceForecastDownloadPDF = async () => {
    if (!farmForecastResult) return;
    setIsGeneratingFarmForecastPDF(true);
    try {
      let logoDataUrl: string | null = null;
      try {
        const logoRes = await fetch(logo);
        const logoBlob = await logoRes.blob();
        logoDataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('logo read failed'));
          reader.readAsDataURL(logoBlob);
        });
      } catch {
        logoDataUrl = null;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = 210;
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentW = pageW - margin * 2;
      let y = 20;
      const maxTextW = contentW;
      const footerReserve = 34;

      const ensureSpace = (h: number) => {
        if (y + h > pageH - footerReserve) {
          pdf.addPage();
          y = 20;
        }
      };

      const writeln = (text: string, lineHeight = 4.8) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(45, 45, 45);
        const lines = pdf.splitTextToSize(text, maxTextW);
        for (const line of lines) {
          ensureSpace(lineHeight);
          pdf.text(line, margin, y);
          y += lineHeight;
        }
      };

      const writeHeading = (text: string) => {
        y += 4;
        ensureSpace(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(40, 40, 40);
        pdf.text(text, margin, y);
        y += 7;
      };

      const writeBullet = (text: string, indent = 6) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(55, 55, 55);
        const lines = pdf.splitTextToSize(text, maxTextW - indent);
        lines.forEach((line: string, i: number) => {
          ensureSpace(5);
          if (i === 0) pdf.text('•', margin + 1, y);
          pdf.text(line, margin + indent, y);
          y += 5;
        });
      };

      const d = farmForecastResult;
      const farmName = selectedField?.farm_name || 'Farm';
      const farmId = selectedField?.farm_id ?? d.farm_id ?? '';
      const monthLabel =
        farmForecastMonths.find((m) => m.value === farmForecastMonth)?.label ||
        (typeof d.month === 'string' ? d.month : '') ||
        'Selected month';
      const fs = d.farm_statistics as
        | { min_yield?: number; mean_yield?: number; max_yield?: number }
        | undefined;

      const fmtNum = (v: unknown, d2 = 2) =>
        v != null && !Number.isNaN(Number(v)) ? Number(v).toFixed(d2) : '—';

      pdf.setFillColor(5, 150, 105);
      pdf.rect(0, 0, pageW, 36, 'F');
      if (logoDataUrl) {
        pdf.addImage(logoDataUrl, 'PNG', margin, 5, 14, 14);
      }
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Rice Yield Forecast Report (Farm)', pageW / 2, 15, { align: 'center' });
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageW / 2, 27, { align: 'center' });
      pdf.setFontSize(8);
      pdf.text('Digisaka WebGIS — Field-level analysis', pageW / 2, 33, { align: 'center' });
      y = 46;

      const shotEl = resolveMapScreenshotEl();
      if (shotEl) {
        try {
          const canvas = await html2canvas(shotEl, {
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#1f2937',
            scale: 1.5,
            logging: false,
          });
          const imgData = canvas.toDataURL('image/jpeg', 0.85);
          const imgAspect = canvas.height / canvas.width;
          const imgW = contentW;
          const imgH = imgW * imgAspect;
          const maxImgH = 95;
          const finalH = Math.min(imgH, maxImgH);
          pdf.setDrawColor(100, 100, 100);
          pdf.setLineWidth(0.3);
          pdf.rect(margin - 0.5, y - 0.5, imgW + 1, finalH + 1);
          pdf.addImage(imgData, 'JPEG', margin, y, imgW, finalH);
          y += finalH + 8;
        } catch {
          /* optional map */
        }
      }

      pdf.setTextColor(40, 40, 40);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Farm & forecast window', 20, y);
      y += 8;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const meta = [
        ['Farm', farmName],
        ['Farm ID', String(farmId || 'N/A')],
        ['Forecast month', monthLabel],
        ['Reference date', d.date ? String(d.date) : 'N/A'],
      ];
      meta.forEach(([label, value]) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${label}:`, 24, y);
        pdf.setFont('helvetica', 'normal');
        pdf.text(String(value), 72, y);
        y += 6;
      });
      y += 4;

      pdf.setDrawColor(5, 150, 105);
      pdf.setLineWidth(0.5);
      pdf.line(20, y, pageW - 20, y);
      y += 10;

      writeHeading('Key figures');
      pdf.setFillColor(236, 253, 245);
      pdf.roundedRect(20, y, 170, fs ? 28 : 18, 2, 2, 'F');
      pdf.setTextColor(6, 95, 70);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(
        `Base yield: ${fmtNum(d.base_yield)} t/ha  ·  Total yield: ${fmtNum(d.total_yield_tons)} t  ·  Area: ${fmtNum(d.total_land_area_hectares)} ha`,
        pageW / 2,
        y + 8,
        { align: 'center' }
      );
      pdf.text(
        `Palay price (reference): ${d.rice_price != null ? `₱${fmtNum(d.rice_price)}/kg` : '—'}`,
        pageW / 2,
        y + 15,
        { align: 'center' }
      );
      if (fs) {
        pdf.text(
          `Farm yield range (model): min ${fmtNum(fs.min_yield)} · mean ${fmtNum(fs.mean_yield)} · max ${fmtNum(fs.max_yield)} t/ha`,
          pageW / 2,
          y + 22,
          { align: 'center' }
        );
      }
      y += fs ? 32 : 22;

      pdf.setDrawColor(5, 150, 105);
      pdf.setLineWidth(0.3);
      pdf.line(margin, y, pageW - margin, y);
      y += 8;

      const meanY = fs?.mean_yield;
      const minY = fs?.min_yield;
      const maxY = fs?.max_yield;
      const spread =
        meanY != null && minY != null && maxY != null && maxY > minY && meanY > 0
          ? ((maxY - minY) / meanY) * 100
          : null;

      writeHeading('Yield forecast summary');
      writeln(
        `This farm-level report estimates expected rice yield and gross production for ${farmName} for ${monthLabel}. Values are derived from the Digisaka farm rice yield forecast service and should be interpreted alongside field scouting and weather.`
      );
      if (spread != null && !Number.isNaN(spread)) {
        if (spread > 35) {
          writeln(
            `The within-farm yield band is relatively wide (~${spread.toFixed(0)}% spread vs mean), suggesting variable performance across sub-plots or higher model uncertainty.`
          );
        } else {
          writeln(
            `The within-farm yield band is comparatively tight (~${spread.toFixed(0)}% spread vs mean), indicating more uniform expected performance.`
          );
        }
      } else {
        writeln(
          'Use base yield and total production to plan harvest logistics, storage, and marketing. Validate against the latest crop stage and soil moisture observations in the dashboard.'
        );
      }

      writeHeading('Market & revenue outlook');
      if (d.rice_price != null && d.total_yield_tons != null && !Number.isNaN(Number(d.rice_price)) && !Number.isNaN(Number(d.total_yield_tons))) {
        const rev = Number(d.total_yield_tons) * 1000 * Number(d.rice_price);
        writeln(
          `At the reference palay price of ₱${fmtNum(d.rice_price)}/kg, rough gross revenue from the forecasted tonnage is on the order of ₱${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(rev)} (before costs; illustrative only).`
        );
      } else {
        writeln(
          'Combine the reference palay price with your local market quotes to estimate revenue and hedge price risk.'
        );
      }

      writeHeading('Recommended actions');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(40, 40, 40);
      ensureSpace(8);
      pdf.text('For the farm / advisor:', margin, y);
      y += 6;
      writeBullet('Compare base yield to your historical farm averages and adjust inputs if the forecast diverges materially.');
      writeBullet('Use NDVI / moisture tools in Digisaka to refine timing of irrigation and harvest.');
      writeBullet('Re-run the forecast if planting dates or variety change.');

      if (y > pageH - footerReserve) {
        pdf.addPage();
        y = 20;
      }
      y += 4;
      const footBottom = pageH - 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(8);
      pdf.text('Contact: deploy@leadsagri.com', pageW / 2, footBottom, { align: 'center' });
      pdf.text('digisaka.com', pageW / 2, footBottom - 6, { align: 'center' });
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(55, 65, 81);
      pdf.text('Digisaka', pageW / 2, footBottom - 13, { align: 'center' });

      const safeId = String(farmId || 'farm').replace(/[^\w\-]+/g, '-').slice(0, 40);
      pdf.save(`farm-rice-yield-forecast-${safeId}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } finally {
      setIsGeneratingFarmForecastPDF(false);
    }
  };

  // Handle time series slider change
  const handleTimeSeriesSliderChange = (index: number) => {
    if (timeSeriesData && timeSeriesData.results && timeSeriesData.results[index]) {
      setTimeSeriesSliderIndex(index);
      if (onTimeSeriesSliderChange) {
        onTimeSeriesSliderChange(timeSeriesData.results[index]);
      }
    }
  };

  // Auto-play functionality for time series
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isPlayingTimeSeries && timeSeriesData?.results) {
      interval = setInterval(() => {
        setTimeSeriesSliderIndex(prev => {
          const nextIndex = (prev + 1) % timeSeriesData.results.length;
          handleTimeSeriesSliderChange(nextIndex);
          return nextIndex;
        });
      }, playbackSpeed);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlayingTimeSeries, playbackSpeed, filteredTimeSeriesData]);

  // Reset slider index if it exceeds filtered data length
  useEffect(() => {
    if (filteredTimeSeriesData?.results && timeSeriesSliderIndex >= filteredTimeSeriesData.results.length) {
      setTimeSeriesSliderIndex(0);
    }
  }, [filteredTimeSeriesData, timeSeriesSliderIndex]);

  // Handle cloud filter toggle
  const handleCloudFilterToggle = () => {
    if (onCloudFilterChange) {
      onCloudFilterChange({ showCloudFilter: !showCloudFilter, maxCloudCoverage });
    }
  };

  // Ensure Time Series tab is active when in multi-field mode
  useEffect(() => {
    if (multiFieldMode) {
      selectAnalysisTab('timeseries');
    }
  }, [multiFieldMode]);

  // Handle cloud coverage threshold change
  const handleCloudCoverageChange = (value: number) => {
    if (onCloudFilterChange) {
      onCloudFilterChange({ showCloudFilter, maxCloudCoverage: value });
    }
  };

  // Export map state
  const [isExporting, setIsExporting] = useState(false);

  // Opacity/Transparency states
  const [heatMapOpacity, setHeatMapOpacity] = useState(1);
  const [timeSeriesOpacity, setTimeSeriesOpacity] = useState(1);

  // Export map as image/PDF
  const exportMap = async (format: 'png' | 'pdf' = 'png') => {
    setIsExporting(true);
    try {
      // Find the map container - look for the main map area that includes legends
      const mapArea = document.querySelector('.flex-1.relative') || // Main map area from page structure
        document.querySelector('.ol-viewport')?.parentElement || // OpenLayers container parent
        document.querySelector('[class*="map"]') ||
        document.body;

      if (!mapArea) {
        console.error('Map area not found');
        return;
      }

      // Temporarily hide analysis panel if open to get clean map view
      const analysisPanel = document.querySelector('[class*="analysis"]');
      const analysisPanelDisplay = analysisPanel ? (analysisPanel as HTMLElement).style.display : undefined;
      if (analysisPanel) {
        (analysisPanel as HTMLElement).style.display = 'none';
      }

      // Configure html2canvas options for better quality
      const canvas = await html2canvas(mapArea as HTMLElement, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f3f4f6', // Gray background
        width: mapArea.clientWidth,
        height: mapArea.clientHeight,
        scale: window.devicePixelRatio || 1.5, // Use device pixel ratio for crisp images
        logging: false,
        ignoreElements: (element) => {
          // Ignore certain UI elements that shouldn't be in the export
          return element.classList.contains('export-ignore') ||
            element.classList.contains('leaflet-control-container') ||
            element.tagName === 'BUTTON';
        }
      });

      // Restore analysis panel
      if (analysisPanel && analysisPanelDisplay !== undefined) {
        (analysisPanel as HTMLElement).style.display = analysisPanelDisplay;
      }

      // Calculate centroid coordinates from field geometry
      const calculateCentroid = (geoJson: any) => {
        if (!geoJson?.geometry?.coordinates) return null;

        const coords = geoJson.geometry.coordinates;
        let lat = 0, lng = 0, pointCount = 0;

        // Handle different geometry types
        if (geoJson.geometry.type === 'Polygon') {
          // For Polygon, use the exterior ring (first array)
          const ring = coords[0];
          for (const point of ring) {
            lng += point[0];
            lat += point[1];
            pointCount++;
          }
        } else if (geoJson.geometry.type === 'MultiPolygon') {
          // For MultiPolygon, iterate through all polygons
          for (const polygon of coords) {
            const ring = polygon[0];
            for (const point of ring) {
              lng += point[0];
              lat += point[1];
              pointCount++;
            }
          }
        }

        if (pointCount === 0) return null;

        return {
          lat: lat / pointCount,
          lng: lng / pointCount
        };
      };

      const centroid = calculateCentroid(fieldGeoJson);

      // Get current analysis info for metadata
      const analysisInfo = {
        field: selectedField?.farm_name || 'Unknown Field',
        fieldArea: selectedField?.area ? `${selectedField.area.toFixed(2)} hectares` : 'Unknown',
        index: indexHeatMapData?.index?.toUpperCase() || 'Unknown Index',
        date: indexHeatMapData?.observationDate || new Date().toISOString().split('T')[0],
        timestamp: new Date().toLocaleString(),
        coordinates: centroid ?
          `${centroid.lat.toFixed(6)}, ${centroid.lng.toFixed(6)}` :
          'Unknown'
      };

      if (format === 'png') {
        // Create download link for PNG
        const link = document.createElement('a');
        link.download = `${analysisInfo.field.replace(/\s+/g, '_')}_${analysisInfo.index}_IndexMap_${analysisInfo.date.replace(/-/g, '')}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } else {
        // Create modern PDF with enhanced design
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4'
        });

        // Modern color palette
        const colors = {
          primary: [16, 185, 129], // Emerald-500
          secondary: [59, 130, 246], // Blue-500
          accent: [139, 92, 246], // Violet-500
          dark: [31, 41, 55], // Gray-800
          light: [243, 244, 246], // Gray-100
          white: [255, 255, 255]
        };

        // Simple header with field name
        pdf.setTextColor(...colors.dark);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Field: ${analysisInfo.field}`, 20, 20);

        // Essential information in clean layout
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Centroid: ${analysisInfo.coordinates}`, 20, 30);
        pdf.text(`Observation Date: ${analysisInfo.date}`, 20, 38);
        pdf.text(`Generated: ${analysisInfo.timestamp}`, 20, 46);

        // Add the map image (original width from first implementation)
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 250; // Original width from first implementation
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const maxHeight = 140; // Original height from first implementation
        const finalHeight = Math.min(imgHeight, maxHeight);

        pdf.addImage(imgData, 'PNG', 20, 55, imgWidth, finalHeight);

        // Simple footer with generated by text
        pdf.setTextColor(...colors.dark);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Generated by Digisaka WebGIS', 20, 200);

        // Save PDF with clean filename
        pdf.save(`${analysisInfo.field.replace(/\s+/g, '_')}_${analysisInfo.index}_IndexMap_${analysisInfo.date.replace(/-/g, '')}.pdf`);
      }

      console.log(`Map exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting map:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Create chart data from time series results
  const createChartData = () => {
    if (!filteredTimeSeriesData?.results) return [];

    // Handle Sentinel-1 data format
    if (filteredTimeSeriesData.isSentinel1 || selectedIndex === 'sentinel1') {
      return filteredTimeSeriesData.results.map((item: any) => ({
        date: item.date,
        value: item.median_backscatter,
        band: item.band,
        orbit_pass: item.orbit_pass,
        formattedDate: new Date(item.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })
      }));
    }

    // Handle regular timeseries data format
    return filteredTimeSeriesData.results.map((item: any) => ({
      date: item.date,
      value: item.mean_index_value,
      cloudCoverage: item.cloud_percentage,
      formattedDate: new Date(item.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    }));
  };


  // CSV Export function for time series data
  const exportTimeSeriesCSV = () => {
    if (!filteredTimeSeriesData?.results || filteredTimeSeriesData.results.length === 0) {
      return;
    }

    let csvData: any[] = [];
    let csvHeaders: string[] = [];
    let filename: string = '';

    // Handle different data types
    if (filteredTimeSeriesData.isSentinel1 || selectedIndex === 'sentinel1') {
      // Sentinel-1 data format
      csvHeaders = ['Date', 'Median_Backscatter', 'Band', 'Orbit_Pass'];
      csvData = filteredTimeSeriesData.results.map((result: any) => [
        result.date,
        result.median_backscatter?.toFixed(6) || '0',
        result.band || '',
        result.orbit_pass || ''
      ]);
      filename = `Sentinel1_TimeSeries_${showCloudFilter ? `Filtered_${maxCloudCoverage}pct_` : ''}${new Date().toISOString().split('T')[0]}.csv`;
    } else {
      // Regular optical data format
      csvHeaders = ['Date', 'Value', 'Cloud_Coverage'];
      csvData = filteredTimeSeriesData.results.map((result: any) => [
        result.date,
        result.mean_index_value !== null && result.mean_index_value !== undefined
          ? result.mean_index_value.toFixed(6)
          : 'null',
        result.cloud_percentage !== null && result.cloud_percentage !== undefined
          ? result.cloud_percentage.toFixed(2)
          : 'null'
      ]);
      const indexType = selectedIndex.toUpperCase();
      filename = `${indexType}_TimeSeries_${showCloudFilter ? `Filtered_${maxCloudCoverage}pct_` : ''}${new Date().toISOString().split('T')[0]}.csv`;
    }

    // Create CSV content
    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);

    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Close report modal
  const closeReportModal = () => {
    setShowReportModal(false);
    setReportData(null);
  };

  const chartData = createChartData();
  const maxValue = chartData.length > 0 ? Math.max(...chartData.map(d => Math.max(d.value, 0))) : 1;
  const minValue = chartData.length > 0 ? Math.min(...chartData.map(d => Math.min(d.value, 0))) : 0;
  const interventionImpact = feedbackTimeSeriesContext
    ? summarizeSeriesAroundAction(
        filteredTimeSeriesData?.results,
        feedbackTimeSeriesContext.plan.actionDate
      )
    : null;

  const isInlineCard = displayMode === 'inline-card';

  // Outer wrapper / header classes change between drawer (legacy) and inline-card (new rail UI).
  // - Drawer (legacy): full-height fixed sheet, `overflow-y-auto` on the wrapper as before.
  // - Inline-card (rail UI): flex column that fills its docked host next to the tool rail. The header
  //   stays pinned via `flex-shrink-0`, and the body sections below get `flex-1 min-h-0
  //   overflow-y-auto` so long content scrolls inside the card instead of overflowing it.
  const wrapperClassName = analysisPanelShellClass(theme, isInlineCard, isOpen);

  return (
    <>
      {/* Mobile overlay — only for drawer mode; inline-card is positioned by the parent (`MapToolRail`). */}
      {!isInlineCard && isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onToggle}
        />
      )}

      {/* Analysis Panel */}
      <div className={wrapperClassName}>
        {/* Drawer still has a close control. The docked card uses the header nav to close. */}
        {!isInlineCard && (
        <div
          className={`border-b ${ap.headerBorder} flex items-center justify-end flex-shrink-0 p-4`}
        >
          <div className="flex items-center space-x-2">
            {onCloseAnalysis && (
              <button
                onClick={onCloseAnalysis}
                className={`rounded p-1 ${panelHeaderActionHover}`}
                title="Close Analysis"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            )}
            <button
              onClick={onToggle}
              className={`p-1 rounded ${panelHeaderActionHover}`}
              title="Hide Panel"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>
        </div>
        )}

        {/* Render based on mode. In inline-card mode each body becomes a scrolling flex child so
            tall content scrolls inside the resizable card; in drawer mode we keep the legacy
            full-height behavior so the outer wrapper's `overflow-y-auto` still handles scrolling. */}
        {
          analysisMode === 'large-scale' ? (
            // Large-scale mode: Show LayerTogglePanel content
            layerVisibility && onLayerToggle ? (
              <div className={isInlineCard ? `flex-1 min-h-0 overflow-y-auto ${panelBodyBg}` : `h-full overflow-hidden ${panelBodyBg}`}>
                <LayerTogglePanel
                  layerVisibility={layerVisibility}
                  layerOpacity={layerOpacity || {}}
                  onLayerToggle={onLayerToggle}
                  onOpacityChange={onOpacityChange || (() => { })}
                  legendUrls={legendUrls}
                  cropAreaLegends={cropAreaLegends}
                  loadingLayers={loadingLayers}
                  onForecastDataUpdate={onForecastDataUpdate}
                  onForecastResultsUpdate={onForecastResultsUpdate}
                  onStrawBurnResultsUpdate={onStrawBurnResultsUpdate}
                  onFloodLayerShow={onFloodLayerShow}
                  onBSRIDateRangeUpdate={onBSRIDateRangeUpdate}
                  bsriIsLoading={bsriIsLoading}
                  riceGrowthStageLoading={riceGrowthStageLoading}
                  irrigationTypeLoading={irrigationTypeLoading}
                  onBSRIAnalyseRequest={onBSRIAnalyseRequest}
                  onStrawBurnDataFetch={onStrawBurnDataFetch}
                  strawBurnIsLoading={strawBurnIsLoading}
                  strawBurnData={strawBurnData}
                  onTreeDetectionDataUpdate={onTreeDetectionDataUpdate}
                  onCrownSegmentationResultsUpdate={onCrownSegmentationResultsUpdate}
                  onCropfieldDelineationUpdate={onCropfieldDelineationUpdate}
                  mapCenter={mapCenter}
                  mapZoom={mapZoom}
                  embedded={true}
                  onLSTPHDateRangeUpdate={onLSTPHDateRangeUpdate}
                  lstIsLoading={lstIsLoading}
                  onVHIPHDateRangeUpdate={onVHIPHDateRangeUpdate}
                  vhiIsLoading={vhiIsLoading}
                  clearMultiTemporalAPITiles={clearMultiTemporalAPITiles}
                  pestRiskPestKey={pestRiskPestKey}
                  onPestRiskPestChange={onPestRiskPestChange}
                  zonalState={zonalState}
                  zonalLayer={zonalLayer}
                  zonalFrameLabel={zonalFrameLabel}
                  zonalPestLabel={zonalPestLabel}
                  zonalProvinces={zonalProvinces}
                  zonalSelectedProvince={zonalSelectedProvince}
                  onZonalProvinceChange={onZonalProvinceChange}
                  onZonalMunicipalitySelect={onZonalMunicipalitySelect}
                  getOlMap={getOlMap}
                  onExpandedTabChange={(tab) => {
                    const tabNames: Record<string, string> = {
                      forecast: 'Rice Yield Forecast',
                      multiTemporal: 'Multi-Temporal Maps',
                      droneData: 'Sample Data',
                      fieldServices: 'Field services',
                      cropArea: 'Crop Type Maps',
                      disasterLossDamage: 'Disaster Loss & Damages',
                      lowCarbon: 'Low Carbon Rice',
                      treeDetection: 'AI Detection: Trees, Crowns & Fields'
                    };
                    setCurrentLayerTab(tab ? tabNames[tab] : null);
                    // Also call parent's handler if provided
                    if (onExpandedTabChange) {
                      onExpandedTabChange(tab);
                    }
                  }}
                  onFloodDamageSelect={onFloodDamageSelect}
                  selectedFloodDamageId={selectedFloodDamageId}
                  allowedEntitlementSet={layerAllowedEntitlementSet}
                  // In inline-card mode the parent (`MapToolRail`) owns the icon strip + active tab.
                  hideTabStrip={isInlineCard}
                  pinnedSectionId={pinnedLargeScaleSection}
                  infoSectionId={infoSectionId}
                  onInfoSectionHandled={onInfoSectionHandled}
                  onClosePinnedSection={onClosePinnedSection}
                  controlledExpandedTab={isInlineCard ? (controlledLargeScaleTab ?? null) : undefined}
                  onExpandedTabRequestChange={onControlledLargeScaleTabChange}
                  fieldGeoJson={fieldGeoJson ?? null}
                  farmId={selectedField?.farm_id ?? null}
                  onRequestDronePrescriptionStats={onRequestDronePrescriptionStats}
                  onClearDronePrescriptionStats={onClearDronePrescriptionStats}
                  droneSites={droneSites}
                />
              </div>
            ) : (
              <div className={`p-4 text-center ${panelMutedText} ${isInlineCard ? 'flex-1 min-h-0' : ''}`}>
                Loading layer controls...
              </div>
            )
          ) : (
            // Field-level mode: Show existing analysis tools
            <div className={
              activeAnalysisTab === 'report' && canFarmFieldReport
                ? isInlineCard
                  ? 'flex min-h-0 flex-1 flex-col overflow-hidden panel-body'
                  : 'flex h-full min-h-0 flex-col overflow-hidden'
                : isInlineCard
                  ? 'flex-1 min-h-0 overflow-y-auto px-2 py-2 panel-body'
                  : 'p-4'
            }>
              {/* Multi-field mode: simplified time series UI */}
              {multiFieldMode ? (
                <>
                  <div className={isInlineCard ? 'mb-4' : 'mb-6'}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className={`text-sm font-medium ${ap.heading}`}>Time Series (Multiple Fields)</h4>
                      <span className={`text-[11px] ${ap.muted}`}>
                        {multiFieldSelection && multiFieldSelection.length > 0
                          ? `${multiFieldSelection.length} field${multiFieldSelection.length > 1 ? 's' : ''} selected`
                          : 'No fields selected'}
                      </span>
                    </div>

                    {/* Index Type */}
                    <div className="mb-4">
                      <label className={`block text-xs ${ap.label} mb-2`}>Index Type</label>
                      <div className="relative">
                        <select
                          value={selectedIndex}
                          onChange={(e) => setSelectedIndex(e.target.value)}
                          className={`w-full ${ap.select}`}
                          disabled={isProcessingMultiField}
                        >
                          {timeSeriesIndexOptions.map((index) => (
                            <option key={index.value} value={index.value}>{index.label}</option>
                          ))}
                        </select>
                        <i className={`ri-arrow-down-s-line absolute right-2 top-2.5 pointer-events-none ${ap.selectIcon}`}></i>
                      </div>
                    </div>

                    {/* Date Range */}
                    <div className="mb-4">
                      <label className={`block text-xs ${ap.label} mb-2`}>Date Range</label>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => {
                            const next = onStartDateChange(
                              e.target.value,
                              endDate,
                              startDate
                            );
                            setStartDate(next.start);
                            setEndDate(next.end);
                          }}
                          className={`${ap.select.replace('w-full ', '')}`}
                          disabled={isProcessingMultiField}
                        />
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => {
                            const next = onEndDateChange(
                              startDate,
                              e.target.value,
                              endDate
                            );
                            setStartDate(next.start);
                            setEndDate(next.end);
                          }}
                          className={`${ap.select.replace('w-full ', '')}`}
                          disabled={isProcessingMultiField}
                        />
                      </div>
                    </div>

                    {/* Processing Status */}
                    {isProcessingMultiField && (
                      <div className={ap.tsProcessingShell}>
                        <div className={ap.tsProcessingText}>
                          <div className={`animate-spin h-4 w-4 border rounded-full border-t-transparent mr-3 ${isDark ? 'border-emerald-400' : 'border-[#008540]'}`}></div>
                          <span>Running multi-field time series, waiting for API response...</span>
                        </div>
                      </div>
                    )}

                    {multiFieldError && (
                      <div className={ap.tsErrorShell}>
                        <div className={`${ap.tsErrorText} text-sm`}>
                          <i className="ri-error-warning-line mr-2"></i>
                          <span>{multiFieldError}</span>
                        </div>
                      </div>
                    )}

                    {/* Time Series Chart */}
                    {multiFieldResponse && multiFieldResult && multiFieldResult.length > 0 && (
                      <div className={ap.tsMultiChartShell}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <i className={`ri-line-chart-line mr-2 ${ap.tsMultiChartIcon}`}></i>
                            <span className={ap.tsMultiChartTitle}>Time Series Chart</span>
                          </div>
                          <button
                            onClick={() => setShowFullscreenChart(true)}
                            className={ap.tsMultiFullscreenBtn}
                            title="View fullscreen chart"
                          >
                            <i className="ri-fullscreen-line text-lg"></i>
                          </button>
                        </div>
                        <div className="flex justify-center">
                          {renderTimeSeriesChart(getChartData(), 400, 200, false)}
                        </div>
                        {getChartData().length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2 justify-center">
                            {getChartData().map((field, idx) => {
                              const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
                              return (
                                <div key={field.fieldId} className="flex items-center text-[10px]">
                                  <div
                                    className="w-3 h-0.5 mr-1"
                                    style={{ backgroundColor: colors[idx % colors.length] }}
                                  ></div>
                                  <span className={ap.tsMultiLegend}>{field.fieldName}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Run + Export Buttons */}
                    <div className="space-y-2">
                      <button
                        onClick={runMultiFieldTimeSeries}
                        disabled={
                          !multiFieldSelection ||
                          multiFieldSelection.length === 0 ||
                          isProcessingMultiField
                        }
                        className={`w-full px-4 py-2 rounded text-sm font-medium transition-colors flex items-center justify-center ${!multiFieldSelection || multiFieldSelection.length === 0
                          ? ap.tsMultiRunDisabled
                          : isProcessingMultiField
                            ? ap.tsMultiRunWait
                            : ap.tsMultiRunActive
                          }`}
                      >
                        {isProcessingMultiField ? (
                          <>
                            <div className="animate-spin h-4 w-4 border border-white rounded-full border-t-transparent mr-2"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <i className="ri-line-chart-line mr-2"></i>
                            Run Time Series
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => {
                          if (!multiFieldResult || multiFieldResult.length === 0) return;

                          const headers = Object.keys(multiFieldResult[0]);
                          const indexName = multiFieldResponse?.index || selectedIndex || 'ndvi';
                          const startDateStr = multiFieldResponse?.start_date || startDate;
                          const endDateStr = multiFieldResponse?.end_date || endDate;

                          const csvRows = [
                            headers.join(','),
                            ...multiFieldResult.map((row) =>
                              headers
                                .map((h) => {
                                  const val = row[h];
                                  const safe = val === null || val === undefined ? '' : String(val);
                                  // Basic CSV escaping
                                  if (safe.includes(',') || safe.includes('"') || safe.includes('\n')) {
                                    return `"${safe.replace(/"/g, '""')}"`;
                                  }
                                  return safe;
                                })
                                .join(',')
                            ),
                          ];

                          const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          const filename = `multi_field_${indexName}_${startDateStr}_to_${endDateStr}.csv`;
                          link.setAttribute('download', filename);
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                        }}
                        disabled={!multiFieldResult || multiFieldResult.length === 0}
                        className={`w-full px-4 py-2 rounded-[10px] text-sm font-medium flex items-center justify-center transition-colors ${!multiFieldResult || multiFieldResult.length === 0
                          ? `${ap.btnDisabled} cursor-not-allowed`
                          : ap.primaryBtn
                          }`}
                      >
                        <i className="ri-file-download-line mr-2"></i>
                        Export CSV
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Farm section buttons sit in the header when `farmTabsInHeader` is set. */}
                  {!farmTabsInHeader && (
                  <div className={isInlineCard ? 'mb-2' : 'mb-6'}>
                    <div
                      className={
                        isInlineCard
                          ? 'farm-analysis-tab-grid grid grid-cols-3 gap-1'
                          : 'flex flex-wrap gap-2 mb-4'
                      }
                    >
                      {(canShowFarmAlertsTab || canShowCADI || canShowPest) && (
                        <button
                          type="button"
                          onClick={() => selectAnalysisTab('alerts')}
                          className={`inline-flex items-center justify-center gap-0.5 border font-medium panel-tab-btn transition-all duration-200 ${
                            activeAnalysisTab === 'alerts'
                              ? panelTabActiveClass
                              : panelTabInactiveClass
                          } ${isInlineCard ? `px-1 py-1.5 text-[10px] leading-tight ${ap.tabShape}` : 'px-3 py-2 text-xs rounded-md'}`}
                        >
                          <i className={`ri-notification-3-line ${isInlineCard ? 'text-[11px]' : ''}`} aria-hidden />
                          <span>{isInlineCard ? 'Alerts & forecasts' : 'Alerts and Forecasts'}</span>
                          {activeFarmAlertCount > 0 && (
                            <span
                              className={`flex items-center justify-center rounded-full bg-red-500 font-bold text-white ${
                                isInlineCard
                                  ? 'h-3.5 min-w-[0.875rem] px-0.5 text-[8px]'
                                  : 'h-4 min-w-[1rem] px-0.5 text-[9px]'
                              }`}
                            >
                              {activeFarmAlertCount}
                            </span>
                          )}
                        </button>
                      )}
                      {canFarmHeatMap && (
                        <button
                          type="button"
                          onClick={() => selectAnalysisTab('heatmap')}
                          className={`inline-flex items-center justify-center gap-0.5 border font-medium panel-tab-btn transition-all duration-200 ${
                            activeAnalysisTab === 'heatmap'
                              ? panelTabActiveClass
                              : panelTabInactiveClass
                          } ${isInlineCard ? `px-1 py-1.5 text-[10px] leading-tight ${ap.tabShape}` : 'px-3 py-2 text-xs rounded-md'}`}
                        >
                          <i className={`ri-map-2-line ${isInlineCard ? 'text-[11px]' : ''}`} aria-hidden />
                          <span>{isInlineCard ? 'Index map' : 'Index Map'}</span>
                        </button>
                      )}
                      {canFarmTimeSeries && (
                        <button
                          type="button"
                          onClick={() => selectAnalysisTab('timeseries')}
                          className={`inline-flex items-center justify-center gap-0.5 border font-medium panel-tab-btn transition-all duration-200 ${
                            activeAnalysisTab === 'timeseries'
                              ? panelTabActiveClass
                              : panelTabInactiveClass
                          } ${isInlineCard ? `px-1 py-1.5 text-[10px] leading-tight ${ap.tabShape}` : 'px-3 py-2 text-xs rounded-md'}`}
                        >
                          <i className={`ri-line-chart-line ${isInlineCard ? 'text-[11px]' : ''}`} aria-hidden />
                          <span>{isInlineCard ? 'Time series' : 'Time Series'}</span>
                        </button>
                      )}
                      {canFarmCropCalendar && (
                        <button
                          type="button"
                          onClick={() => selectAnalysisTab('cropcalendar')}
                          className={`inline-flex items-center justify-center gap-0.5 border font-medium panel-tab-btn transition-all duration-200 ${
                            activeAnalysisTab === 'cropcalendar'
                              ? panelTabActiveClass
                              : panelTabInactiveClass
                          } ${isInlineCard ? `px-1 py-1.5 text-[10px] leading-tight ${ap.tabShape}` : 'px-3 py-2 text-xs rounded-md'}`}
                        >
                          <i className={`ri-calendar-line ${isInlineCard ? 'text-[11px]' : ''}`} aria-hidden />
                          <span>{isInlineCard ? 'Crop calendar' : 'Crop Calendar'}</span>
                        </button>
                      )}
                      {canFarmClimate && (
                        <button
                          type="button"
                          onClick={() => selectAnalysisTab('climate')}
                          className={`inline-flex items-center justify-center gap-0.5 border font-medium panel-tab-btn transition-all duration-200 ${
                            activeAnalysisTab === 'climate'
                              ? panelTabActiveClass
                              : panelTabInactiveClass
                          } ${isInlineCard ? `px-1 py-1.5 text-[10px] leading-tight ${ap.tabShape}` : 'px-3 py-2 text-xs rounded-md'}`}
                        >
                          <i className={`ri-cloud-line ${isInlineCard ? 'text-[11px]' : ''}`} aria-hidden />
                          <span>Climate</span>
                        </button>
                      )}
                      {canFarmRiceForecast && (
                        <button
                          type="button"
                          onClick={() => selectAnalysisTab('farmForecast')}
                          className={`inline-flex items-center justify-center gap-0.5 border font-medium panel-tab-btn text-center transition-all duration-200 ${
                            activeAnalysisTab === 'farmForecast'
                              ? panelTabActiveClass
                              : panelTabInactiveClass
                          } ${isInlineCard ? `px-1 py-1.5 text-[10px] leading-tight ${ap.tabShape}` : 'px-3 py-2 text-xs rounded-md'}`}
                        >
                          <i className={`ri-seedling-line ${isInlineCard ? 'text-[11px]' : ''}`} aria-hidden />
                          <span>{isInlineCard ? 'Yield forecast' : 'Rice Yield Forecast'}</span>
                        </button>
                      )}
                      {canFarmBlsForecast && (
                        <button
                          type="button"
                          onClick={() => selectAnalysisTab('blsForecast')}
                          className={`inline-flex items-center justify-center gap-0.5 border font-medium panel-tab-btn text-center transition-all duration-200 ${
                            activeAnalysisTab === 'blsForecast'
                              ? panelTabActiveClass
                              : panelTabInactiveClass
                          } ${isInlineCard ? `px-1 py-1.5 text-[10px] leading-tight ${ap.tabShape}` : 'px-3 py-2 text-xs rounded-md'}`}
                        >
                          <i className={`ri-plant-line ${isInlineCard ? 'text-[11px]' : ''}`} aria-hidden />
                          <span>{isInlineCard ? 'Black sigatoka' : 'Black Sigatoka'}</span>
                        </button>
                      )}
                      {canFarmDroneData && (
                        <button
                          type="button"
                          onClick={() => selectAnalysisTab('droneData')}
                          className={`inline-flex items-center justify-center gap-0.5 border font-medium panel-tab-btn text-center transition-all duration-200 ${
                            activeAnalysisTab === 'droneData'
                              ? panelTabActiveClass
                              : panelTabInactiveClass
                          } ${isInlineCard ? `px-1 py-1.5 text-[10px] leading-tight ${ap.tabShape}` : 'px-3 py-2 text-xs rounded-md'}`}
                        >
                          <i className={`ri-drone-fill ${isInlineCard ? 'text-[11px]' : ''}`} aria-hidden />
                          <span>{isInlineCard ? 'Sample' : 'Sample Data'}</span>
                        </button>
                      )}
                      {canFarmFieldReport && (
                        <button
                          type="button"
                          onClick={() => selectAnalysisTab('report')}
                          className={`inline-flex items-center justify-center gap-0.5 border font-medium panel-tab-btn transition-all duration-200 ${
                            activeAnalysisTab === 'report'
                              ? panelTabActiveClass
                              : panelTabInactiveClass
                          } ${isInlineCard ? `px-1 py-1.5 text-[10px] leading-tight ${ap.tabShape}` : 'px-3 py-2 text-xs rounded-md'}`}
                        >
                          <i className={`ri-file-text-line ${isInlineCard ? 'text-[11px]' : ''}`} aria-hidden />
                          <span>Report</span>
                        </button>
                      )}
                    </div>
                  </div>
                  )}
                </>
              )}

              <InfoPopupDialog
                open={indexGuideOpen}
                onClose={() => setIndexGuideOpen(false)}
                title={indexReadingGuide(selectedIndex).shortLabel}
                accent="emerald"
              >
                <div className="space-y-2">
                  <p>
                    <span className="font-semibold">When to use it. </span>
                    {indexReadingGuide(selectedIndex).use}
                  </p>
                  <p>
                    <span className="font-semibold">How to read the colors. </span>
                    {indexReadingGuide(selectedIndex).colors}
                  </p>
                </div>
              </InfoPopupDialog>

              {/* Index Map Analysis */}
              {activeAnalysisTab === 'heatmap' && canFarmHeatMap && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium mb-4">Index Map Analysis</h4>

                  {/* Index Selection */}
                  <div className="mb-4">
                    <div className="mb-2 flex items-center gap-1.5">
                      <label className={`text-xs ${ap.label}`}>Index</label>
                      <button
                        type="button"
                        title="How to read this index"
                        aria-label="How to read this index"
                        onClick={() => setIndexGuideOpen(true)}
                        className={`inline-flex items-center justify-center rounded-full p-0.5 ${
                          isDark ? 'text-[#8b949e] hover:text-[#34d399]' : 'text-[#6b7280] hover:text-[#008540]'
                        }`}
                      >
                        <i className="ri-information-line text-sm" aria-hidden />
                      </button>
                    </div>
                    <div className="relative">
                      <select
                        value={selectedIndex}
                        onChange={(e) => setSelectedIndex(e.target.value)}
                        className={`w-full ${ap.select}`}
                        disabled={isProcessingHeatMap}
                      >
                        {heatMapIndexOptions.map((index) => (
                          <option key={index.value} value={index.value}>{index.label}</option>
                        ))}
                      </select>
                      <i className={`ri-arrow-down-s-line absolute right-2 top-2.5 pointer-events-none ${ap.selectIcon}`}></i>
                    </div>
                  </div>

                  {!externalHeatMapObservationDate && !isProcessingHeatMap && (
                    <div className={`mb-3 text-xs rounded-lg border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-800/60 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                      Waiting for observation dates…
                    </div>
                  )}

                  {isProcessingHeatMap && (
                    <div className={`mb-3 flex items-center gap-2 rounded-[10px] border px-3 py-2 text-sm ${isDark ? 'border-[#34d399]/40 bg-[rgba(52,211,153,.12)] text-[#34d399]' : 'border-[#00854080] bg-[#00854014] text-[#008540]'}`}>
                      <div className={`animate-spin h-4 w-4 border rounded-full border-t-transparent ${isDark ? 'border-[#34d399]' : 'border-[#008540]'}`} />
                      {processingStage || 'Loading index map…'}
                    </div>
                  )}

                  {processingError && activeAnalysisTab === 'heatmap' && (
                    <div
                      className={`mb-3 rounded-lg border px-3 py-2.5 text-xs leading-relaxed ${
                        processingError.includes('whole farm was under cloud') || processingError.includes('next observation date')
                          ? isDark
                            ? 'border-sky-500/50 bg-sky-950/40 text-sky-100'
                            : 'border-sky-300 bg-sky-50 text-sky-900'
                          : 'border-red-500/40 bg-red-900/20 text-red-300'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <i
                          className={`mt-0.5 ${
                            processingError.includes('whole farm was under cloud') || processingError.includes('next observation date')
                              ? 'ri-cloudy-2-line text-sky-400'
                              : 'ri-error-warning-line'
                          }`}
                          aria-hidden
                        />
                        <span title="Open Time series to see every date at once and pick a clear day.">
                          {processingError}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Cloud Masking Summary - Show after index map is generated */}
                  {indexHeatMapData && !isProcessingHeatMap && selectedIndex !== 'radar_soil_moisture' && heatMapSatellite !== 'S1' && (
                    <div className={ap.tsCloudMaskShell}>
                      <div className="flex items-center mb-2">
                        <i className={`ri-cloud-off-line mr-2 ${ap.tsCloudMaskIcon}`}></i>
                        <span className={ap.tsCloudMaskTitle}>Cloud Masking Applied</span>
                      </div>
                      <p className={ap.tsCloudMaskText}>
                        Cloud and cloud-shadow pixels are removed from the index map
                        {heatMapSatellite === 'L8'
                          ? ' (Landsat QA_PIXEL)'
                          : heatMapSatellite === 'S2'
                            ? ' (Sentinel-2 SCL)'
                            : ''}
                        . Only clear pixels are shown.
                      </p>
                    </div>
                  )}

                  {indexHeatMapData && !isProcessingHeatMap && (indexHeatMapData.spatialResolution || indexHeatMapData.spatial_resolution) && (
                    <div className={`mb-3 rounded-[10px] border px-3 py-2 text-xs ${isDark ? 'border-[#2d3748] bg-[#1c2432] text-[#c9d1d9]' : 'border-[#e5e7eb] bg-[#f3f4f6] text-[#374151]'}`}>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">Map detail:</span>
                        <span>
                          {indexHeatMapData.spatialResolution || indexHeatMapData.spatial_resolution} per pixel
                        </span>
                        <button
                          type="button"
                          title="What does this mean?"
                          aria-label="What does map detail / spatial resolution mean?"
                          aria-expanded={spatialResolutionInfoOpen}
                          onClick={() => setSpatialResolutionInfoOpen(true)}
                          className={`inline-flex items-center justify-center rounded-full p-0.5 transition-colors ${
                            isDark
                              ? 'text-[#8b949e] hover:text-[#34d399] hover:bg-white/[0.06]'
                              : 'text-[#6b7280] hover:text-[#008540] hover:bg-[#f3f4f6]'
                          }`}
                        >
                          <i className="ri-information-line text-sm" aria-hidden />
                        </button>
                      </div>
                      <p className={`mt-1 leading-snug ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        Smaller number = sharper view of your farm. Tap ⓘ to learn more.
                      </p>
                      <InfoPopupDialog
                        open={spatialResolutionInfoOpen}
                        onClose={() => setSpatialResolutionInfoOpen(false)}
                        title="How sharp is this map?"
                        accent="emerald"
                      >
                        <p>
                          <span className="font-semibold">In simple words:</span> “Spatial resolution”
                          means how big each tiny square (pixel) is on your farm. Think of the map as a
                          grid of colored tiles laid over the field.
                        </p>
                        <p>
                          <span className="font-semibold">Smaller number = sharper picture.</span>{" "}
                          Larger number = each tile covers more land, so fine details blend together.
                        </p>
                        <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <li>
                            <span className="font-semibold">10 m</span> — about 10 × 10 meters
                            (roughly a small room or a few crop rows).
                          </li>
                          <li>
                            <span className="font-semibold">20 m</span> — about 20 × 20 meters.
                          </li>
                          <li>
                            <span className="font-semibold">30 m</span> — about 30 × 30 meters;
                            still useful for whole-field patterns.
                          </li>
                        </ul>
                        <p>
                          <span className="font-semibold">This map: </span>
                          {indexHeatMapData.spatialResolution || indexHeatMapData.spatial_resolution} per
                          pixel.
                        </p>
                      </InfoPopupDialog>
                    </div>
                  )}

                  {/* Soil Moisture Statistics - Show after radar soil moisture is generated */}
                  {soilMoistureStats && !isProcessingHeatMap && selectedIndex === 'radar_soil_moisture' && (
                    <div className="mb-3 p-4 bg-gradient-to-br from-blue-900/40 to-cyan-900/30 rounded-lg border border-cyan-500/40 space-y-3">
                      <div className="flex items-center mb-2">
                        <i className="ri-water-percent-line text-cyan-400 mr-2 text-lg"></i>
                        <span className="text-sm font-semibold text-cyan-300">Soil Moisture Statistics</span>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className={`${ap.statBox} p-2 rounded`}>
                          <p className="text-xs text-gray-400 mb-1">Mean</p>
                          <p className="text-base font-bold text-cyan-300">
                            {(soilMoistureStats.mean * 100).toFixed(2)}%
                          </p>
                        </div>
                        <div className={`${ap.statBox} p-2 rounded`}>
                          <p className="text-xs text-gray-400 mb-1">Std Dev</p>
                          <p className="text-base font-bold text-cyan-300">
                            {(soilMoistureStats.std * 100).toFixed(2)}%
                          </p>
                        </div>
                      </div>

                      <div className={`${ap.statBox} p-3 rounded space-y-2`}>
                        <p className={`text-xs font-semibold ${ap.accentText}`}>Metadata</p>
                        <div className={`space-y-1.5 text-xs ${ap.body}`}>
                          <div className="flex justify-between">
                            <span className={ap.muted}>Algorithm:</span>
                            <span className={`font-medium ${ap.heading}`}>{soilMoistureStats.metadata.algorithm}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className={ap.muted}>Memory Days:</span>
                            <span className={`font-medium ${ap.heading}`}>{soilMoistureStats.metadata.memory_days}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className={ap.muted}>Frequency:</span>
                            <span className={`font-medium ${ap.heading}`}>{soilMoistureStats.metadata.frequency_band}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className={ap.muted}>Units:</span>
                            <span className={`font-medium ${ap.heading}`}>{soilMoistureStats.metadata.units}</span>
                          </div>
                        </div>
                        <div className={`pt-2 border-t ${isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]'}`}>
                          <p className={`text-xs italic leading-relaxed ${ap.muted}`}>
                            {soilMoistureStats.metadata.paper_reference}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Opacity Control - Show only when heat map is available */}
                  {indexHeatMapData && !isProcessingHeatMap && (
                    <div className={ap.tsOpacityShell}>
                      <label className={`block text-xs ${ap.label} mb-2`}>
                        Index Map Transparency: {Math.round(heatMapOpacity * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={heatMapOpacity}
                        onChange={(e) => {
                          const newOpacity = parseFloat(e.target.value);
                          setHeatMapOpacity(newOpacity);
                          if (onAnalysisRun) {
                            onAnalysisRun('adjust-heatmap-opacity', { opacity: newOpacity });
                          }
                        }}
                        className={ap.tsRangeClass}
                      />
                      <div className={`flex justify-between text-xs mt-1 ${ap.muted}`}>
                        <span>0% (Hidden)</span>
                        <span>100% (Solid)</span>
                      </div>
                    </div>
                  )}

                  {/* Export Map Buttons - Show only when heat map is available */}
                  {indexHeatMapData && !isProcessingHeatMap && (
                    <div className="space-y-2">
                      {isExporting && (
                        <div className={`text-center text-xs py-2 rounded-[10px] ${ap.infoNote}`}>
                          <i className="ri-download-line animate-pulse mr-1"></i>
                          Capturing map screenshot...
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => exportMap('png')}
                          disabled={isExporting}
                          className={`px-3 py-2 rounded-[10px] text-sm font-medium transition-colors flex items-center justify-center ${isExporting
                            ? `${ap.btnDisabled} cursor-not-allowed`
                            : ap.primaryBtn
                            }`}
                          title="Export map as PNG image"
                        >
                          {isExporting ? (
                            <div className="animate-spin w-4 h-4 border border-white rounded-full border-t-transparent mr-1"></div>
                          ) : (
                            <i className="ri-image-line mr-1"></i>
                          )}
                          PNG
                        </button>
                        <button
                          onClick={() => exportMap('pdf')}
                          disabled={isExporting}
                          className={`px-3 py-2 rounded-[10px] text-sm font-medium transition-colors flex items-center justify-center ${isExporting
                            ? `${ap.btnDisabled} cursor-not-allowed`
                            : ap.primaryBtn
                            }`}
                          title="Export map as PDF with metadata"
                        >
                          {isExporting ? (
                            <div className="animate-spin w-4 h-4 border border-white rounded-full border-t-transparent mr-1"></div>
                          ) : (
                            <i className="ri-file-pdf-line mr-1"></i>
                          )}
                          PDF
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Satellite + forecast alerts — saved fields with farm_id */}
              {activeAnalysisTab === 'alerts' && (canShowFarmAlertsTab || canShowCADI || canShowPest) && (
                <div className={isInlineCard ? 'mb-3' : 'mb-6'}>
                  {farmAlertsLoading && canShowFarmAlertsTab && (
                    <ApiListSkeleton count={3} label="Loading alerts" className="mb-3" />
                  )}

                  {/* Category filters — All, Drought, Pest, Crop Health, Weather */}
                  {!farmAlertsLoading && hasAnyFarmAlerts && (
                    <div className={`mb-3 flex flex-wrap gap-1 ${isInlineCard ? 'text-[9px]' : 'text-[10px]'}`}>
                      {(
                        [
                          { key: 'all',         label: 'All',          icon: 'ri-apps-line' },
                          { key: 'drought',     label: 'Drought',      show: hasCADIData,          icon: 'ri-bar-chart-2-line' },
                          { key: 'pest',        label: 'Pest',         show: hasPestData,          icon: 'ri-bug-line' },
                          { key: 'cropHealth',  label: 'Crop Health',  show: hasCropHealthAlerts,  icon: 'ri-plant-line' },
                          { key: 'weather',     label: 'Weather',      show: hasWeatherAlerts,     icon: 'ri-cloud-rain-line' },
                        ] as { key: typeof alertsFilter; label: string; show?: boolean; icon?: string }[]
                      )
                        .filter((f) => f.key === 'all' || f.show)
                        .map(({ key, label, icon }) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setAlertsFilter(key)}
                            className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 font-medium transition-colors ${
                              alertsFilter === key
                                ? 'border-transparent bg-[#008540] text-white'
                                : isDark
                                  ? 'border-gray-700 bg-gray-800/60 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                                  : 'border-gray-200 bg-white text-gray-500 hover:text-gray-800 hover:border-gray-300'
                            }`}
                          >
                            {icon && <i className={icon} aria-hidden />}
                            {label}
                          </button>
                        ))}
                    </div>
                  )}

                  {/* ── 1. Drought Outlook (CADI) — one alert, chart hidden ── */}
                  {hasCADIData && showAlertsDrought && (
                    <>
                      {cadiState.status === 'loaded' && cadiAlertPoint && (
                        <div className={`${isInlineCard ? 'mb-3 space-y-2' : 'mb-4 space-y-2.5'}`}>
                          <FarmCadiMonthAlertCard
                            key={cadiAlertPoint.date}
                            point={cadiAlertPoint}
                            compact={isInlineCard}
                            showActions={Boolean(cadiFarmId)}
                            canSend={Boolean(cadiFarmId)}
                            onSend={
                              cadiFarmId
                                ? () => void handleSendCADIAlert(cadiAlertPoint)
                                : undefined
                            }
                            onViewFeedback={
                              cadiFarmId ? () => void handleToggleCADIFeedback() : undefined
                            }
                            sending={cadiSending}
                            feedbackLoading={cadiFeedbackLoading}
                            feedbackOpen={cadiFeedbackOpen}
                          />

                          {cadiFarmId ? (
                            <>
                              {cadiSendFeedback && (
                                <p
                                  role="status"
                                  className={`${isInlineCard ? 'text-[10px]' : 'text-[11px]'} leading-snug ${
                                    cadiSendFeedback.type === 'success'
                                      ? isDark
                                        ? 'text-emerald-300'
                                        : 'text-[#008540]'
                                      : 'text-red-500'
                                  }`}
                                >
                                  {cadiSendFeedback.message}
                                </p>
                              )}
                              {cadiFeedbackOpen && (
                                <FarmAlertFeedbackList
                                  loading={cadiFeedbackLoading}
                                  error={cadiFeedbackError}
                                  feedbacks={cadiFeedbackItems}
                                  compact={isInlineCard}
                                  onCheckBeforeAfter={
                                    canFarmTimeSeries ? handleCheckFeedbackTimeSeries : undefined
                                  }
                                  onZoomToLocation={handleZoomToFeedbackLocation}
                                />
                              )}
                            </>
                          ) : (
                            <p
                              className={`${isInlineCard ? 'text-[9px]' : 'text-[10px]'} ${ap.faint} flex items-center gap-1`}
                            >
                              <i className="ri-save-line shrink-0" aria-hidden />
                              Save this field to send drought alerts and view farmer feedback.
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* ── 1b. Pest Risk — highest current-quarter pest ── */}
                  {hasPestData && showAlertsPest && (
                    <>
                      {pestState.status === 'loaded' && pestAlertPoint && (
                        <div className={`${isInlineCard ? 'mb-3 space-y-2' : 'mb-4 space-y-2.5'}`}>
                          <FarmPestAlertCard
                            key={`${pestAlertPoint.pestKey}-${pestAlertPoint.quarter}`}
                            point={pestAlertPoint}
                            lastUpdated={pestLastUpdated}
                            compact={isInlineCard}
                            showActions={Boolean(pestFarmId)}
                            canSend={Boolean(pestFarmId)}
                            onSend={
                              pestFarmId
                                ? () => void handleSendPestAlert(pestAlertPoint)
                                : undefined
                            }
                            onViewFeedback={
                              pestFarmId ? () => void handleTogglePestFeedback() : undefined
                            }
                            sending={pestSending}
                            feedbackLoading={pestFeedbackLoading}
                            feedbackOpen={pestFeedbackOpen}
                          />

                          {pestFarmId ? (
                            <>
                              {pestSendFeedback && (
                                <p
                                  role="status"
                                  className={`${isInlineCard ? 'text-[10px]' : 'text-[11px]'} leading-snug ${
                                    pestSendFeedback.type === 'success'
                                      ? isDark
                                        ? 'text-emerald-300'
                                        : 'text-[#008540]'
                                      : 'text-red-500'
                                  }`}
                                >
                                  {pestSendFeedback.message}
                                </p>
                              )}
                              {pestFeedbackOpen && (
                                <FarmAlertFeedbackList
                                  loading={pestFeedbackLoading}
                                  error={pestFeedbackError}
                                  feedbacks={pestFeedbackItems}
                                  compact={isInlineCard}
                                  onCheckBeforeAfter={
                                    canFarmTimeSeries ? handleCheckFeedbackTimeSeries : undefined
                                  }
                                  onZoomToLocation={handleZoomToFeedbackLocation}
                                />
                              )}
                            </>
                          ) : (
                            <p
                              className={`${isInlineCard ? 'text-[9px]' : 'text-[10px]'} ${ap.faint} flex items-center gap-1`}
                            >
                              <i className="ri-save-line shrink-0" aria-hidden />
                              Save this field to send pest alerts and view farmer feedback.
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* ── 2. Crop health (satellite + vegetation/moisture forecasts) ─ */}
                  {!farmAlertsLoading && hasCropHealthAlerts && showAlertsCropHealth && (
                    <>
                      {hasSatelliteFarmAlerts && (
                        <>
                      <div className={`${isInlineCard ? 'mb-3 space-y-1.5' : 'mb-4 space-y-2'}`}>
                        {farmNdviAlert && (
                          <div className={isInlineCard ? 'space-y-1.5' : 'space-y-2'}>
                            <FarmSatelliteAlertCard
                              title="Vegetation"
                              icon="ri-leaf-line"
                              message={farmNdviAlert.alert_message}
                              variant="vegetation"
                              alertKind="ndvi"
                              compact={isInlineCard}
                              actionsDisabled={farmAlertsLoading}
                              sending={alertSendingKind === 'ndvi'}
                              feedbackLoading={alertFeedbackByKind.ndvi.loading}
                              feedbackOpen={alertFeedbackByKind.ndvi.open}
                              onSend={() => void handleSendSingleAlert('ndvi')}
                              onViewFeedback={() => void handleToggleCardFeedback('ndvi')}
                            />
                            {alertSendFeedbackByKind.ndvi && (
                              <p
                                role="status"
                                className={`text-[11px] leading-snug ${
                                  alertSendFeedbackByKind.ndvi.type === 'success'
                                    ? isDark
                                      ? 'text-emerald-300'
                                      : 'text-[#008540]'
                                    : 'text-red-500'
                                }`}
                              >
                                {alertSendFeedbackByKind.ndvi.message}
                              </p>
                            )}
                            {alertFeedbackByKind.ndvi.open && (
                              <FarmAlertFeedbackList
                                loading={alertFeedbackByKind.ndvi.loading}
                                error={alertFeedbackByKind.ndvi.error}
                                feedbacks={alertFeedbackByKind.ndvi.items}
                                compact={isInlineCard}
                                onCheckBeforeAfter={
                                  canFarmTimeSeries ? handleCheckFeedbackTimeSeries : undefined
                                }
                                onZoomToLocation={handleZoomToFeedbackLocation}
                              />
                            )}
                          </div>
                        )}
                        {farmNdmiAlert && (
                          <div className={isInlineCard ? 'space-y-1.5' : 'space-y-2'}>
                            <FarmSatelliteAlertCard
                              title="Moisture"
                              icon="ri-drop-fill"
                              message={farmNdmiAlert.alert_message}
                              variant="moisture"
                              alertKind="ndmi"
                              compact={isInlineCard}
                              actionsDisabled={farmAlertsLoading}
                              sending={alertSendingKind === 'ndmi'}
                              feedbackLoading={alertFeedbackByKind.ndmi.loading}
                              feedbackOpen={alertFeedbackByKind.ndmi.open}
                              onSend={() => void handleSendSingleAlert('ndmi')}
                              onViewFeedback={() => void handleToggleCardFeedback('ndmi')}
                            />
                            {alertSendFeedbackByKind.ndmi && (
                              <p
                                role="status"
                                className={`text-[11px] leading-snug ${
                                  alertSendFeedbackByKind.ndmi.type === 'success'
                                    ? isDark
                                      ? 'text-emerald-300'
                                      : 'text-[#008540]'
                                    : 'text-red-500'
                                }`}
                              >
                                {alertSendFeedbackByKind.ndmi.message}
                              </p>
                            )}
                            {alertFeedbackByKind.ndmi.open && (
                              <FarmAlertFeedbackList
                                loading={alertFeedbackByKind.ndmi.loading}
                                error={alertFeedbackByKind.ndmi.error}
                                feedbacks={alertFeedbackByKind.ndmi.items}
                                compact={isInlineCard}
                                onCheckBeforeAfter={
                                  canFarmTimeSeries ? handleCheckFeedbackTimeSeries : undefined
                                }
                                onZoomToLocation={handleZoomToFeedbackLocation}
                              />
                            )}
                          </div>
                        )}
                      </div>
                        </>
                      )}

                      {cropHealthForecastCount > 0 && (
                        <>
                          {hasCropHealthForecastCharts && (
                            <div className={`${isInlineCard ? 'mb-3 space-y-2' : 'mb-4 space-y-3'}`}>
                              {forecastChartVisibility.ndvi && (
                                <FarmForecastTrendChart
                                  title="Vegetation (NDVI)"
                                  alerts={sortedForecastAlerts.ndvi}
                                  valueKind="ndvi"
                                  compact={isInlineCard}
                                />
                              )}
                              {forecastChartVisibility.ndmi && (
                                <FarmForecastTrendChart
                                  title="Moisture (NDMI)"
                                  alerts={sortedForecastAlerts.ndmi}
                                  valueKind="ndmi"
                                  compact={isInlineCard}
                                />
                              )}
                            </div>
                          )}

                          {hasCropHealthForecastActions && (
                            <>
                              <div className={`${isInlineCard ? 'mb-3 space-y-1.5' : 'mb-4 space-y-2'}`}>
                                {actionableForecastAlerts(sortedForecastAlerts.ndvi).map((alert) => (
                                  <FarmForecastAlertRow
                                    key={`ndvi-fc-${alert.id}`}
                                    alert={alert}
                                    compact={isInlineCard}
                                    actionsDisabled={farmAlertsLoading}
                                    sending={forecastAlertSendingId === alert.id}
                                    sendFeedback={forecastAlertSendFeedbackById[alert.id]}
                                    feedbackState={getForecastAlertFeedbackState(alert.id)}
                                    onSend={() => void handleSendForecastAlert(alert)}
                                    onViewFeedback={() => void handleToggleForecastFeedback(alert)}
                                    onCheckBeforeAfter={
                                      canFarmTimeSeries ? handleCheckFeedbackTimeSeries : undefined
                                    }
                                    onZoomToLocation={handleZoomToFeedbackLocation}
                                  />
                                ))}
                                {actionableForecastAlerts(sortedForecastAlerts.ndmi).map((alert) => (
                                  <FarmForecastAlertRow
                                    key={`ndmi-fc-${alert.id}`}
                                    alert={alert}
                                    compact={isInlineCard}
                                    actionsDisabled={farmAlertsLoading}
                                    sending={forecastAlertSendingId === alert.id}
                                    sendFeedback={forecastAlertSendFeedbackById[alert.id]}
                                    feedbackState={getForecastAlertFeedbackState(alert.id)}
                                    onSend={() => void handleSendForecastAlert(alert)}
                                    onViewFeedback={() => void handleToggleForecastFeedback(alert)}
                                    onCheckBeforeAfter={
                                      canFarmTimeSeries ? handleCheckFeedbackTimeSeries : undefined
                                    }
                                    onZoomToLocation={handleZoomToFeedbackLocation}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* ── 3. Weather outlook (next-day alerts only; no rain/temp charts) ─ */}
                  {!farmAlertsLoading && hasWeatherAlerts && showAlertsWeather && (
                    <div className={`${isInlineCard ? 'mb-3 space-y-1.5' : 'mb-4 space-y-2'}`}>
                      {actionableForecastAlerts(sortedForecastAlerts.weather).map((alert) => (
                        <FarmForecastAlertRow
                          key={`wx-fc-${alert.id}`}
                          alert={alert}
                          compact={isInlineCard}
                          actionsDisabled={farmAlertsLoading}
                          sending={forecastAlertSendingId === alert.id}
                          sendFeedback={forecastAlertSendFeedbackById[alert.id]}
                          feedbackState={getForecastAlertFeedbackState(alert.id)}
                          onSend={() => void handleSendForecastAlert(alert)}
                          onViewFeedback={() => void handleToggleForecastFeedback(alert)}
                          onCheckBeforeAfter={
                            canFarmTimeSeries ? handleCheckFeedbackTimeSeries : undefined
                          }
                          onZoomToLocation={handleZoomToFeedbackLocation}
                        />
                      ))}
                    </div>
                  )}


                  {/* Demo field notice — CADI/pest show above; satellite/forecast need a saved farm */}
                  {!farmAlertsLoading && !canShowFarmAlertsTab && (canShowCADI || canShowPest) && (cadiState.status === 'loaded' || pestState.status === 'loaded') && (
                    <div className={`mt-3 rounded-xl border px-3 py-2.5 ${ap.cardSofter} ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <p className={`text-[10px] ${ap.muted}`}>
                        <i className="ri-information-line mr-1" aria-hidden />
                        Satellite, crop health, and weather forecasts are only available for saved farms.{' '}
                        <button
                          type="button"
                          className={`underline ${ap.link ?? 'text-[#008540]'}`}
                          onClick={() => selectAnalysisTab('heatmap')}
                        >
                          View Index Map
                        </button>
                      </p>
                    </div>
                  )}

                  {!farmAlertsLoading && !hasSatelliteFarmAlerts && forecastAlertCount === 0 && !hasCADIData && !hasPestData && (
                    <div className={`rounded-xl border px-4 py-6 text-center ${ap.cardSolid}`}>
                      <i className={`ri-notification-off-line mb-2 text-3xl ${ap.faint}`} aria-hidden />
                      <p className={`mb-1 text-sm font-medium ${ap.subheading}`}>No alerts yet</p>
                      <p className={`mb-4 text-xs ${ap.muted}`}>
                        {canShowFarmAlertsTab
                          ? 'Crop health and weather forecasts will appear here when available.'
                          : 'Save this field to see crop health and weather forecast alerts.'}
                      </p>
                      <button
                        type="button"
                        onClick={() => selectAnalysisTab('heatmap')}
                        className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-xs font-medium ${ap.primaryBtn}`}
                      >
                        <i className="ri-map-2-line mr-2" aria-hidden />
                        Go to Index Map
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Time Series Analysis */}
              {activeAnalysisTab === 'timeseries' && canFarmTimeSeries && !multiFieldMode && (
                <div className={isInlineCard ? 'mb-3' : 'mb-6'}>
                  {!isInlineCard && (
                    <div className="flex items-center justify-between mb-4">
                      <h4 className={`text-sm font-medium ${ap.heading}`}>Time Series Analysis</h4>
                    </div>
                  )}

                  {feedbackTimeSeriesContext && (
                    <div className={`mb-4 rounded-xl border px-3 py-2.5 ${ap.cardSofter}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-semibold ${ap.heading} ${isInlineCard ? 'text-[11px]' : 'text-xs'}`}>
                            Before / after intervention
                          </p>
                          <p className={`mt-1 leading-snug ${ap.muted} ${isInlineCard ? 'text-[10px]' : 'text-[11px]'}`}>
                            Action date{' '}
                            {new Date(`${feedbackTimeSeriesContext.plan.actionDate}T12:00:00`).toLocaleDateString(
                              'en-US',
                              { year: 'numeric', month: 'short', day: 'numeric' }
                            )}
                            .
                            {feedbackTimeSeriesContext.pair
                              ? ` Comparing the clearest scene near that date (${new Date(`${feedbackTimeSeriesContext.pair.nearDate}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${feedbackTimeSeriesContext.pair.nearCloud != null ? `, ${Math.round(feedbackTimeSeriesContext.pair.nearCloud)}% cloud` : ''}) with the clearest scene after the action (${new Date(`${feedbackTimeSeriesContext.pair.afterDate}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${feedbackTimeSeriesContext.pair.afterCloud != null ? `, ${Math.round(feedbackTimeSeriesContext.pair.afterCloud)}% cloud` : ''}).`
                              : ` Searching for one low-cloud scene near the action and one after it — not the full image stack.`}
                            {feedbackTimeSeriesContext.actionsTaken.length > 0
                              ? ` Action: ${feedbackTimeSeriesContext.actionsTaken.join(', ')}.`
                              : ''}
                            {feedbackTimeSeriesContext.effect
                              ? ` Farmer-reported effect: ${feedbackTimeSeriesContext.effect}.`
                              : ''}
                            {feedbackTimeSeriesContext.pairError
                              ? ` ${feedbackTimeSeriesContext.pairError}`
                              : ''}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFeedbackTimeSeriesContext(null)}
                          className={`${ap.faint} p-1`}
                          title="Dismiss"
                        >
                          <i className="ri-close-line" aria-hidden />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Index Type */}
                  <div className={isInlineCard ? 'mb-2' : 'mb-4'}>
                    <div className={`flex items-center gap-1.5 ${isInlineCard ? 'mb-1' : 'mb-2'}`}>
                      <label className={`text-xs ${ap.label}`}>Index</label>
                      <button
                        type="button"
                        title="How to read this index"
                        aria-label="How to read this index"
                        onClick={() => setIndexGuideOpen(true)}
                        className={`inline-flex items-center justify-center rounded-full p-0.5 ${
                          isDark ? 'text-[#8b949e] hover:text-[#34d399]' : 'text-[#6b7280] hover:text-[#008540]'
                        }`}
                      >
                        <i className="ri-information-line text-sm" aria-hidden />
                      </button>
                    </div>
                    <div className="relative">
                      <select
                        value={selectedIndex}
                        onChange={(e) => {
                          const newIndex = e.target.value;

                          // Only clear if there's existing timeseries data and we're changing to a different type
                          if (timeSeriesData &&
                            ((timeSeriesData.isSentinel1 && newIndex !== 'sentinel1') ||
                              (!timeSeriesData.isSentinel1 && newIndex === 'sentinel1') ||
                              (timeSeriesData.index && timeSeriesData.index !== newIndex))) {

                            // Clear existing results when switching between different data types
                            setTimeSeriesSliderIndex(0);
                            setIsPlayingTimeSeries(false);

                            if (onAnalysisRun) {
                              onAnalysisRun('clear', {
                                type: 'clear-timeseries',
                                clearMap: true
                              });
                            }
                          }

                          setSelectedIndex(newIndex);
                        }}
                        className={`w-full ${ap.select}`}
                        disabled={isProcessingTimeSeries}
                      >
                        {timeSeriesIndexOptions.map((index) => (
                          <option key={index.value} value={index.value}>{index.label}</option>
                        ))}
                      </select>
                      <i className={`ri-arrow-down-s-line absolute right-2 top-2.5 pointer-events-none ${ap.selectIcon}`}></i>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className={isInlineCard ? 'mb-2' : 'mb-4'}>
                    <label className={`block text-xs ${ap.label} ${isInlineCard ? 'mb-1' : 'mb-2'}`}>Date Range</label>
                    <div className={`grid grid-cols-2 gap-2 ${isInlineCard ? 'mb-2' : 'mb-3'}`}>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          const next = onStartDateChange(
                            e.target.value,
                            endDate,
                            startDate
                          );
                          setStartDate(next.start);
                          setEndDate(next.end);
                        }}
                        className={`${ap.select.replace('w-full ', '')}`}
                        disabled={isProcessingTimeSeries}
                      />
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          const next = onEndDateChange(
                            startDate,
                            e.target.value,
                            endDate
                          );
                          setStartDate(next.start);
                          setEndDate(next.end);
                        }}
                        className={`${ap.select.replace('w-full ', '')}`}
                        disabled={isProcessingTimeSeries}
                      />
                    </div>
                  </div>

                  {/* Generate button – always directly under Date Range */}
                  <button
                    onClick={handleRunAnalysis}
                    disabled={timeSeriesGenerateDisabled}
                    className={`w-full rounded font-medium transition-colors flex items-center justify-center ${
                      isInlineCard ? 'px-3 py-1.5 text-xs mb-2' : 'px-4 py-2 text-sm mb-4'
                    } ${
                      timeSeriesGenerateDisabled
                        ? ap.tsGenerateDisabled
                        : ap.primaryBtn
                      }`}
                  >
                    {isProcessingTimeSeries ? (
                      <>
                        <div className="animate-spin h-4 w-4 border border-white rounded-full border-t-transparent mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className={`ri-line-chart-line ${isInlineCard ? 'mr-1' : 'mr-2'}`} aria-hidden />
                        {isInlineCard ? 'Generate' : 'Generate Time Series'}
                      </>
                    )}
                  </button>

                  {/* Processing Status */}
                  {isProcessingTimeSeries && (
                    <div className={ap.tsProcessingShell}>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className={ap.tsProcessingTitle}>Generating Time Series</h5>
                        <div className={ap.tsProcessingProgress}>{processingProgress}%</div>
                      </div>

                      <div className={ap.tsProgressTrack}>
                        <div
                          className={ap.tsProgressBar}
                          style={{ width: `${processingProgress}%` }}
                        ></div>
                      </div>

                      <div className={ap.tsProcessingText}>
                        <div className={`animate-spin h-4 w-4 border rounded-full border-t-transparent mr-3 ${isDark ? 'border-emerald-400' : 'border-[#008540]'}`}></div>
                        <span>{processingStage}</span>
                      </div>
                    </div>
                  )}

                  {processingError && activeAnalysisTab === 'timeseries' && (
                    <div className={ap.tsErrorShell}>
                      <div className={`${ap.tsErrorText} text-sm`}>
                        <i className="ri-error-warning-line mr-2"></i>
                        <span>{processingError}</span>
                      </div>
                    </div>
                  )}

                  {timeSeriesData?.results?.some((result: any) => result.cloud_cover !== undefined || result.cloud_percentage !== undefined) && chartData.length > 0 && (
                    <div className={ap.tsFilterShell}>
                      <div className={`flex items-center justify-between ${isInlineCard ? 'mb-1' : 'mb-2'}`}>
                        <div className="min-w-0">
                          <label className={ap.tsFilterLabel}>Cloudy dates</label>
                          <p className={`${isInlineCard ? 'text-[10px]' : 'text-xs'} ${ap.muted}`}>
                            Hide satellite photos that are too cloudy to trust
                          </p>
                        </div>
                        <button
                          onClick={handleCloudFilterToggle}
                          className={`${isInlineCard ? 'px-2 py-0.5 text-[10px]' : 'px-3 py-1 text-xs'} shrink-0 rounded flex items-center font-medium transition-colors ${showCloudFilter ? ap.tsFilterOn : ap.tsFilterOff}`}
                        >
                          <i className="ri-cloud-line mr-1" aria-hidden />
                          {showCloudFilter ? 'ON' : 'OFF'}
                        </button>
                      </div>

                      {showCloudFilter && (
                        <>
                          <label className={`block ${isInlineCard ? 'text-[10px]' : 'text-xs'} ${ap.label} mb-1`}>
                            Keep dates under {maxCloudCoverage}% cloud
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={
                              typeof maxCloudCoverage === 'number' && !Number.isNaN(maxCloudCoverage)
                                ? maxCloudCoverage
                                : DEFAULT_MAX_CLOUD_COVERAGE_PERCENT
                            }
                            onChange={(e) => handleCloudCoverageChange(Number(e.target.value))}
                            className="w-full h-1.5 bg-gradient-to-r from-green-200 to-red-200 rounded-lg appearance-none cursor-pointer"
                            aria-label="Maximum cloud coverage"
                          />
                          <p className={`mt-1.5 ${isInlineCard ? 'text-[10px]' : 'text-xs'} ${ap.muted}`}>
                            Showing {filteredTimeSeriesData?.results?.length || 0} of {timeSeriesData?.results?.length || 0} dates
                          </p>
                        </>
                      )}
                    </div>
                  )}

                  {/* Time Series Line Chart */}
                  {chartData.length > 0 && filteredTimeSeriesData && (
                    filteredTimeSeriesData.index === selectedIndex ||
                    (filteredTimeSeriesData.isSentinel1 && selectedIndex === 'sentinel1')
                  ) && (
                      <div className={ap.tsChartShell}>
                        <div className={`flex items-center justify-between ${isInlineCard ? 'mb-2' : 'mb-4'}`}>
                          <h5 className={ap.tsChartTitle}>
                            {isInlineCard ? 'How this field changed' : `${selectedIndex.toUpperCase()} Time Series`}
                          </h5>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={exportTimeSeriesCSV}
                              className={`${isExporting ? ap.tsExportBtnDisabled : ap.tsExportBtn}`}
                              disabled={!filteredTimeSeriesData?.results || filteredTimeSeriesData.results.length === 0 || isExporting}
                              title={showCloudFilter ? `Export ${filteredTimeSeriesData?.results?.length || 0} filtered tiles (≤${maxCloudCoverage}% cloud)` : `Export all ${filteredTimeSeriesData?.results?.length || 0} tiles`}
                            >
                              {isExporting ? (
                                <div className="animate-spin w-3 h-3 border border-white rounded-full border-t-transparent mr-1"></div>
                              ) : (
                                <i className="ri-download-line mr-1"></i>
                              )}
                              CSV
                            </button>
                            <div className={`${isInlineCard ? 'text-[10px]' : 'text-xs'} ${ap.muted}`}>
                              {chartData.length} obs
                            </div>
                          </div>
                        </div>

                        {/* Compact cloud legend — inline under title */}
                        {!(timeSeriesData?.isSentinel1 || selectedIndex === 'sentinel1') && (
                          <div className={ap.tsChartLegend}>
                            <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${ap.tsChartLegendText}`}>
                              <span className={ap.tsChartLegendLabel}>Cloud:</span>
                              <span className="inline-flex items-center gap-1">
                                <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden />
                                Low
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <span className="h-2 w-2 rounded-full bg-yellow-500" aria-hidden />
                                Med
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <span className="h-2 w-2 rounded-full bg-red-500" aria-hidden />
                                High
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Sentinel-1 specific legend */}
                        {(timeSeriesData?.isSentinel1 || selectedIndex === 'sentinel1') && (
                          <div className={ap.tsChartLegend}>
                            <div className="flex items-center justify-between mb-1">
                              <span className={ap.tsChartLegendLabel}>Sentinel-1 SAR Data:</span>
                            </div>
                            <div className={`space-y-1 ${ap.tsChartLegendText}`}>
                              <div>• Backscatter values in dB (-15 to -25 range)</div>
                              <div>• Weather independent (no cloud coverage)</div>
                              <div>• Band: VH polarization (default)</div>
                              <div>• Orbit: DESCENDING pass (default)</div>
                            </div>
                          </div>
                        )}

                        {feedbackTimeSeriesContext && interventionImpact && (
                          <div className={`mb-3 grid grid-cols-3 gap-2 ${isInlineCard ? 'text-[10px]' : 'text-[11px]'}`}>
                            <div className={`rounded-lg border px-2 py-1.5 ${ap.cardSolid}`}>
                              <div className={ap.faint}>Before</div>
                              <div className={`font-semibold ${ap.heading}`}>
                                {interventionImpact.beforeMean.toFixed(3)}
                              </div>
                              <div className={ap.faint}>{interventionImpact.beforeCount} scenes</div>
                            </div>
                            <div className={`rounded-lg border px-2 py-1.5 ${ap.cardSolid}`}>
                              <div className={ap.faint}>After</div>
                              <div className={`font-semibold ${ap.heading}`}>
                                {interventionImpact.afterMean.toFixed(3)}
                              </div>
                              <div className={ap.faint}>{interventionImpact.afterCount} scenes</div>
                            </div>
                            <div className={`rounded-lg border px-2 py-1.5 ${ap.cardSolid}`}>
                              <div className={ap.faint}>Change</div>
                              <div
                                className={`font-semibold ${
                                  interventionImpact.delta > 0
                                    ? 'text-emerald-500'
                                    : interventionImpact.delta < 0
                                      ? 'text-amber-500'
                                      : ap.heading
                                }`}
                              >
                                {interventionImpact.delta > 0 ? '+' : ''}
                                {interventionImpact.delta.toFixed(3)}
                              </div>
                              <div className={ap.faint}>{selectedIndex.toUpperCase()}</div>
                            </div>
                          </div>
                        )}

                        {/* Chart Container */}
                        <div
                          className="relative w-full"
                          style={{ height: isInlineCard ? '200px' : '360px' }}
                        >
                          <svg width="100%" height="100%" viewBox="0 0 500 300" className="overflow-visible">
                            {/* Chart background */}
                            <rect width="450" height="250" x="40" y="20" fill={ap.chartBg} stroke="none" />

                            {/* Horizontal grid lines */}
                            {[0, 0.2, 0.4, 0.6, 0.8, 1].map((ratio, index) => {
                              const y = 270 - (ratio * 250);
                              const value = minValue + (maxValue - minValue) * ratio;
                              return (
                                <g key={index}>
                                  <line
                                    x1="40"
                                    y1={y}
                                    x2="490"
                                    y2={y}
                                    stroke={ap.chartGrid}
                                    strokeWidth="1"
                                  />
                                  <text
                                    x="35"
                                    y={y + 3}
                                    fill={ap.chartLabel}
                                    fontSize="20"
                                    textAnchor="end"
                                  >
                                    {value.toFixed(2)}
                                  </text>
                                </g>
                              );
                            })}

                            {feedbackTimeSeriesContext && chartData.length > 1 && (() => {
                              const first = new Date(chartData[0].date).getTime();
                              const last = new Date(chartData[chartData.length - 1].date).getTime();
                              const action = new Date(
                                `${feedbackTimeSeriesContext.plan.actionDate}T12:00:00`
                              ).getTime();
                              if (
                                Number.isNaN(first) ||
                                Number.isNaN(last) ||
                                Number.isNaN(action) ||
                                last <= first ||
                                action < first ||
                                action > last
                              ) {
                                return null;
                              }
                              const x = 40 + ((action - first) / (last - first)) * 450;
                              return (
                                <g>
                                  <line
                                    x1={x}
                                    y1="20"
                                    x2={x}
                                    y2="270"
                                    stroke="#f59e0b"
                                    strokeWidth="1.5"
                                    strokeDasharray="4 3"
                                  />
                                  <text
                                    x={x}
                                    y="16"
                                    fill="#f59e0b"
                                    fontSize="11"
                                    textAnchor="middle"
                                  >
                                    Action
                                  </text>
                                </g>
                              );
                            })()}

                            {/* Chart line */}
                            <path
                              d={chartData.map((data, index) => {
                                const x = 40 + (index / (chartData.length - 1)) * 450;
                                const normalizedValue = maxValue > minValue
                                  ? ((data.value - minValue) / (maxValue - minValue))
                                  : 0.5;
                                const y = 270 - (normalizedValue * 250);
                                return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                              }).join(' ')}
                              fill="none"
                              stroke={ap.chartLine}
                              strokeWidth="2"
                            />

                            {/* Data points */}
                            {chartData.map((data, index) => {
                              const x = 40 + (index / (chartData.length - 1)) * 450;
                              const normalizedValue = maxValue > minValue
                                ? ((data.value - minValue) / (maxValue - minValue))
                                : 0.5;
                              const y = 270 - (normalizedValue * 250);
                              const isSelected = index === timeSeriesSliderIndex;
                              const isSentinel1 = timeSeriesData?.isSentinel1 || selectedIndex === 'sentinel1';

                              // Determine color based on data type
                              let fillColor = ap.chartPointDefault;

                              if (isSentinel1) {
                                fillColor = isSelected ? ap.chartPointSelected : ap.chartPointDefault;
                              } else {
                                if (data.cloudCoverage !== undefined) {
                                  if (data.cloudCoverage > 80) fillColor = "#ef4444";
                                  else if (data.cloudCoverage > 50) fillColor = "#f59e0b";
                                  else fillColor = isDark ? "#34d399" : "#008540";
                                }
                                if (isSelected) fillColor = ap.chartPointSelected;
                              }

                              return (
                                <g key={index}>
                                  <circle
                                    cx={x}
                                    cy={y}
                                    r={isSelected ? "8" : "4"}
                                    fill={fillColor}
                                    stroke={ap.chartBg}
                                    strokeWidth="2"
                                    className="cursor-pointer transition-all duration-200"
                                    onClick={() => handleTimeSeriesSliderChange(index)}
                                  />
                                  {/* Cloud coverage indicator ring for high cloud coverage - only for optical data */}
                                  {!isSentinel1 && data.cloudCoverage !== undefined && data.cloudCoverage > 70 && !isSelected && (
                                    <circle
                                      cx={x}
                                      cy={y}
                                      r="6"
                                      fill="none"
                                      stroke="#f59e0b"
                                      strokeWidth="1"
                                      strokeDasharray="2,2"
                                      className="opacity-70"
                                    />
                                  )}
                                </g>
                              );
                            })}

                            {/* X-axis */}
                            <line x1="40" y1="270" x2="490" y2="270" stroke={ap.chartAxis} strokeWidth="1" />

                            {/* X-axis labels */}
                            {chartData.map((data, index) => {
                              // Show at most ~5 evenly spaced date labels to avoid overlap
                              const maxLabels = 5;
                              const step = Math.max(1, Math.ceil(chartData.length / maxLabels));
                              const isLast = index === chartData.length - 1;

                              // Skip if this index is not on a step and not the last point
                              if (!isLast && index % step !== 0) {
                                return null;
                              }

                              const x = 40 + (index / (chartData.length - 1)) * 450;
                              return (
                                <text
                                  key={index}
                                  x={x}
                                  y="290"
                                  fill="#9ca3af"
                                  fontSize="12"
                                  textAnchor="middle"
                                  transform={`rotate(-45, ${x}, 290)`}
                                >
                                  {new Date(data.date).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </text>
                              );
                            })}

                            {/* Y-axis */}
                            <line x1="40" y1="20" x2="40" y2="270" stroke={ap.chartAxis} strokeWidth="1" />
                          </svg>
                        </div>

                      </div>
                    )}

                  {filteredTimeSeriesData?.results?.[timeSeriesSliderIndex] && chartData.length > 0 && (
                    filteredTimeSeriesData.index === selectedIndex ||
                    (filteredTimeSeriesData.isSentinel1 && selectedIndex === 'sentinel1')
                  ) && (() => {
                    const row = filteredTimeSeriesData.results[timeSeriesSliderIndex];
                    const isSar = filteredTimeSeriesData.isSentinel1 || selectedIndex === 'sentinel1';
                    const currentValue = isSar ? row.median_backscatter : row.mean_index_value;
                    const previousRow = timeSeriesSliderIndex > 0
                      ? filteredTimeSeriesData.results[timeSeriesSliderIndex - 1]
                      : null;
                    const previousValue = previousRow
                      ? (isSar ? previousRow.median_backscatter : previousRow.mean_index_value)
                      : null;
                    const canCompare =
                      currentValue != null && previousValue != null && Number.isFinite(Number(currentValue)) && Number.isFinite(Number(previousValue));
                    const delta = canCompare ? Number(currentValue) - Number(previousValue) : null;
                    const wentUp = delta != null && delta > 0;
                    const wentDown = delta != null && delta < 0;
                    const cloudValue = row.cloud_percentage !== undefined ? row.cloud_percentage : row.cloud_cover;
                    const cloudLabel =
                      cloudValue == null
                        ? null
                        : cloudValue > 80
                          ? 'Very cloudy'
                          : cloudValue > 50
                            ? 'Partly cloudy'
                            : cloudValue > 20
                              ? 'Mostly clear'
                              : 'Clear sky';
                    const cloudClass =
                      cloudValue == null
                        ? ap.muted
                        : cloudValue > 80
                          ? 'text-red-400'
                          : cloudValue > 50
                            ? 'text-yellow-400'
                            : 'text-green-400';
                    const indexMeaning = indexReadingGuide(selectedIndex).meaning;
                    const indexLabel = `${selectedIndex.toUpperCase()} · ${indexMeaning}`;

                    return (
                      <div className={ap.tsDetailShell}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className={ap.tsDetailTitle}>
                              {new Date(row.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </div>
                            <div className={`mt-0.5 ${isInlineCard ? 'text-[10px]' : 'text-xs'} ${ap.muted}`}>
                              {indexLabel}
                            </div>
                          </div>
                          <div className={`shrink-0 text-right ${isInlineCard ? 'text-[10px]' : 'text-xs'} ${ap.muted}`}>
                            Date {timeSeriesSliderIndex + 1} of {chartData.length}
                          </div>
                        </div>

                        <div className="mt-2 flex items-end justify-between gap-3">
                          <div className={ap.tsDetailValue}>
                            {currentValue != null ? Number(currentValue).toFixed(isSar ? 2 : 3) : '—'}
                            {isSar && currentValue != null ? <span className="ml-1 text-sm font-medium">dB</span> : null}
                          </div>
                          {delta != null ? (
                            <div
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${
                                wentUp
                                  ? 'bg-emerald-500/15 text-emerald-400'
                                  : wentDown
                                    ? 'bg-red-500/15 text-red-400'
                                    : isDark
                                      ? 'bg-white/10 text-[#c9d1d9]'
                                      : 'bg-[#f3f4f6] text-[#374151]'
                              }`}
                            >
                              <i
                                className={
                                  wentUp
                                    ? 'ri-arrow-up-line'
                                    : wentDown
                                      ? 'ri-arrow-down-line'
                                      : 'ri-subtract-line'
                                }
                                aria-hidden
                              />
                              {wentUp ? 'Up' : wentDown ? 'Down' : 'Same'}{' '}
                              {Math.abs(delta).toFixed(isSar ? 2 : 3)}
                              <span className="font-medium opacity-80">vs last date</span>
                            </div>
                          ) : (
                            <div className={`text-[11px] ${ap.muted}`}>First date in this view</div>
                          )}
                        </div>

                        {cloudLabel && (
                          <div className={`mt-1.5 text-[11px] font-medium ${cloudClass}`}>
                            {cloudLabel}
                            {cloudValue != null ? ` · ${Number(cloudValue).toFixed(0)}% cloud` : ''}
                          </div>
                        )}

                        <div className={isInlineCard ? 'mt-2' : 'mt-3'}>
                          <input
                            type="range"
                            min="0"
                            max={chartData.length - 1}
                            value={timeSeriesSliderIndex}
                            onChange={(e) => handleTimeSeriesSliderChange(Number(e.target.value))}
                            className={ap.tsRangeClass}
                            aria-label="Choose a date"
                          />
                          <div className={`flex justify-between ${isInlineCard ? 'text-[10px]' : 'text-xs'} mt-1 ${ap.muted}`}>
                            <span>Older</span>
                            <span>Newer</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {filteredTimeSeriesData && !isProcessingTimeSeries && (
                    <div className={ap.tsOpacityShell}>
                      <label className={`block ${isInlineCard ? 'text-[10px] mb-1' : 'text-xs mb-2'} ${ap.label}`}>
                        Map layer strength: {Math.round(timeSeriesOpacity * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={timeSeriesOpacity}
                        onChange={(e) => {
                          const newOpacity = parseFloat(e.target.value);
                          setTimeSeriesOpacity(newOpacity);
                          if (onAnalysisRun) {
                            onAnalysisRun('adjust-timeseries-opacity', { opacity: newOpacity });
                          }
                        }}
                        className={ap.tsRangeClass}
                      />
                      <div className={`flex justify-between text-xs mt-1 ${ap.muted}`}>
                        <span>0% (Hidden)</span>
                        <span>100% (Solid)</span>
                      </div>
                    </div>
                  )}

                </div>
              )}

              {/* Crop Calendar Analysis */}
              {activeAnalysisTab === 'cropcalendar' && canFarmCropCalendar && (
                <div className="mb-6">
                  <CropCalendar
                    fieldId={selectedField?.farm_id}
                    farmName={selectedField?.farm_name}
                    farmerInput={cropCalendarFarmerInput}
                    onAskPandoy={onCropCalendarAskPandoy}
                  />
                </div>
              )}

              {/* Climate Analysis */}
              {activeAnalysisTab === 'climate' && canFarmClimate && (
                <div className="mb-6">
                  <h4 className={`text-sm font-medium mb-4 ${ap.heading}`}>Climate Data Analysis</h4>

                  {/* Dataset Selection */}
                  <div className="mb-4">
                    <label className={`block text-xs ${ap.label} mb-2`}>Data Source</label>
                    <div className="relative">
                      <select
                        value={climateDataset}
                        onChange={(e) =>
                          handleClimateDatasetChange(e.target.value as 'era5_land' | 'ecmwf_ifs_nrt')
                        }
                        className={`w-full ${ap.select}`}
                        disabled={isProcessingClimate}
                      >
                        <option value="era5_land">ERA5-Land (historical daily)</option>
                        <option value="ecmwf_ifs_nrt">ECMWF IFS NRT (15-day forecast)</option>
                      </select>
                      <i className={`ri-arrow-down-s-line absolute right-2 top-2.5 pointer-events-none ${ap.selectIcon}`}></i>
                    </div>
                  </div>

                  {/* Climate Parameter Selection */}
                  <div className="mb-4">
                    <label className={`block text-xs ${ap.label} mb-2`}>Climate Parameter</label>
                    <div className="relative">
                      <select
                        value={climateParameter}
                        onChange={(e) => setClimateParameter(e.target.value)}
                        className={`w-full ${ap.select}`}
                        disabled={isProcessingClimate}
                      >
                        {climateDataset === 'era5_land' ? (
                          <>
                            <option value="temperature_2m">Temperature (°C)</option>
                            <option value="surface_net_solar_radiation_sum">Solar Radiation (J/m²)</option>
                            <option value="total_precipitation_sum">Rainfall (mm)</option>
                            <option value="total_evaporation_sum">Evapotranspiration (mm)</option>
                            <option value="relative_humidity">Relative Humidity (%)</option>
                          </>
                        ) : (
                          <>
                            <option value="temperature_2m">Temperature — mean (°C)</option>
                            <option value="temperature_2m_max">Daily max temperature (°C)</option>
                            <option value="temperature_2m_min">Daily min temperature (°C)</option>
                            <option value="dewpoint_temperature_2m">Dewpoint temperature (°C)</option>
                            <option value="relative_humidity">Relative humidity (%)</option>
                            <option value="total_precipitation_sum">Rainfall (mm)</option>
                            <option value="runoff">Surface runoff (mm)</option>
                            <option value="surface_net_solar_radiation_sum">Net solar radiation (J/m²)</option>
                            <option value="surface_solar_radiation_downwards">Incoming solar radiation (J/m²)</option>
                            <option value="soil_moisture_0_7cm">Soil moisture 0–7 cm (%)</option>
                            <option value="soil_moisture_7_28cm">Soil moisture 7–28 cm (%)</option>
                            <option value="soil_moisture_28_100cm">Soil moisture 28–100 cm (%)</option>
                            <option value="soil_temperature_0_7cm">Soil temperature 0–7 cm (°C)</option>
                            <option value="wind_speed_10m">Wind speed 10 m (m/s)</option>
                          </>
                        )}
                      </select>
                      <i className={`ri-arrow-down-s-line absolute right-2 top-2.5 pointer-events-none ${ap.selectIcon}`}></i>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="mb-4">
                    <label className={`block text-xs ${ap.label} mb-2`}>
                      Date Range
                      {climateDataset === 'ecmwf_ifs_nrt' ? ' (forecast window)' : ''}
                    </label>

                    {climateAvailabilityLoading && (
                      <div className={ap.tsProcessingShell}>
                        <div className={ap.tsProcessingText}>
                          <div className={`animate-spin h-4 w-4 border-2 rounded-full border-t-transparent mr-3 shrink-0 ${isDark ? 'border-[#34d399]' : 'border-[#008540]'}`} />
                          <span>Loading available date range from data source…</span>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={startDate}
                        min={climateAvailability?.available_start}
                        max={climateAvailability?.available_end || endDate}
                        onChange={(e) => {
                          const next = onStartDateChange(
                            e.target.value,
                            endDate,
                            startDate
                          );
                          let start = next.start;
                          let end = next.end;
                          if (climateAvailability?.available_start && start < climateAvailability.available_start) {
                            start = climateAvailability.available_start;
                          }
                          if (climateAvailability?.available_end && end > climateAvailability.available_end) {
                            end = climateAvailability.available_end;
                          }
                          setStartDate(start);
                          setEndDate(end);
                        }}
                        className={`${ap.select.replace('w-full ', '')}`}
                        disabled={isProcessingClimate || climateAvailabilityLoading}
                      />
                      <input
                        type="date"
                        value={endDate}
                        min={climateAvailability?.available_start || startDate}
                        max={climateAvailability?.available_end}
                        onChange={(e) => {
                          const next = onEndDateChange(
                            startDate,
                            e.target.value,
                            endDate
                          );
                          let start = next.start;
                          let end = next.end;
                          if (climateAvailability?.available_end && end > climateAvailability.available_end) {
                            end = climateAvailability.available_end;
                          }
                          if (climateAvailability?.available_start && start < climateAvailability.available_start) {
                            start = climateAvailability.available_start;
                          }
                          setStartDate(start);
                          setEndDate(end);
                        }}
                        className={`${ap.select.replace('w-full ', '')}`}
                        disabled={isProcessingClimate || climateAvailabilityLoading}
                      />
                    </div>
                    {climateAvailability?.available_end && !climateAvailabilityLoading && (
                      <p className={`text-[11px] mt-1.5 ${ap.muted}`}>
                        Available data: {climateAvailability.available_start || '…'} → {climateAvailability.available_end}
                        {climateDataset === 'ecmwf_ifs_nrt' ? ' (full ~15-day forecast)' : ''}
                      </p>
                    )}
                  </div>

                  {/* Info Box */}
                  <div className={ap.infoNote}>
                    <div className="flex items-start">
                      <i className={`ri-information-line text-sm mr-2 mt-0.5 flex-shrink-0 ${ap.infoNoteIcon}`}></i>
                      <div className={`text-xs ${ap.infoNoteBody}`}>
                        <p className={`font-medium mb-1 ${ap.infoNoteTitle}`}>Climate Data Source</p>
                        <p>
                          {climateData?.metadata?.dataset_label || climateDatasetMeta.title}
                        </p>
                        <p className={`mt-1 ${ap.muted}`}>
                          {climateData?.metadata?.availability?.available_end
                            ? `Data available through ${climateData.metadata.availability.available_end}`
                            : climateAvailability?.available_end
                              ? `Data available through ${climateAvailability.available_end}`
                              : climateDatasetMeta.detail}
                          {climateData?.metadata?.availability?.dates_adjusted
                            ? ' · dates auto-adjusted to available window'
                            : ''}
                        </p>
                        {climateAvailability?.creation_time_utc && climateDataset === 'ecmwf_ifs_nrt' && (
                          <p className={`mt-1 ${ap.muted}`}>
                            Forecast run: {climateAvailability.creation_time_utc}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Processing Status */}
                  {isProcessingClimate && (
                    <div className={ap.tsProcessingShell}>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className={ap.tsProcessingTitle}>Fetching Climate Data</h5>
                      </div>
                      <div className={ap.tsProcessingText}>
                        <div className={`animate-spin h-4 w-4 border rounded-full border-t-transparent mr-3 ${isDark ? 'border-[#34d399]' : 'border-[#008540]'}`}></div>
                        <span>{climateDatasetMeta.loading}</span>
                      </div>
                    </div>
                  )}

                  {/* Error State */}
                  {processingError && !isProcessingClimate && activeAnalysisTab === 'climate' && (
                    <div className="mb-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                      <div className="flex items-center text-red-400">
                        <i className="ri-error-warning-line mr-2"></i>
                        <span className="text-sm">{processingError}</span>
                      </div>
                    </div>
                  )}

                  {/* Generate Button */}
                  <button
                    onClick={async () => {
                      if (!selectedField || !fieldGeoJson) {
                        console.error('No field selected');
                        return;
                      }

                      const jobGen = beginFarmJob();
                      const abortController = new AbortController();
                      climateAbortRef.current = abortController;
                      const stillCurrent = () => jobGen === farmJobGenRef.current;

                      setIsProcessingClimate(true);
                      setProcessingError('');

                      try {
                        const { start_date: cStart, end_date: cEnd } = normalizeDateRange(startDate, endDate);

                        const response = await apiClient.post('/climate-data/', {
                          geometry: fieldGeoJson,
                          parameter: climateParameter,
                          dataset: climateDataset,
                          start_date: cStart,
                          end_date: cEnd
                        }, { signal: abortController.signal });

                        if (!stillCurrent()) return;

                        console.log('Climate data response:', response.data);

                        if (response.data.success) {
                          setClimateData(response.data);
                          if (response.data.start_date) setStartDate(response.data.start_date);
                          if (response.data.end_date) setEndDate(response.data.end_date);
                          if (response.data.metadata?.availability) {
                            setClimateAvailability((prev) => ({
                              ...(prev || {}),
                              ...response.data.metadata.availability,
                              suggested_start: response.data.start_date,
                              suggested_end: response.data.end_date,
                            }));
                          }
                          // Automatically open fullscreen view
                          setShowClimateFullscreen(true);

                          if (onAnalysisRun) {
                            onAnalysisRun('climate', {
                              type: 'climate',
                              data: response.data,
                              parameter: climateParameter,
                              dataset: climateDataset,
                              startDate: cStart,
                              endDate: cEnd
                            });
                          }
                        }
                      } catch (error: any) {
                        if (isRequestAbortError(error) || !stillCurrent()) return;
                        console.error('Error fetching climate data:', error);
                        const detail =
                          error?.response?.data?.detail ||
                          error?.message ||
                          'Failed to fetch climate data. Please try again.';
                        setProcessingError(typeof detail === 'string' ? detail : 'Failed to fetch climate data. Please try again.');
                      } finally {
                        if (stillCurrent()) setIsProcessingClimate(false);
                      }
                    }}
                    disabled={!selectedField || processing || isProcessingClimate || climateAvailabilityLoading}
                    className={`w-full px-4 py-2 rounded-[10px] text-sm font-medium transition-colors flex items-center justify-center mb-4 ${isProcessingClimate || climateAvailabilityLoading || !selectedField || processing
                      ? `${ap.btnDisabled} cursor-not-allowed`
                      : ap.primaryBtn
                      }`}
                  >
                    {isProcessingClimate ? (
                      <>
                        <div className="animate-spin h-4 w-4 border border-white rounded-full border-t-transparent mr-2"></div>
                        Fetching...
                      </>
                    ) : climateAvailabilityLoading ? (
                      <>
                        <div className="animate-spin h-4 w-4 border border-white rounded-full border-t-transparent mr-2"></div>
                        Loading date range...
                      </>
                    ) : (
                      <>
                        <i className="ri-cloud-line mr-2"></i>
                        Fetch Climate Data
                      </>
                    )}
                  </button>

                  {/* Climate Data Visualization */}
                  {climateData && !isProcessingClimate && (
                    <div className="space-y-4">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className={`p-3 rounded-[10px] border ${ap.cardSolid}`}>
                          <div className={`text-xs ${ap.muted}`}>Total Days</div>
                          <div className={`text-lg font-bold ${ap.heading}`}>{climateData.total_days}</div>
                        </div>
                        <div className={`p-3 rounded-[10px] border ${ap.cardSolid}`}>
                          <div className={`text-xs ${ap.muted}`}>Total Weeks</div>
                          <div className={`text-lg font-bold ${ap.heading}`}>{climateData.total_weeks}</div>
                        </div>
                      </div>

                      {/* Data View Chart */}
                      <div className={`p-3 rounded-lg border ${ap.cardSolid}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <h5 className={`text-xs font-medium truncate ${ap.subheading}`}>
                              {climateData.parameter_name}
                            </h5>
                            {/* Daily/Weekly Toggle */}
                            <div className={`flex items-center rounded-lg p-0.5 shrink-0 ${ap.segmentWrap}`}>
                              <button
                                onClick={() => setClimateDataView('daily')}
                                className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                                  climateDataView === 'daily' ? ap.segmentActive : ap.segmentInactive
                                }`}
                              >
                                Daily
                              </button>
                              <button
                                onClick={() => setClimateDataView('weekly')}
                                className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                                  climateDataView === 'weekly' ? ap.segmentActive : ap.segmentInactive
                                }`}
                              >
                                Weekly
                              </button>
                            </div>
                          </div>
                          <button
                            onClick={() => setShowClimateFullscreen(true)}
                            className={`px-2 py-1 text-[10px] rounded flex items-center transition-colors shrink-0 ${
                              isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                            title="View Fullscreen"
                          >
                            <i className="ri-fullscreen-line mr-1"></i>
                            Fullscreen
                          </button>
                        </div>

                        {/* Simple SVG Line Chart */}
                        <div className="relative" style={{ height: '140px', width: '100%' }}>
                          <svg width="100%" height="100%" viewBox="0 0 500 140" className="overflow-visible">
                            {/* Chart background */}
                            <rect width="450" height="120" x="40" y="10" fill={ap.chartBg} stroke="none" />

                            {/* Grid lines */}
                            {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
                              const y = 130 - (ratio * 120);
                              const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                              const maxVal = Math.max(...currentData.map((d: any) => d.value));
                              const minVal = Math.min(...currentData.map((d: any) => d.value));
                              const value = minVal + (maxVal - minVal) * ratio;
                              return (
                                <g key={index}>
                                  <line
                                    x1="40"
                                    y1={y}
                                    x2="490"
                                    y2={y}
                                    stroke={ap.chartGrid}
                                    strokeWidth="1"
                                  />
                                  <text
                                    x="35"
                                    y={y + 3}
                                    fill={ap.chartLabel}
                                    fontSize="7"
                                    textAnchor="end"
                                  >
                                    {value.toFixed(1)}
                                  </text>
                                </g>
                              );
                            })}

                            {/* Line Chart */}
                            <path
                              d={(() => {
                                const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                                const maxVal = Math.max(...currentData.map((d: any) => d.value));
                                const minVal = Math.min(...currentData.map((d: any) => d.value));
                                return currentData.map((data: any, index: number) => {
                                  const x = 40 + (index / (currentData.length - 1)) * 450;
                                  const normalizedValue = (data.value - minVal) / (maxVal - minVal);
                                  const y = 130 - (normalizedValue * 120);
                                  return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                                }).join(' ');
                              })()}
                              fill="none"
                              stroke={ap.chartLine}
                              strokeWidth="2"
                            />
                          </svg>
                        </div>

                        {/* Statistics */}
                        <div className="mt-2 grid grid-cols-3 gap-1.5 text-[10px]">
                          <div className={`rounded p-1.5 border ${ap.cardSolid}`}>
                            <div className={ap.muted}>Average</div>
                            <div className={`font-semibold ${ap.accentText}`}>
                              {(() => {
                                const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                                return (currentData.reduce((sum: number, d: any) => sum + d.value, 0) / currentData.length).toFixed(2);
                              })()}
                            </div>
                          </div>
                          <div className={`rounded p-1.5 border ${ap.cardSolid}`}>
                            <div className={ap.muted}>Maximum</div>
                            <div className="text-red-500 font-semibold">
                              {(() => {
                                const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                                return Math.max(...currentData.map((d: any) => d.value)).toFixed(2);
                              })()}
                            </div>
                          </div>
                          <div className={`rounded p-1.5 border ${ap.cardSolid}`}>
                            <div className={ap.muted}>Minimum</div>
                            <div className={`font-semibold ${isDark ? 'text-blue-400' : 'text-sky-600'}`}>
                              {(() => {
                                const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                                return Math.min(...currentData.map((d: any) => d.value)).toFixed(2);
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Export Button */}
                      <button
                        onClick={() => {
                          // Create CSV from current data view (daily or weekly)
                          const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                          const csvHeaders = climateDataView === 'daily'
                            ? ['Date', climateData.parameter_name]
                            : ['Week Start', 'Week End', climateData.parameter_name];

                          const csvData = climateDataView === 'daily'
                            ? currentData.map((d: any) => [d.date, d.value])
                            : currentData.map((d: any) => [d.week_start, d.week_end, d.value]);

                          const csvContent = [
                            csvHeaders.join(','),
                            ...csvData.map((row: any) => row.join(','))
                          ].join('\n');

                          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                          const link = document.createElement('a');
                          const url = URL.createObjectURL(blob);
                          link.setAttribute('href', url);
                          link.setAttribute('download', `climate_${climateParameter}_${climateDataView}_${startDate}_${endDate}.csv`);
                          link.style.visibility = 'hidden';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className={`w-full px-3 py-2 text-xs rounded-lg transition-colors flex items-center justify-center ${ap.primaryBtn}`}
                      >
                        <i className="ri-download-line mr-2"></i>
                        Export {climateDataView === 'daily' ? 'Daily' : 'Weekly'} Data (CSV)
                      </button>
                    </div>
                  )}

                  {/* Parameter Descriptions - Show only when no data */}
                  {!climateData && !isProcessingClimate && (
                    <div className="space-y-2">
                      <h5 className={`text-xs font-medium mb-2 ${ap.subheading}`}>Available Parameters:</h5>
                      <div className="space-y-2 text-xs">
                        {[
                          ['Temperature (°C)', '2-meter air temperature'],
                          ['Solar Radiation (J/m²)', 'Surface net solar radiation sum'],
                          ['Rainfall (mm)', 'Total precipitation sum'],
                          ['Evapotranspiration (mm)', 'Total evaporation sum'],
                          ['Relative Humidity (%)', 'Calculated from temperature and dewpoint'],
                        ].map(([title, detail]) => (
                          <div key={title} className={`p-2 rounded-[10px] border ${ap.cardSolid}`}>
                            <div className={`font-medium ${ap.heading}`}>{title}</div>
                            <div className={ap.muted}>{detail}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}


              {/* Report Generation */}
              {activeAnalysisTab === 'report' && canFarmFieldReport && (
                <GenerateReportPanel
                  isDark={isDark}
                  farmName={selectedField?.farm_name}
                  farmId={farmPublicId(selectedField) || selectedField?.farm_id}
                  reportKind={reportKind}
                  onReportKind={setReportKind}
                  briefLang={briefLang}
                  onBriefLang={setBriefLang}
                  selectedIndices={selectedReportIndices}
                  onSelectedIndices={setSelectedReportIndices}
                  isGenerating={isGeneratingReport}
                  onDownload={(dates) => {
                    if (reportKind === 'brief') generateFarmBrief();
                    else generateReport(dates);
                  }}
                  onClose={onCloseAnalysis}
                />
              )}


              {/* Farm-level banana BLS outlook — uses this field's OpenWeather */}
              {activeAnalysisTab === 'blsForecast' && canFarmBlsForecast && (
                <div className="mb-6">
                  <BlsForecastCard
                    headingClass={ap.heading}
                    mutedClass={ap.muted}
                    inputClass={ap.select}
                    compact={isInlineCard}
                    fieldGeoJson={(fieldGeoJson as Record<string, unknown> | null) ?? null}
                    farmId={selectedField?.farm_id ?? null}
                    fieldName={selectedField?.farm_name ?? null}
                    requireField
                  />
                </div>
              )}

              {/* Farm-level Rice Yield Forecast (field analysis tools) */}
              {activeAnalysisTab === 'farmForecast' && canFarmRiceForecast && (
                <div className="mb-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className={rsEmerald.iconWrap}>
                      <i className={`ri-seedling-fill text-lg ${rsEmerald.icon}`}></i>
                    </div>
                    <div>
                      <h4 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Rice Yield Forecast (Field)</h4>
                      <p className={`text-[11px] ${rsEmerald.muted}`}>
                        Farm-level yield prediction similar to large-scale forecast
                      </p>
                    </div>
                  </div>

                  {/* Info about selected field */}
                  <div className={`mb-4 p-3 rounded-lg ${rsEmerald.fieldCard}`}>
                    {selectedField ? (
                      <>
                        <p className={`text-xs mb-1 ${rsEmerald.muted}`}>Using farm from Digisaka:</p>
                        <p className={rsEmerald.fieldName}>
                          {selectedField.farm_name || 'Unnamed Field'}
                        </p>
                        <p className={`text-[11px] break-all ${rsEmerald.muted}`}>
                          Farm ID:{' '}
                          <span className={rsEmerald.body}>
                            {farmPublicId(selectedField) || selectedField.farm_id}
                          </span>
                        </p>
                      </>
                    ) : (
                      <p className={`text-xs ${rsEmerald.muted}`}>
                        Please select a field to generate a farm-level forecast.
                      </p>
                    )}
                  </div>

                  {/* Forecast Month - same style as large-scale panel */}
                  <div className="mb-3">
                    <label className={`flex items-center text-[11px] font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className={`ri-calendar-line mr-1.5 ${rsEmerald.icon}`}></i>
                      Select Month
                    </label>
                    <div className="relative">
                      <select
                        value={farmForecastMonth}
                        onChange={(e) => setFarmForecastMonth(e.target.value)}
                        className={rsEmerald.select}
                      >
                        <option value="">Choose a month...</option>
                        {farmForecastMonths.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <i className={`ri-arrow-down-s-line absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${rsEmerald.icon}`}></i>
                    </div>
                  </div>

                  {/* Error Message */}
                  {farmForecastError && (
                    <div className={`mb-3 p-2.5 rounded-lg text-[11px] flex items-start space-x-2 ${isDark ? 'bg-red-900/40 border border-red-700/60 text-red-200' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                      <i className="ri-error-warning-line text-sm mt-0.5"></i>
                      <span>{farmForecastError}</span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    onClick={handleFarmRiceForecastSubmit}
                    disabled={isFarmForecastLoading || !selectedField}
                    className={`w-full py-2.5 px-4 text-[11px] font-semibold rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 shadow-md ${
                      isFarmForecastLoading || !selectedField
                        ? rsEmerald.submitDisabled
                        : rsEmerald.submitActive
                    }`}
                  >
                    {isFarmForecastLoading ? (
                      <>
                        <div className="animate-spin h-3.5 w-3.5 border-2 border-white rounded-full border-t-transparent"></div>
                        <span>Loading Forecast...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-bar-chart-box-line text-sm"></i>
                        <span>Generate Farm Forecast</span>
                      </>
                    )}
                  </button>

                  {/* Basic Results Summary */}
                  {farmForecastResult && (
                    <div className={`mt-4 space-y-2 ${rsEmerald.summaryShell}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h5 className={rsEmerald.summaryTitle}>Forecast Summary</h5>
                        <button
                          type="button"
                          onClick={handleFarmRiceForecastDownloadPDF}
                          disabled={isGeneratingFarmForecastPDF}
                          className={rsEmerald.pdfBtn}
                          title="Export farm forecast report to PDF"
                        >
                          {isGeneratingFarmForecastPDF ? (
                            <i className="ri-loader-4-line animate-spin" />
                          ) : (
                            <i className="ri-file-pdf-2-line" />
                          )}
                          PDF
                        </button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                        <div>
                          <p className={rsEmerald.summaryMetric}>Base Yield</p>
                          <p className={`font-semibold ${rsEmerald.summaryValue}`}>
                            {typeof farmForecastResult.base_yield === 'number'
                              ? farmForecastResult.base_yield.toFixed(2)
                              : farmForecastResult.base_yield}{' '}
                            <span className={`font-normal ${rsEmerald.muted}`}>t/ha</span>
                          </p>
                        </div>
                        <div>
                          <p className={rsEmerald.summaryMetric}>Total Yield</p>
                          <p className={`font-semibold ${rsEmerald.summaryValue}`}>
                            {typeof farmForecastResult.total_yield_tons === 'number'
                              ? farmForecastResult.total_yield_tons.toFixed(2)
                              : farmForecastResult.total_yield_tons}{' '}
                            <span className={`font-normal ${rsEmerald.muted}`}>tons</span>
                          </p>
                        </div>
                        <div>
                          <p className={rsEmerald.summaryMetric}>Land Area</p>
                          <p className={`font-semibold ${rsEmerald.summaryValue}`}>
                            {typeof farmForecastResult.total_land_area_hectares === 'number'
                              ? farmForecastResult.total_land_area_hectares.toFixed(2)
                              : farmForecastResult.total_land_area_hectares}{' '}
                            <span className={`font-normal ${rsEmerald.muted}`}>ha</span>
                          </p>
                        </div>
                        <div>
                          <p className={rsEmerald.summaryMetric}>Rice Price</p>
                          <p className={`font-semibold ${rsEmerald.summaryValue}`}>
                            ₱{farmForecastResult.rice_price}{' '}
                            <span className={`font-normal ${rsEmerald.muted}`}>/kg</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Farm-level Drone Data — compact panel matching farm analysis tab sizing */}
              {activeAnalysisTab === 'droneData' && canFarmDroneData && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium mb-4">Sample Data</h4>
                  {layerVisibility && onLayerToggle ? (
                    <FarmDroneDataPanel
                      isDark={isDark}
                      isCompact={isInlineCard}
                      selectedField={selectedField}
                      fieldGeoJson={fieldGeoJson ?? null}
                      layerVisibility={layerVisibility}
                      layerOpacity={layerOpacity || {}}
                      onLayerToggle={onLayerToggle}
                      onOpacityChange={onOpacityChange || (() => {})}
                      onRequestDronePrescriptionStats={onRequestDronePrescriptionStats}
                      onClearDronePrescriptionStats={onClearDronePrescriptionStats}
                      onShowDroneOrthoIndexMap={onShowDroneOrthoIndexMap}
                      onClearDroneOrthoIndexMap={onClearDroneOrthoIndexMap}
                      allowedEntitlementSet={layerAllowedEntitlementSet}
                    />
                  ) : (
                    <div className={`p-4 text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Loading drone layer controls...
                    </div>
                  )}
                </div>
              )}


            </div>
          )
        }

      </div>

      {showReportModal && reportData && (
        <Report
          reportData={{
            ...reportData,
            startDate,
            endDate,
            fieldGeoJson,
          }}
          onClose={closeReportModal}
        />
      )}

      {/* Climate chart — portaled so the fields list cannot cover the left of the graph. */}
      {showClimateFullscreen && climateData && typeof document !== 'undefined' &&
        createPortal(
        <div
          className={`fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4 ${isDark ? 'bg-black/85' : 'bg-black/40'}`}
          onClick={() => {
            setShowClimateFullscreen(false);
            setHoveredDataPoint(null);
          }}
        >
          <div
            className={`rounded-xl w-full max-w-3xl max-h-[min(72vh,640px)] overflow-hidden flex flex-col shadow-2xl border ${
              isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-4 py-3 border-b ${ap.headerBorder}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <h2 className={`text-base sm:text-lg font-bold truncate ${ap.heading}`}>{climateData.parameter_name}</h2>
                  {/* Daily/Weekly Toggle */}
                  <div className={`flex items-center rounded-lg p-0.5 ${ap.segmentWrap}`}>
                    <button
                      onClick={() => {
                        setClimateDataView('daily');
                        setHoveredDataPoint(null);
                      }}
                      className={`px-2.5 py-1 text-xs rounded transition-colors ${
                        climateDataView === 'daily' ? ap.segmentActive : ap.segmentInactive
                      }`}
                    >
                      Daily ({climateData.total_days} days)
                    </button>
                    <button
                      onClick={() => {
                        setClimateDataView('weekly');
                        setHoveredDataPoint(null);
                      }}
                      className={`px-2.5 py-1 text-xs rounded transition-colors ${
                        climateDataView === 'weekly' ? ap.segmentActive : ap.segmentInactive
                      }`}
                    >
                      Weekly ({climateData.total_weeks} weeks)
                    </button>
                  </div>
                </div>
                <p className={`text-xs mt-1 ${ap.muted}`}>
                  {climateData.start_date} to {climateData.end_date}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowClimateFullscreen(false);
                  setHoveredDataPoint(null);
                }}
                className={`p-1.5 rounded-lg transition-colors shrink-0 ml-2 ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                title="Close"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Chart Content */}
            <div className="flex-1 px-3 py-2 overflow-hidden min-h-0">
              <div className="h-full flex flex-col">
                {/* SVG Chart with Hover */}
                <div
                  className={`flex-1 rounded-lg p-1.5 relative min-h-0 overflow-x-auto border ${ap.cardSolid}`}
                  style={{ height: '260px' }}
                >
                  <svg
                    width={(() => {
                      const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                      const minWidth = Math.max(720, currentData.length * 6);
                      return minWidth;
                    })()}
                    height="240"
                    viewBox={(() => {
                      const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                      const minWidth = Math.max(720, currentData.length * 6);
                      return `0 0 ${minWidth} 320`;
                    })()}
                    preserveAspectRatio="xMidYMid meet"
                    className="overflow-visible"
                    onMouseLeave={() => setHoveredDataPoint(null)}
                  >
                    {/* Chart background */}
                    <rect
                      width={(() => {
                        const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                        const minWidth = Math.max(720, currentData.length * 6);
                        return minWidth - 60;
                      })()}
                      height="260"
                      x="50"
                      y="30"
                      fill={ap.chartBg}
                      stroke="none"
                    />

                    {/* Grid lines */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
                      const y = 290 - (ratio * 260);
                      const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                      const maxVal = Math.max(...currentData.map((d: any) => d.value));
                      const minVal = Math.min(...currentData.map((d: any) => d.value));
                      const value = minVal + (maxVal - minVal) * ratio;
                      const chartWidth = Math.max(720, currentData.length * 6);
                      return (
                        <g key={index}>
                          <line
                            x1="50"
                            y1={y}
                            x2={chartWidth - 10}
                            y2={y}
                            stroke={ap.chartGrid}
                            strokeWidth="1"
                          />
                          <text
                            x="42"
                            y={y + 4}
                            fill={ap.chartLabel}
                            fontSize="10"
                            textAnchor="end"
                          >
                            {value.toFixed(1)}
                          </text>
                        </g>
                      );
                    })}

                    {/* X-axis labels */}
                    {(() => {
                      const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                      const chartWidth = Math.max(720, currentData.length * 6) - 60;
                      const labelInterval = climateDataView === 'daily'
                        ? Math.max(1, Math.floor(currentData.length / 16))
                        : Math.max(1, Math.floor(currentData.length / 10));
                      return currentData.map((data: any, index: number) => {
                        if (index % labelInterval === 0 || index === currentData.length - 1) {
                          const x = 50 + (index / (currentData.length - 1)) * chartWidth;
                          const dateLabel = climateDataView === 'daily'
                            ? new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : new Date(data.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                          return (
                            <text
                              key={index}
                              x={x}
                              y="310"
                              fill={ap.chartLabel}
                              fontSize="10"
                              textAnchor="middle"
                            >
                              {dateLabel}
                            </text>
                          );
                        }
                        return null;
                      });
                    })()}

                    {/* Area under the line */}
                    <path
                      d={(() => {
                        const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                        const maxVal = Math.max(...currentData.map((d: any) => d.value));
                        const minVal = Math.min(...currentData.map((d: any) => d.value));
                        const chartWidth = Math.max(720, currentData.length * 6) - 60;
                        const points = currentData.map((data: any, index: number) => {
                          const x = 50 + (index / (currentData.length - 1)) * chartWidth;
                          const normalizedValue = (data.value - minVal) / (maxVal - minVal);
                          const y = 290 - (normalizedValue * 260);
                          return `${x},${y}`;
                        });
                        return `M 50,290 L ${points.join(' L ')} L ${50 + chartWidth},290 Z`;
                      })()}
                      fill="url(#climate-gradient)"
                      opacity="0.25"
                    />

                    {/* Gradient definition */}
                    <defs>
                      <linearGradient id="climate-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={ap.chartLine} stopOpacity="0.8" />
                        <stop offset="100%" stopColor={ap.chartLine} stopOpacity="0" />
                      </linearGradient>
                    </defs>

                    {/* Line Chart */}
                    <path
                      d={(() => {
                        const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                        const maxVal = Math.max(...currentData.map((d: any) => d.value));
                        const minVal = Math.min(...currentData.map((d: any) => d.value));
                        const chartWidth = Math.max(720, currentData.length * 6) - 60;
                        return currentData.map((data: any, index: number) => {
                          const x = 50 + (index / (currentData.length - 1)) * chartWidth;
                          const normalizedValue = (data.value - minVal) / (maxVal - minVal);
                          const y = 290 - (normalizedValue * 260);
                          return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                        }).join(' ');
                      })()}
                      fill="none"
                      stroke={ap.chartLine}
                      strokeWidth="2"
                    />

                    {/* Data points with hover */}
                    {(() => {
                      const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                      const maxVal = Math.max(...currentData.map((d: any) => d.value));
                      const minVal = Math.min(...currentData.map((d: any) => d.value));
                      const chartWidth = Math.max(720, currentData.length * 6) - 60;
                      return currentData.map((data: any, index: number) => {
                        const x = 50 + (index / (currentData.length - 1)) * chartWidth;
                        const normalizedValue = (data.value - minVal) / (maxVal - minVal);
                        const y = 290 - (normalizedValue * 260);
                        const isHovered = hoveredDataPoint?.index === index;

                        return (
                          <g key={index}>
                            <circle
                              cx={x}
                              cy={y}
                              r={isHovered ? '6' : '3'}
                              fill={ap.chartLine}
                              stroke={isDark ? '#ffffff' : '#ffffff'}
                              strokeWidth={isHovered ? '2' : '1.5'}
                              className="cursor-pointer transition-all"
                              onMouseEnter={() => setHoveredDataPoint({ index, data, x, y })}
                            />
                          </g>
                        );
                      });
                    })()}

                    {/* Hover tooltip */}
                    {hoveredDataPoint && (
                      <g>
                        <line
                          x1={hoveredDataPoint.x}
                          y1="30"
                          x2={hoveredDataPoint.x}
                          y2="290"
                          stroke={ap.chartLine}
                          strokeWidth="1.5"
                          strokeDasharray="4,4"
                          opacity="0.5"
                        />

                        <rect
                          x={hoveredDataPoint.x - 70}
                          y={hoveredDataPoint.y - 52}
                          width="140"
                          height="42"
                          fill={ap.chartBg}
                          stroke={ap.chartLine}
                          strokeWidth="1.5"
                          rx="4"
                        />

                        <text
                          x={hoveredDataPoint.x}
                          y={hoveredDataPoint.y - 34}
                          fill={ap.chartLabel}
                          fontSize="10"
                          textAnchor="middle"
                          fontWeight="500"
                        >
                          {climateDataView === 'daily'
                            ? new Date(hoveredDataPoint.data.date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                            : `${new Date(hoveredDataPoint.data.week_start).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })} - ${new Date(hoveredDataPoint.data.week_end).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}`
                          }
                        </text>

                        <text
                          x={hoveredDataPoint.x}
                          y={hoveredDataPoint.y - 18}
                          fill={ap.chartLine}
                          fontSize="13"
                          textAnchor="middle"
                          fontWeight="bold"
                        >
                          {hoveredDataPoint.data.value.toFixed(2)}
                        </text>
                      </g>
                    )}
                  </svg>
                </div>
              </div>
            </div>

            {/* Footer with actions */}
            <div className={`px-3 py-2.5 border-t flex flex-wrap justify-between items-center gap-2 ${ap.headerBorder}`}>
              {/* Statistics - Compact inline version */}
              <div className="flex flex-wrap gap-2">
                <div className={`rounded px-2 py-1 border text-xs ${ap.cardSolid}`}>
                  <span className={ap.muted}>Avg: </span>
                  <span className={`font-semibold ${ap.accentText}`}>
                    {(() => {
                      const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                      return (currentData.reduce((sum: number, d: any) => sum + d.value, 0) / currentData.length).toFixed(2);
                    })()}
                  </span>
                </div>
                <div className={`rounded px-2 py-1 border text-xs ${ap.cardSolid}`}>
                  <span className={ap.muted}>Max: </span>
                  <span className="font-semibold text-red-500">
                    {(() => {
                      const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                      return Math.max(...currentData.map((d: any) => d.value)).toFixed(2);
                    })()}
                  </span>
                </div>
                <div className={`rounded px-2 py-1 border text-xs ${ap.cardSolid}`}>
                  <span className={ap.muted}>Min: </span>
                  <span className={`font-semibold ${isDark ? 'text-blue-400' : 'text-sky-600'}`}>
                    {(() => {
                      const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                      return Math.min(...currentData.map((d: any) => d.value)).toFixed(2);
                    })()}
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  const currentData = climateDataView === 'daily' ? climateData.daily_data : climateData.weekly_data;
                  const csvHeaders = climateDataView === 'daily'
                    ? ['Date', climateData.parameter_name]
                    : ['Week Start', 'Week End', climateData.parameter_name];

                  const csvData = climateDataView === 'daily'
                    ? currentData.map((d: any) => [d.date, d.value])
                    : currentData.map((d: any) => [d.week_start, d.week_end, d.value]);

                  const csvContent = [
                    csvHeaders.join(','),
                    ...csvData.map((row: any) => row.join(','))
                  ].join('\n');

                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const link = document.createElement('a');
                  const url = URL.createObjectURL(blob);
                  link.setAttribute('href', url);
                  link.setAttribute('download', `climate_${climateParameter}_${climateDataView}_${startDate}_${endDate}.csv`);
                  link.style.visibility = 'hidden';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className={`px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center shrink-0 ${ap.primaryBtn}`}
              >
                <i className="ri-download-line mr-1.5"></i>
                Export {climateDataView === 'daily' ? 'Daily' : 'Weekly'} CSV
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Fullscreen Chart Modal — portaled to body so Analysis Tools rail (z-30 sibling of the
          floating panel) cannot stack above this overlay. */}
      {showFullscreenChart &&
        multiFieldResponse &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-90 z-[200] flex flex-col">
          {/* Header */}
          <div className="bg-gray-900 border-b border-gray-700 p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <i className="ri-line-chart-line text-emerald-400 text-xl"></i>
              <div>
                <h3 className="text-lg font-semibold text-white">Multiple Field Time Series Analysis</h3>
                <p className="text-xs text-gray-400">
                  {multiFieldResponse.index?.toUpperCase() || selectedIndex.toUpperCase()} - {multiFieldResponse.start_date} to {multiFieldResponse.end_date}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowFullscreenChart(false)}
              className="text-gray-400 hover:text-white transition-colors p-2 rounded hover:bg-gray-800"
              title="Close"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-7xl mx-auto space-y-8">
              {getChartData().map((field, idx) => {
                const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
                return (
                  <div key={field.fieldId} className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-semibold text-white flex items-center">
                          <div
                            className="w-4 h-1 mr-2"
                            style={{ backgroundColor: colors[idx % colors.length] }}
                          ></div>
                          {field.fieldName}
                        </h4>
                        <span className="text-sm text-gray-400">
                          {field.data.length} data points
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Field ID: {field.fieldId}
                      </div>
                    </div>

                    {/* Large Chart */}
                    <div className="bg-gray-900 rounded p-4">
                      {renderTimeSeriesChart([field], 900, 400, true)}
                    </div>

                    {/* Data Table */}
                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full text-xs text-left text-gray-300">
                        <thead>
                          <tr className="border-b border-gray-700">
                            <th className="px-3 py-2 font-semibold">Date</th>
                            <th className="px-3 py-2 font-semibold">Mean Index</th>
                          </tr>
                        </thead>
                        <tbody>
                          {field.data.map((point: any, pointIdx: number) => (
                            <tr key={pointIdx} className="border-b border-gray-800 hover:bg-gray-700/40">
                              <td className="px-3 py-2">
                                {point.date.toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </td>
                              <td className="px-3 py-2 font-medium text-emerald-300">
                                {point.value.toFixed(4)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          </div>,
          document.body,
        )}
    </>
  );
}